import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { db, auth, functions, ADMIN_EMAILS } from './firebase';
import {
  collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot,
  query, where, getDocs, getDoc, serverTimestamp
} from 'firebase/firestore';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { computeNextSchedule } from './shared/nextSchedule';
import { ROBIN_ORDER as SHARED_ROBIN_ORDER, MATCHES_PER_BLOCK as SHARED_MATCHES_PER_BLOCK } from './shared/robinScheduleConfig';

import {
  Printer, ArrowLeftRight,
  AlertTriangle, Plus, ArrowLeft, X, LogOut, Shield, Trash2,
  Home, Store, Menu, Calendar, HelpCircle, Save, List, Edit2,
  Trophy, Play, ChevronLeft, ChevronRight, Settings, Check, CheckCircle, Lock, RotateCcw, PlayCircle, FlaskConical, QrCode, Eye, EyeOff
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import autoTable from 'jspdf-autotable';
import './App.css';

const rndName = () => `f-${Math.random().toString(36).slice(2, 10)}`;

/* ============================================================
   ペアリングヘルパー関数（ハイ&ロー方式 厳守）
   ============================================================ */
function buildHighLow(allEntries) {
  if (allEntries.length < 2) return { pairs: [], leftover: null };

  const sorted = [...allEntries].sort((a, b) => Number(b.rating) - Number(a.rating));
  const n = sorted.length;
  const half = Math.floor(n / 2);

  const pairs = [];
  for (let i = 0; i < half; i++) {
    pairs.push({ player1: sorted[i], player2: sorted[n - 1 - i] });
  }

  const leftover = n % 2 === 1 ? sorted[half] : null;
  return { pairs, leftover };
}

/* ============================================================
   決勝トーナメント ヘルパー（シングルエリミネーション）
   ============================================================ */
// 標準シード配置 (8人なら [1,8,4,5,3,6,2,7]、上位シードを早期対戦から散らす)
function standardSeedPositions(bracketSize) {
  if (bracketSize === 1) return [1];
  let positions = [1];
  let size = 2;
  while (size <= bracketSize) {
    const next = [];
    for (const p of positions) {
      next.push(p);
      next.push(size + 1 - p);
    }
    positions = next;
    size *= 2;
  }
  return positions;
}

// ブロック内のランキング計算（共通化）
function computeBlockRanking(block) {
  if (!block || !block.pairs) return [];
  const n = block.pairs.length;
  const stats = block.pairs.map((p, i) => ({
    teamNo: i + 1,
    pair: p,
    setsWon: 0,
    legsWon: 0,
    legsLost: 0,
  }));
  const scores = block.scores || {};
  Object.entries(scores).forEach(([key, value]) => {
    if (typeof value !== 'string') return;
    const [t1, t2] = key.split('-').map(s => parseInt(s, 10));
    const [a, b] = value.split('-').map(s => parseInt(s, 10));
    if (isNaN(t1) || isNaN(t2) || isNaN(a) || isNaN(b)) return;
    const i1 = t1 - 1, i2 = t2 - 1;
    if (i1 < 0 || i2 < 0 || i1 >= n || i2 >= n) return;
    stats[i1].legsWon += a;
    stats[i1].legsLost += b;
    stats[i2].legsWon += b;
    stats[i2].legsLost += a;
    if (a === 2 && b < 2) stats[i1].setsWon += 1;
    if (b === 2 && a < 2) stats[i2].setsWon += 1;
  });
  // 第一ソート: setsWon降順 > leg差降順 > 失leg昇順
  const ranked = [...stats].sort((a, b) => {
    if (b.setsWon !== a.setsWon) return b.setsWon - a.setsWon;
    const diffA = a.legsWon - a.legsLost;
    const diffB = b.legsWon - b.legsLost;
    if (diffB !== diffA) return diffB - diffA;
    return a.legsLost - b.legsLost;
  });
  // 第二ソート: 隣接2チームが上記3項目で完全同点なら、直接対決の勝者を上位に。
  // ★RobinTableScreen 内の computeStats (画面表示用) と同一ロジック。
  //   画面の順位と決勝進出/復活枠の順位を一致させるための同期実装。
  //   computeStats 側の直接対決ロジックを変更したら、ここも必ず同期すること。
  //   (将来課題: shared/ranking.js への共通化候補)
  //   3チーム以上の同点は触らない (computeStats と同じ安全設計・運営判断に委ねる)。
  const directWinDelta = (teamA, teamB) => {
    const x = Math.min(teamA, teamB), y = Math.max(teamA, teamB);
    const val = scores[`${x}-${y}`];
    if (!val) return 0;
    const [s1, s2] = val.split('-').map(s => parseInt(s, 10));
    if (isNaN(s1) || isNaN(s2)) return 0;
    const teamAisSmaller = (teamA === x);
    const aScore = teamAisSmaller ? s1 : s2;
    const bScore = teamAisSmaller ? s2 : s1;
    if (aScore === 2 && bScore < 2) return 1;
    if (bScore === 2 && aScore < 2) return -1;
    return 0;
  };
  for (let i = 0; i < ranked.length - 1; i++) {
    const A = ranked[i], B = ranked[i + 1];
    const sameAll = (A.setsWon === B.setsWon) &&
      ((A.legsWon - A.legsLost) === (B.legsWon - B.legsLost)) &&
      (A.legsLost === B.legsLost);
    if (!sameAll) continue;
    const prevTie = i > 0 && (
      ranked[i - 1].setsWon === A.setsWon &&
      (ranked[i - 1].legsWon - ranked[i - 1].legsLost) === (A.legsWon - A.legsLost) &&
      ranked[i - 1].legsLost === A.legsLost
    );
    const nextTie = i + 2 < ranked.length && (
      ranked[i + 2].setsWon === B.setsWon &&
      (ranked[i + 2].legsWon - ranked[i + 2].legsLost) === (B.legsWon - B.legsLost) &&
      ranked[i + 2].legsLost === B.legsLost
    );
    if (prevTie || nextTie) continue;
    const delta = directWinDelta(A.teamNo, B.teamNo);
    if (delta === -1) {
      ranked[i] = B;
      ranked[i + 1] = A;
    }
  }
  return ranked;
}

/* ============================================================
   v2 ブラケット配置ロジック (Phase B: 純粋関数群)

   - これらの関数は既存のブラケット生成ロジックには影響しない。
   - 後続パッチ (P2 以降) で buildTournamentMatches_v2 から呼び出される。
   - 設計ロック: T1〜T13 テストケースと整合する純粋関数。
   - Phase 2 で対応予定: placementAudit (履歴管理), warnings.severity 明示拡張
   ============================================================ */

/**
 * F01: 通過者と復活通過を rank 別に分類して返す純粋関数。
 *
 * 入力:
 *   advancers: [{ blockId, rank }, ...]
 *   losersAdvancers: [{ idx, blockId? }, ...]  // blockId 未確定時は null/省略可
 *
 * 出力:
 *   { rank1, rank2, lower, losers }
 *   rank1, rank2, lower は { sourceType: 'block', blockId, rank } の配列
 *   losers は { sourceType: 'losers', losersIdx, blockId|null } の配列
 *
 * ソート順:
 *   rank1, rank2: blockId 昇順
 *   lower: rank 昇順 → blockId 昇順
 *   losers: 入力順 (idx 順)
 *
 * 防御的処理:
 *   - 配列でない入力は空配列扱い
 *   - rank が数値化できない/blockId が無いエントリは除外
 *   - losers の idx が無い場合は配列インデックスを使用
 */
function classifyAdvancers(advancers, losersAdvancers) {
  const safeAdvancers = Array.isArray(advancers) ? advancers : [];
  const safeLosers = Array.isArray(losersAdvancers) ? losersAdvancers : [];

  const byBlockId = (a, b) => {
    if (a.blockId === b.blockId) return 0;
    return a.blockId < b.blockId ? -1 : 1;
  };
  const byRankThenBlock = (a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    return byBlockId(a, b);
  };

  const normalized = safeAdvancers
    .map(a => {
      const rank = typeof a.rank === 'number' ? a.rank : parseInt(a.rank, 10);
      if (!Number.isFinite(rank) || a.blockId == null) return null;
      return { sourceType: 'block', blockId: a.blockId, rank };
    })
    .filter(Boolean);

  const rank1 = normalized.filter(a => a.rank === 1).sort(byBlockId);
  const rank2 = normalized.filter(a => a.rank === 2).sort(byBlockId);
  const lower = normalized.filter(a => a.rank >= 3).sort(byRankThenBlock);

  const losers = safeLosers.map((l, i) => ({
    sourceType: 'losers',
    losersIdx: typeof l?.idx === 'number' ? l.idx : i,
    blockId: l?.blockId || null,
  }));

  return { rank1, rank2, lower, losers };
}

/**
 * F02: ブラケットサイズ等のメタ情報を計算する純粋関数。
 *
 * 入力: rank1, rank2, lower, losers (各配列、空可)
 * 出力: { N, bracketSize, totalRounds, byeCount }
 *   N < 2 の場合は bracketSize=0, totalRounds=0, byeCount=0 を返す
 */
function computeBracketMeta(rank1, rank2, lower, losers) {
  const N = (rank1?.length || 0)
    + (rank2?.length || 0)
    + (lower?.length || 0)
    + (losers?.length || 0);
  if (N < 2) {
    return { N, bracketSize: 0, totalRounds: 0, byeCount: 0 };
  }
  const totalRounds = Math.ceil(Math.log2(N));
  const bracketSize = Math.pow(2, totalRounds);
  const byeCount = bracketSize - N;
  return { N, bracketSize, totalRounds, byeCount };
}

/**
 * F04: 進行設定/ブラケット生成前に構成の妥当性を検証する純粋関数。
 *
 * 重要 (warnings の扱い):
 *   この関数が返す warnings は「実行時点の snapshot」。
 *   状態がその後変わっても自動更新はされない (dirty=true 時に古くなる可能性あり)。
 *   現在状態の指標ではなく、この時点の構成評価として扱うこと。
 *
 * 警告の分類:
 *   この関数が返すのは「設定警告 (settingWarnings)」。大会構成の妥当性に関する警告。
 *   実際の配置で発生する「配置警告 (placementWarnings)」は
 *   _buildTournamentMatches_v2 が返す。両者は別物として扱う。
 *
 * 入力:
 *   advancers: [{ blockId, rank }, ...]
 *   losersCount: number (進行設定の復活枠数)
 *
 * 出力:
 *   { ok, settingWarnings, summary }
 *   ok = critical な settingWarning が無いこと
 *   settingWarnings = 警告コード配列 (設定段階で検出可能なもの)
 *   summary = 人数集計 (UI 表示用)
 */
function validateBracketComposition(advancers, losersCount) {
  const safeAdvancers = Array.isArray(advancers) ? advancers : [];
  const safeLosersCount = Math.max(0, parseInt(losersCount, 10) || 0);

  const rank1Count = safeAdvancers.filter(a => a.rank === 1).length;
  const rank2Count = safeAdvancers.filter(a => a.rank === 2).length;
  const lowerCount = safeAdvancers.filter(a => a.rank >= 3).length;
  const totalCount = safeAdvancers.length + safeLosersCount;

  const settingWarnings = [];

  // 復活枠 > 2位通過数 (要件 Phase A #1)
  if (safeLosersCount > rank2Count) {
    settingWarnings.push({
      code: 'LOSERS_EXCEED_RANK2',
      detail: `復活枠 ${safeLosersCount} 組が2位通過 ${rank2Count} 組を超えています。余りは下位通過と対戦します。`,
    });
  }

  // 2位通過ゼロ + 復活あり (要件 Phase A #2 例外ケース)
  if (rank2Count === 0 && safeLosersCount > 0) {
    settingWarnings.push({
      code: 'NO_RANK2_WITH_LOSERS',
      detail: '2位通過が無いため、復活通過は1位通過または下位と対戦します。',
    });
  }

  // 復活枠 > 2位 + 下位 (1位 vs 復活 確定発生)
  const absorbCapacity = rank2Count + lowerCount;
  if (safeLosersCount > absorbCapacity) {
    settingWarnings.push({
      code: 'LOSERS_FORCE_VS_1ST',
      detail: `復活枠 ${safeLosersCount} 組が2位+下位通過 ${absorbCapacity} 組を超え、1位 vs 復活が発生します。`,
    });
  }

  // 参加者不足 (critical)
  if (totalCount < 2) {
    settingWarnings.push({
      code: 'INSUFFICIENT_PARTICIPANTS',
      detail: `参加者 ${totalCount} 組では決勝Tを構成できません。`,
      critical: true,
    });
  }

  const hasCritical = settingWarnings.some(w => w.critical === true);

  return {
    ok: !hasCritical,
    settingWarnings,
    summary: {
      rank1Count,
      rank2Count,
      lowerCount,
      losersCount: safeLosersCount,
      total: totalCount,
    },
  };
}

/* ============================================================
   P2: 山割り当て + ペアリング系 (ctx パターン)

   - ctx は { rank1, rank2, lower, losers, used, matchPairs, placementWarnings,
              halfAssignment, meta } を持つ作業オブジェクト。
   - 各 Phase 関数は ctx.matchPairs / ctx.used / ctx.placementWarnings を更新する。
   - 純粋性は失うが、引数の数を抑えて可読性を保つ意図的な選択。
   - keyOf は ctx の used Set 管理に使う一意キー生成補助。
   ============================================================ */

/**
 * 補助: advancer/loser エントリの一意キーを返す純粋関数。
 * used Set への重複登録防止に使用。
 */
function keyOf(item) {
  if (!item) return null;
  if (item.sourceType === 'block') return `block:${item.blockId}:${item.rank}`;
  if (item.sourceType === 'losers') return `losers:${item.losersIdx}`;
  return null;
}

/**
 * F03: ブロックを「左山(L)」「右山(R)」に交互割り当てする純粋関数。
 *
 * 入力: advancers: [{ blockId, rank }, ...]
 * 出力: { [blockId]: 'L' | 'R' }
 *
 * 仕様:
 *   blockId 昇順で L, R, L, R... の交互割り当て。
 *
 * 既知の限界(TODO Phase2):
 *   通過人数・1位人数・ブロック歯抜けを考慮したバランス割り当ては未対応。
 *   現状は blockId 数の偏りで山の人数が偏る可能性があるが、
 *   スワップ補正と placeIntoBracket 側の no-hint 補完で実用上は問題なし。
 */
function assignBlockHalves(advancers) {
  const blockIds = [];
  const seen = new Set();
  (Array.isArray(advancers) ? advancers : []).forEach(a => {
    if (a && a.blockId != null && !seen.has(a.blockId)) {
      blockIds.push(a.blockId);
      seen.add(a.blockId);
    }
  });
  blockIds.sort();
  const result = {};
  blockIds.forEach((id, i) => {
    result[id] = (i % 2 === 0) ? 'L' : 'R';
  });
  return result;
}

/**
 * F05: BYE 取得者を決定して ctx.matchPairs に積む (予約付き)。
 *
 * 重要な設計:
 *   Phase 5-A (pairLosersWithSeconds) で必要な2位数を予約してBYEに渡さない。
 *   予約数 = min(losers.length, rank2.length)
 *   予約対象は rank2 の先頭 (blockId 昇順)。
 *
 * BYE 配布優先順位:
 *   1位通過 → 2位通過(予約対象外) → lower → losers
 *
 * 副作用:
 *   ctx.matchPairs に { top, bottom: null, type: 'BYE', halfHint } を push
 *   ctx.used に keyOf を add
 *
 * 入力: ctx (必須プロパティ: meta, rank1, rank2, lower, losers, used,
 *              matchPairs, halfAssignment)
 */
function assignByes(ctx) {
  if (!ctx || !ctx.meta) return;
  let byeRemaining = ctx.meta.byeCount || 0;
  if (byeRemaining <= 0) return;

  // 【BYE 予約ロジック】
  // 復活通過 vs 2位通過 を 1回戦で成立させるため、必要な2位は BYE 対象から除外する。
  // 予約数 = min(復活通過数, 2位通過数)
  // この予約を行わないと、BYE 配布が先に走った場合に 2位 が全部 BYE を取って
  // しまい、Phase 5-A (pairLosersWithSeconds) で復活と組む 2位 が枯渇する。
  // T5 (復活枠超過) ケースで実証済みの設計修正点。
  const reservedRank2Count = Math.min(
    (ctx.losers || []).length,
    (ctx.rank2 || []).length
  );
  const reservedKeys = new Set(
    (ctx.rank2 || []).slice(0, reservedRank2Count).map(r => keyOf(r))
  );

  // BYE 配布優先順位: 1位通過 → 2位通過(予約対象外) → lower → losers
  const byeQueue = [
    ...(ctx.rank1 || []),
    ...(ctx.rank2 || []).filter(r => !reservedKeys.has(keyOf(r))),
    ...(ctx.lower || []),
    ...(ctx.losers || []),
  ];

  for (const item of byeQueue) {
    if (byeRemaining === 0) break;
    const k = keyOf(item);
    if (!k || ctx.used.has(k)) continue;
    ctx.matchPairs.push({
      top: item,
      bottom: null,
      type: 'BYE',
      halfHint: item.blockId ? (ctx.halfAssignment[item.blockId] || null) : null,
    });
    ctx.used.add(k);
    byeRemaining--;
  }
}

/**
 * F06: 復活通過と2位通過のペアを生成 (Phase 5-A)。
 *
 * 処理順序:
 *   1. confirmedLosers (blockId 確定) を先に処理 → 異ブロック2位を厳密選択
 *   2. unconfirmedLosers (blockId null) を後に処理 → 残った2位を任意選択
 *
 * 同ブロック妥協時は warning 'LOSERS_SAME_BLOCK' を発行。
 * 2位枯渇時はそのままスキップ(残り復活は Phase 5-C で処理)。
 */
function pairLosersWithSeconds(ctx) {
  if (!ctx) return;

  const availableLosers = (ctx.losers || []).filter(l => !ctx.used.has(keyOf(l)));
  const confirmedLosers = availableLosers.filter(l => l.blockId);
  const unconfirmedLosers = availableLosers.filter(l => !l.blockId);

  const pickAndPair = (loser, allowSameBlock) => {
    let pick = null;
    if (!allowSameBlock) {
      pick = (ctx.rank2 || []).find(r =>
        !ctx.used.has(keyOf(r)) && r.blockId !== loser.blockId
      );
    } else {
      pick = (ctx.rank2 || []).find(r => !ctx.used.has(keyOf(r)));
      if (pick && pick.blockId === loser.blockId) {
        ctx.placementWarnings.push({
          code: 'LOSERS_SAME_BLOCK',
          detail: `復活枠${loser.losersIdx + 1} (${loser.blockId}) vs ${pick.blockId}-2位`,
        });
      }
    }
    if (!pick) return false;
    ctx.matchPairs.push({
      top: pick,
      bottom: loser,
      type: 'LOSERS_VS_2ND',
      halfHint: ctx.halfAssignment[pick.blockId] || null,
    });
    ctx.used.add(keyOf(pick));
    ctx.used.add(keyOf(loser));
    return true;
  };

  // Pass1: confirmed losers, 異ブロック厳守
  for (const loser of confirmedLosers) {
    if (pickAndPair(loser, false)) continue;
    // 異ブロック候補なし → 同ブロック妥協
    pickAndPair(loser, true);
  }

  // Pass2: unconfirmed losers, 任意の残り2位と
  for (const loser of unconfirmedLosers) {
    pickAndPair(loser, true);
  }
}

/**
 * F07: 残った1位通過の対戦相手を決定 (Phase 5-B)。
 *
 * 優先順位 (Phase A 仕様):
 *   ① lower 異ブロック (理想)
 *   ② lower 同ブロック (警告: SAME_BLOCK_FORCED)
 *   ③ rank2 異ブロック (警告: 1ST_VS_2ND)
 *   ④ rank2 同ブロック (警告: 1ST_VS_2ND + SAME_BLOCK_FORCED)
 *   ⑤ losers 異ブロック (警告: 1ST_VS_LOSERS)
 *   ⑥ losers 同ブロック (警告: 1ST_VS_LOSERS + SAME_BLOCK_FORCED)
 *   ⑦ 残り1位 (警告: 1ST_VS_1ST, critical)
 */
function pairFirstSeeds(ctx) {
  if (!ctx) return;

  const remaining1 = (ctx.rank1 || []).filter(r => !ctx.used.has(keyOf(r)));

  for (const r1 of remaining1) {
    if (ctx.used.has(keyOf(r1))) continue;
    let pick = null;
    let typeCode = 'FIRST_VS_LOWER';

    // ① lower 異ブロック
    pick = (ctx.lower || []).find(l =>
      !ctx.used.has(keyOf(l)) && l.blockId !== r1.blockId
    );

    // ② lower 同ブロック (警告)
    if (!pick) {
      pick = (ctx.lower || []).find(l => !ctx.used.has(keyOf(l)));
      if (pick) {
        ctx.placementWarnings.push({
          code: 'SAME_BLOCK_FORCED',
          detail: `${r1.blockId}-1 vs ${pick.blockId}-${pick.rank}`,
        });
      }
    }

    // ③ rank2 異ブロック (1ST_VS_2ND 警告)
    if (!pick) {
      pick = (ctx.rank2 || []).find(r =>
        !ctx.used.has(keyOf(r)) && r.blockId !== r1.blockId
      );
      if (pick) {
        typeCode = 'FIRST_VS_SECOND';
        ctx.placementWarnings.push({
          code: '1ST_VS_2ND',
          detail: `${r1.blockId}-1 vs ${pick.blockId}-2`,
        });
      }
    }

    // ④ rank2 同ブロック
    if (!pick) {
      pick = (ctx.rank2 || []).find(r => !ctx.used.has(keyOf(r)));
      if (pick) {
        typeCode = 'FIRST_VS_SECOND';
        ctx.placementWarnings.push({
          code: '1ST_VS_2ND',
          detail: `${r1.blockId}-1 vs ${pick.blockId}-2 (同ブロック妥協)`,
        });
        if (pick.blockId === r1.blockId) {
          ctx.placementWarnings.push({
            code: 'SAME_BLOCK_FORCED',
            detail: `${r1.blockId}-1 vs ${pick.blockId}-2`,
          });
        }
      }
    }

    // ⑤ losers 異ブロック (1ST_VS_LOSERS 警告)
    if (!pick) {
      pick = (ctx.losers || []).find(l =>
        !ctx.used.has(keyOf(l)) && l.blockId !== r1.blockId
      );
      if (pick) {
        typeCode = 'FIRST_VS_LOSERS';
        ctx.placementWarnings.push({
          code: '1ST_VS_LOSERS',
          detail: `${r1.blockId}-1 vs 復活枠${pick.losersIdx + 1}`,
        });
      }
    }

    // ⑥ losers (任意、同ブロック含む)
    if (!pick) {
      pick = (ctx.losers || []).find(l => !ctx.used.has(keyOf(l)));
      if (pick) {
        typeCode = 'FIRST_VS_LOSERS';
        ctx.placementWarnings.push({
          code: '1ST_VS_LOSERS',
          detail: `${r1.blockId}-1 vs 復活枠${pick.losersIdx + 1}`,
        });
        if (pick.blockId === r1.blockId) {
          ctx.placementWarnings.push({
            code: 'SAME_BLOCK_FORCED',
            detail: `${r1.blockId}-1 vs 復活枠(${pick.blockId})`,
          });
        }
      }
    }

    // ⑦ 残り1位 (critical)
    if (!pick) {
      pick = remaining1.find(r => r !== r1 && !ctx.used.has(keyOf(r)));
      if (pick) {
        typeCode = 'FIRST_VS_FIRST';
        ctx.placementWarnings.push({
          code: '1ST_VS_1ST',
          detail: `${r1.blockId}-1 vs ${pick.blockId}-1`,
          critical: true,
        });
      }
    }

    if (!pick) continue;

    ctx.matchPairs.push({
      top: r1,
      bottom: pick,
      type: typeCode,
      halfHint: ctx.halfAssignment[r1.blockId] || null,
    });
    ctx.used.add(keyOf(r1));
    ctx.used.add(keyOf(pick));
  }
}

/**
 * F08: 残った通過者同士を相互対戦させる (Phase 5-C)。
 *
 * 異ブロック組み合わせを優先。同ブロックは仕方なく組成 (警告)。
 * 復活同士は warning 'LOSERS_VS_LOSERS' を発行。
 *
 * blockId が null (未確定復活) は「異ブロック」として扱う
 * → 確定後にスワップ補正で再評価される設計。
 */
function pairLeftovers(ctx) {
  if (!ctx) return;

  const leftovers = [
    ...(ctx.rank2 || []).filter(r => !ctx.used.has(keyOf(r))),
    ...(ctx.lower || []).filter(l => !ctx.used.has(keyOf(l))),
    ...(ctx.losers || []).filter(l => !ctx.used.has(keyOf(l))),
  ];

  while (leftovers.length >= 2) {
    const first = leftovers.shift();

    // 異ブロック候補を探す (null 同士は異扱い、null vs 確定も異扱い)
    let idx = leftovers.findIndex(x => {
      if (x.blockId == null || first.blockId == null) return true;
      return x.blockId !== first.blockId;
    });

    if (idx === -1) {
      idx = 0;
      ctx.placementWarnings.push({
        code: 'LEFTOVER_SAME_BLOCK',
        detail: `leftover同ブロック対戦: ${first.blockId}`,
      });
    }

    const second = leftovers.splice(idx, 1)[0];

    let typeCode = 'LEFTOVER';
    if (first.sourceType === 'losers' && second.sourceType === 'losers') {
      typeCode = 'LOSERS_VS_LOSERS';
      ctx.placementWarnings.push({
        code: 'LOSERS_VS_LOSERS',
        detail: `復活同士: 枠${first.losersIdx + 1} vs 枠${second.losersIdx + 1}`,
      });
    }

    const halfHint =
      ctx.halfAssignment[first.blockId] ||
      ctx.halfAssignment[second.blockId] ||
      null;

    ctx.matchPairs.push({
      top: first,
      bottom: second,
      type: typeCode,
      halfHint,
    });
    ctx.used.add(keyOf(first));
    ctx.used.add(keyOf(second));
  }
}

/* ============================================================
   P3: 配置・スワップ補正・matches生成・エントリ・既存退避

   ここで v2 ブラケット配置ロジックの主要処理が完成する。
   - placeIntoBracket: 山割り考慮の standardSeedPositions 展開
   - applySwapCorrection: 同ブロックのみの最終補正 (3回試行)
   - generateMatchRecords: matches配列生成 (2回戦以降の空試合含む)
   - _buildTournamentMatches_v2: Step 1〜8 を統合するエントリ
   - _legacyBuildTournamentMatches_v1: 既存ロジックの退避版
   - buildTournamentMatches: placementRule で v1/v2 切替する新エントリ

   placementRule 切替仕様:
     options.placementRule === 'v1-classic'         → 旧ロジック
     options.placementRule === 'v2-losers-vs-second' (デフォルト) → 新ロジック
   ============================================================ */

/**
 * F09: matchPairs を山割りに従って slots に展開する純粋関数。
 *
 * 入力:
 *   matchPairs: [{ top, bottom, type, halfHint }, ...]
 *   bracketSize: 2のべき乗
 *   halfAssignment: { [blockId]: 'L' | 'R' } (使わない、互換維持のため受け取る)
 *
 * 出力: slots[bracketSize], 各要素は advancer/loser/null
 *
 * 配置方針:
 *   - 上山(前半matchCount/2試合) = slots[0..bracketSize/2-1]
 *   - 下山(後半matchCount/2試合) = slots[bracketSize/2..bracketSize-1]
 *   - matchPairs を halfHint='L'/'R'/null で振り分け
 *   - 偏った場合は no-hint で補充、それでも溢れた側は反対側へ
 *   - 山内では type 優先度順 (BYE > LOSERS_VS_2ND > FIRST_VS_LOWER > LEFTOVER > その他)
 *     で上位試合 (matchIdx 小さい方) に配置 → 各山の上位に重要対戦が来る
 *
 *   試合 i は slots[i*2] vs slots[i*2+1] で構成される (generateMatchRecords 仕様)。
 *   上山と下山の勝者は決勝で当たる構造のため、同ブロックを反対山に置けば
 *   準決勝まで対戦が遠ざかる。
 */
function placeIntoBracket(matchPairs, bracketSize, halfAssignment) {
  const slots = new Array(bracketSize).fill(null);
  if (!matchPairs || matchPairs.length === 0 || bracketSize === 0) return slots;

  const matchCount = bracketSize / 2;
  const halfMatchCount = Math.floor(matchCount / 2);

  // bracketSize < 4 の場合 (1試合のみ) は単純配置
  if (bracketSize < 4) {
    const pair = matchPairs[0];
    if (pair) {
      slots[0] = pair.top;
      slots[1] = pair.bottom;
    }
    return slots;
  }

  // halfHint で振り分け
  const upperPairs = [];
  const lowerPairs = [];
  const noHintPairs = [];
  matchPairs.forEach(p => {
    if (p.halfHint === 'L') upperPairs.push(p);
    else if (p.halfHint === 'R') lowerPairs.push(p);
    else noHintPairs.push(p);
  });

  // 不足側に no-hint で補充
  while (upperPairs.length < halfMatchCount && noHintPairs.length > 0) {
    upperPairs.push(noHintPairs.shift());
  }
  while (lowerPairs.length < halfMatchCount && noHintPairs.length > 0) {
    lowerPairs.push(noHintPairs.shift());
  }
  // 残った no-hint を均等配分
  while (noHintPairs.length > 0) {
    if (upperPairs.length <= lowerPairs.length) upperPairs.push(noHintPairs.shift());
    else lowerPairs.push(noHintPairs.shift());
  }
  // 上山溢れたら下山へ、その逆も
  while (upperPairs.length > halfMatchCount) lowerPairs.push(upperPairs.pop());
  while (lowerPairs.length > halfMatchCount) upperPairs.push(lowerPairs.pop());

  // type 優先度ソート (上位試合に重要対戦)
  const typePriority = {
    'BYE': 0,
    'LOSERS_VS_2ND': 1,
    'FIRST_VS_LOWER': 2,
    'LEFTOVER': 3,
    'FIRST_VS_SECOND': 4,
    'FIRST_VS_LOSERS': 5,
    'LOSERS_VS_LOSERS': 6,
    'FIRST_VS_FIRST': 7,
  };
  const byPriority = (a, b) => {
    const pa = typePriority[a.type] ?? 99;
    const pb = typePriority[b.type] ?? 99;
    return pa - pb;
  };
  upperPairs.sort(byPriority);
  lowerPairs.sort(byPriority);

  // 上山配置: 試合 0..halfMatchCount-1 → slots[0..halfMatchCount*2-1]
  for (let i = 0; i < halfMatchCount; i++) {
    const pair = upperPairs[i];
    if (!pair) continue;
    slots[i * 2] = pair.top;
    slots[i * 2 + 1] = pair.bottom;
  }

  // 下山配置: 試合 halfMatchCount..matchCount-1 → slots[halfMatchCount*2..bracketSize-1]
  for (let i = 0; i < halfMatchCount; i++) {
    const pair = lowerPairs[i];
    if (!pair) continue;
    const matchIdx = halfMatchCount + i;
    slots[matchIdx * 2] = pair.top;
    slots[matchIdx * 2 + 1] = pair.bottom;
  }

  return slots;
}

/**
 * F10: 最後の砦としての同ブロック対戦回避スワップ補正。
 *
 * 責務分担 (ChatGPT 設計レビュー反映):
 *   - ペアリング段 (Phase 4-A〜D) で 90% 解決
 *   - 配置段 (placeIntoBracket) で 9% 解決
 *   - スワップ補正でカバーするのは残り 1% のみ
 *
 * 仕様:
 *   - 同ブロックのみを対象 (1位同士・復活同士は Phase 4 で解決済みのはず)
 *   - 試行回数 3回 (v1 の 10回 から大幅削減)
 *   - 解決不能なら placementWarnings に critical を記録して終了
 *
 * 副作用: slots を mutate、placementWarnings に追加
 */
function applySwapCorrection(slots, bracketSize, placementWarnings) {
  const MAX_ATTEMPTS = 3;
  const matchCount = bracketSize / 2;

  const isSameBlock = (a, b) => {
    if (!a || !b) return false;
    if (a.sourceType !== 'block' || b.sourceType !== 'block') return false;
    return a.blockId === b.blockId;
  };

  const findConflicts = () => {
    const conflicts = [];
    for (let mi = 0; mi < matchCount; mi++) {
      const top = slots[mi * 2];
      const bot = slots[mi * 2 + 1];
      if (isSameBlock(top, bot)) {
        conflicts.push({ matchIdx: mi, topPos: mi * 2, botPos: mi * 2 + 1 });
      }
    }
    return conflicts;
  };

  const matchOf = (idx) => Math.floor(idx / 2);

  const canSwap = (idxA, idxB) => {
    if (idxA === idxB) return false;
    if (matchOf(idxA) === matchOf(idxB)) return false;
    const valA = slots[idxA];
    const valB = slots[idxB];
    const partnerA = slots[idxA % 2 === 0 ? idxA + 1 : idxA - 1];
    const partnerB = slots[idxB % 2 === 0 ? idxB + 1 : idxB - 1];
    // 交換後の試合 A: valB と partnerA / 試合 B: valA と partnerB
    if (isSameBlock(valB, partnerA)) return false;
    if (isSameBlock(valA, partnerB)) return false;
    return true;
  };

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const conflicts = findConflicts();
    if (conflicts.length === 0) return;

    let resolvedAny = false;
    for (const conflict of conflicts) {
      let swapped = false;
      // bottom 側を全位置と入れ替え試行
      for (let k = 0; k < bracketSize; k++) {
        if (canSwap(conflict.botPos, k)) {
          [slots[conflict.botPos], slots[k]] = [slots[k], slots[conflict.botPos]];
          resolvedAny = true;
          swapped = true;
          break;
        }
      }
      // それでもダメなら top 側
      if (!swapped) {
        for (let k = 0; k < bracketSize; k++) {
          if (canSwap(conflict.topPos, k)) {
            [slots[conflict.topPos], slots[k]] = [slots[k], slots[conflict.topPos]];
            resolvedAny = true;
            break;
          }
        }
      }
    }

    if (!resolvedAny) {
      const remaining = findConflicts();
      placementWarnings.push({
        code: 'SAME_BLOCK_UNAVOIDABLE',
        count: remaining.length,
        detail: remaining.map(c => {
          const t = slots[c.topPos];
          const b = slots[c.botPos];
          return `${t?.blockId}${t?.rank || ''} vs ${b?.blockId}${b?.rank || ''}`;
        }).join(', '),
        critical: true,
      });
      return;
    }
  }

  // MAX_ATTEMPTS 到達
  const remaining = findConflicts();
  if (remaining.length > 0) {
    placementWarnings.push({
      code: 'SWAP_MAX_ATTEMPTS',
      count: remaining.length,
      critical: true,
    });
  }
}

/**
 * F11: slots から matches[] 配列を生成する純粋関数。
 *
 * 1回戦: slots を2つずつペアにして matches に積む。BYE 判定込み。
 * 2回戦以降: 空試合 (sourceType:'winner') を生成、parent matchId 紐付け。
 *
 * 構造は既存 v1 と互換 (resolveTournamentSlot や Firestore スキーマに影響なし)。
 */
function generateMatchRecords(slots, bracketSize, totalRounds) {
  const matches = [];

  // 1回戦
  for (let i = 0; i < bracketSize / 2; i++) {
    const topP = slots[i * 2];
    const bottomP = slots[i * 2 + 1];
    let winner = null;
    let isBye = false;
    if (topP && !bottomP) { winner = 'top'; isBye = true; }
    if (!topP && bottomP) { winner = 'bottom'; isBye = true; }
    matches.push({
      id: `r1-m${i + 1}`,
      round: 1,
      position: i + 1,
      top: topP,
      bottom: bottomP,
      score: null,
      winner,
      confirmed: false,
      isBye,
    });
  }

  // 2回戦以降の空試合
  for (let r = 2; r <= totalRounds; r++) {
    const matchesInRound = bracketSize / Math.pow(2, r);
    for (let p = 1; p <= matchesInRound; p++) {
      matches.push({
        id: `r${r}-m${p}`,
        round: r,
        position: p,
        top: { sourceType: 'winner', fromMatchId: `r${r - 1}-m${(p - 1) * 2 + 1}` },
        bottom: { sourceType: 'winner', fromMatchId: `r${r - 1}-m${(p - 1) * 2 + 2}` },
        score: null,
        winner: null,
        confirmed: false,
        isBye: false,
      });
    }
  }

  return matches;
}

/**
 * F12: v2 ブラケット生成のエントリ関数 (Step 1〜8 を統合)。
 *
 * 内部処理順:
 *   Step 1: classifyAdvancers     → rank1, rank2, lower, losers
 *   Step 2: computeBracketMeta    → N, bracketSize, totalRounds, byeCount
 *   Step 3: assignBlockHalves     → halfAssignment
 *   Step 4: assignByes (予約付き)
 *   Step 5-A: pairLosersWithSeconds
 *   Step 5-B: pairFirstSeeds
 *   Step 5-C: pairLeftovers
 *   Step 6: placeIntoBracket
 *   Step 7: applySwapCorrection
 *   Step 8: generateMatchRecords
 *
 * 返り値: { matches, bracketSize, totalRounds, N, placementRule, placementWarnings }
 *
 * placementWarnings は「配置警告」: 実際のペアリング/配置で発生した妥協
 * (同ブロック対戦、1位vs2位、1位vs復活など)。
 * 「設定警告 (settingWarnings)」は validateBracketComposition が別に返す。
 */
function _buildTournamentMatches_v2(advancers, losersAdvancers) {
  const { rank1, rank2, lower, losers } = classifyAdvancers(advancers, losersAdvancers);
  const meta = computeBracketMeta(rank1, rank2, lower, losers);

  if (meta.N < 2) {
    return {
      matches: [],
      bracketSize: 0,
      totalRounds: 0,
      N: meta.N,
      placementRule: 'v2-losers-vs-second',
      placementWarnings: [],
    };
  }

  const halfAssignment = assignBlockHalves(advancers);

  const ctx = {
    rank1: [...rank1],
    rank2: [...rank2],
    lower: [...lower],
    losers: [...losers],
    used: new Set(),
    matchPairs: [],
    placementWarnings: [],
    halfAssignment,
    meta,
  };

  assignByes(ctx);
  pairLosersWithSeconds(ctx);
  pairFirstSeeds(ctx);
  pairLeftovers(ctx);

  const slots = placeIntoBracket(ctx.matchPairs, meta.bracketSize, halfAssignment);
  applySwapCorrection(slots, meta.bracketSize, ctx.placementWarnings);
  const matches = generateMatchRecords(slots, meta.bracketSize, meta.totalRounds);

  return {
    matches,
    bracketSize: meta.bracketSize,
    totalRounds: meta.totalRounds,
    N: meta.N,
    placementRule: 'v2-losers-vs-second',
    placementWarnings: ctx.placementWarnings,
  };
}

/* ============================================================
   既存ロジック退避 (v1-classic)

   この関数は v1 当時のブラケット生成ロジックを保持するためのもの。
   コード本体は当時のまま、関数名のみ _legacyBuildTournamentMatches_v1 に変更。
   placementRule: 'v1-classic' が指定された時にのみ呼ばれる。
   既存ブラケット (placementRule 未定義) も生成時はこちらだった。
   ============================================================ */

// 通過者+復活者からトーナメント全試合を生成 (v1: 旧ロジック)
// 配置: 標準シード配置(上位シード散らし) + 同ブロック対戦回避スワップ
// シード順: 1位群(blockId昇順) → 2位群 → 3位群... → 復活戦勝者
// → BYEが上位シードに来る = 1位群が優先的にBYE取得
// pair自体は保存せず blockId+rank のみ保存(予選結果が変わったら自動追従)
function _legacyBuildTournamentMatches_v1(advancers, losersAdvancers) {
  // 通過者をrank昇順、blockId昇順でシード化
  const advData = (advancers || []).map(a => ({
    sourceType: 'block',
    blockId: a.blockId,
    rank: a.rank,
  })).sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    return a.blockId < b.blockId ? -1 : 1;
  });
  const losersData = (losersAdvancers || []).map((_, i) => ({
    sourceType: 'losers',
    losersIdx: i,
  }));

  // シード順: 通過者 → 復活戦(末尾)
  const seeded = [...advData, ...losersData];
  const N = seeded.length;
  if (N < 2) return { matches: [], bracketSize: 0, totalRounds: 0, N: 0 };

  const totalRounds = Math.ceil(Math.log2(N));
  const bracketSize = Math.pow(2, totalRounds);

  // 標準シード位置に配置(BYEは末尾シード位置 → 上位シードと対戦するBYE扱い)
  const seedPositions = standardSeedPositions(bracketSize);
  const slots = new Array(bracketSize).fill(null);
  for (let i = 0; i < N; i++) {
    const slotIdx = seedPositions[i] - 1;
    slots[slotIdx] = seeded[i];
  }

  // スワップ補正: 全位置間で交換可能にしてproblematic対戦を解消
  // 優先順位: ①同ブロック対戦(絶対) ②1位同士 ③復活戦同士
  const matchCount = bracketSize / 2;
  const matchOf = (idx) => Math.floor(idx / 2);
  const partnerOf = (idx) => idx % 2 === 0 ? idx + 1 : idx - 1;

  const isProblematicMatch = (top, bot) => {
    if (!top || !bot) return false; // 片方BYEなら問題なし
    if (top.sourceType === 'block' && bot.sourceType === 'block') {
      if (top.blockId === bot.blockId) return true;       // 同ブロック
      if (top.rank === 1 && bot.rank === 1) return true;  // 1位同士
    }
    if (top.sourceType === 'losers' && bot.sourceType === 'losers') return true; // 復活戦同士
    return false;
  };

  // idxAとidxBを交換しても両試合がOKになるか
  const canSwap = (idxA, idxB) => {
    if (idxA === idxB) return false;
    if (matchOf(idxA) === matchOf(idxB)) return false; // 同じ試合内のスワップは意味なし
    const partnerAIdx = partnerOf(idxA);
    const partnerBIdx = partnerOf(idxB);
    const valA = slots[idxA];
    const valB = slots[idxB];
    const partnerA = slots[partnerAIdx];
    const partnerB = slots[partnerBIdx];
    // 交換後: idxA位置にvalB、idxB位置にvalA
    // 試合A: (もしidxAがtopなら) valB vs partnerA / (idxAがbottomなら) partnerA vs valB
    const newA = idxA % 2 === 0 ? [valB, partnerA] : [partnerA, valB];
    const newB = idxB % 2 === 0 ? [valA, partnerB] : [partnerB, valA];
    if (isProblematicMatch(newA[0], newA[1])) return false;
    if (isProblematicMatch(newB[0], newB[1])) return false;
    return true;
  };

  for (let attempt = 0; attempt < 10; attempt++) {
    let anyChanged = false;
    for (let mi = 0; mi < matchCount; mi++) {
      const ti = mi * 2, bi = mi * 2 + 1;
      if (!isProblematicMatch(slots[ti], slots[bi])) continue;
      // bottom側を全位置と入れ替え試行
      let swapped = false;
      for (let k = 0; k < bracketSize; k++) {
        if (canSwap(bi, k)) {
          [slots[bi], slots[k]] = [slots[k], slots[bi]];
          anyChanged = true;
          swapped = true;
          break;
        }
      }
      // それでもダメならtop側を全位置と入れ替え試行
      if (!swapped) {
        for (let k = 0; k < bracketSize; k++) {
          if (canSwap(ti, k)) {
            [slots[ti], slots[k]] = [slots[k], slots[ti]];
            anyChanged = true;
            break;
          }
        }
      }
    }
    if (!anyChanged) break;
  }

  const matches = [];

  // 1回戦
  for (let i = 0; i < bracketSize / 2; i++) {
    const topP = slots[i * 2];
    const bottomP = slots[i * 2 + 1];
    let winner = null;
    let isBye = false;
    if (topP && !bottomP) { winner = 'top'; isBye = true; }
    if (!topP && bottomP) { winner = 'bottom'; isBye = true; }
    matches.push({
      id: `r1-m${i + 1}`,
      round: 1,
      position: i + 1,
      top: topP,
      bottom: bottomP,
      score: null,
      winner,
      confirmed: false,
      isBye,
    });
  }

  // 2回戦以降の空試合
  for (let r = 2; r <= totalRounds; r++) {
    const matchesInRound = bracketSize / Math.pow(2, r);
    for (let p = 1; p <= matchesInRound; p++) {
      matches.push({
        id: `r${r}-m${p}`,
        round: r,
        position: p,
        top: { sourceType: 'winner', fromMatchId: `r${r - 1}-m${(p - 1) * 2 + 1}` },
        bottom: { sourceType: 'winner', fromMatchId: `r${r - 1}-m${(p - 1) * 2 + 2}` },
        score: null,
        winner: null,
        confirmed: false,
        isBye: false,
      });
    }
  }

  return { matches, bracketSize, totalRounds, N };
}

/**
 * F13: 新エントリ関数 (placementRule で v1/v2 切替)。
 *
 * 既存呼び出し側 (handleBuildBracket 等) はそのまま buildTournamentMatches を呼ぶ。
 * options.placementRule で挙動を選択:
 *   - 'v2-losers-vs-second' (デフォルト): 新ロジック (復活vs2位優先・山割り考慮)
 *   - 'v1-classic': 旧ロジック (順詰め+スワップ補正)
 *
 * 後方互換: options を省略すると v2 で動作。
 * 既存ブラケットの再生成時もデフォルトで v2 にアップグレードされる。
 */
function buildTournamentMatches(advancers, losersAdvancers, options) {
  const opts = options || {};
  const rule = opts.placementRule || 'v2-losers-vs-second';

  if (rule === 'v1-classic') {
    const result = _legacyBuildTournamentMatches_v1(advancers, losersAdvancers);
    return {
      matches: result.matches,
      bracketSize: result.bracketSize,
      totalRounds: result.totalRounds,
      N: result.N,
      placementRule: 'v1-classic',
      placementWarnings: [],
    };
  }

  return _buildTournamentMatches_v2(advancers, losersAdvancers);
}

/* ============================================================
   P5b: 復活枠選択モーダルのプレビュー

   previewLosersOpponent は「もしこの候補を選んだら誰と当たるか」を
   実シミュレーションで返す純粋関数。
   候補数だけ _buildTournamentMatches_v2 を呼ぶ (N回案、ChatGPT 推奨)。

   負荷対策:
     - useMemo で losersInput 変化時のみ発火 (モーダル開閉時のみ)
     - try/catch で失敗時 fallback (警告マーク無し、モーダル動作は維持)

   候補数=40 (最大想定) でも 100ms 程度、UX上問題なし。
   ============================================================ */

/**
 * F14: 復活枠 losersIdx に prospectiveLoser を当てた場合の対戦相手を予測する純粋関数。
 *
 * 仕様:
 *   現在の advancers + 既存 losersResults をベースに、losersIdx の位置だけ
 *   prospectiveLoser で上書きして _buildTournamentMatches_v2 を1回走らせる。
 *   返り値の matches から該当 losersIdx の対戦相手を取り出す。
 *
 * 入力:
 *   losersIdx: number (プレビューしたい復活枠番号)
 *   advancers: [{ blockId, rank }, ...] (確定済み通過者)
 *   losersResults: [{ idx, blockId, ... }, ...] (既存復活枠選択)
 *   losersCount: number (進行設定の復活枠総数)
 *   prospectiveLoser: { blockId } | null (今選択中の候補、未選択なら null)
 *
 * 出力: { opponent, sameBlock, isBye } | null
 *   opponent = 対戦相手のスロット情報 (sourceType: 'block' or 'losers')
 *   sameBlock = prospectiveLoser と opponent が同ブロックか
 *   isBye = この復活枠が BYE 当たりか
 *   失敗時は null を返す (呼び出し側で警告マークを出さない fallback)
 */
function previewLosersOpponent(losersIdx, advancers, losersResults, losersCount, prospectiveLoser) {
  try {
    // 仮の losersAdvancers を losersCount 個ぶん組み立てる
    // 既存 losersResults を base に、losersIdx の位置だけ prospectiveLoser で上書き
    const fakeLosers = [];
    for (let i = 0; i < losersCount; i++) {
      if (i === losersIdx) {
        // プレビュー対象: prospectiveLoser の blockId を使う、無ければ null
        fakeLosers.push({
          idx: i,
          blockId: prospectiveLoser?.blockId || null,
        });
      } else {
        const existing = (losersResults || []).find(r => r.idx === i);
        fakeLosers.push({
          idx: i,
          blockId: existing?.blockId || null,
        });
      }
    }

    const result = _buildTournamentMatches_v2(advancers, fakeLosers);
    if (!result || !result.matches) return null;

    // 1回戦の matches から losersIdx 該当を探す
    const match = result.matches.find(m =>
      m.round === 1 &&
      ((m.top?.sourceType === 'losers' && m.top.losersIdx === losersIdx) ||
        (m.bottom?.sourceType === 'losers' && m.bottom.losersIdx === losersIdx))
    );

    if (!match) return null; // 配置されていない

    // BYE 当たり判定
    if (match.isBye) {
      return { opponent: null, sameBlock: false, isBye: true };
    }

    // 対戦相手 = me ではない方
    const meIsTop = match.top?.sourceType === 'losers' && match.top.losersIdx === losersIdx;
    const opponent = meIsTop ? match.bottom : match.top;

    // 同ブロック判定 (prospectiveLoser.blockId と opponent.blockId)
    const sameBlock = !!(
      prospectiveLoser?.blockId &&
      opponent?.sourceType === 'block' &&
      opponent.blockId === prospectiveLoser.blockId
    );

    return { opponent, sameBlock, isBye: false };
  } catch (err) {
    // 計算失敗 → fallback (警告マーク無し)
    return null;
  }
}

/* ============================================================
   v2 ブラケット配置ロジック (P1+P2+P3+P4+P5a+P5b: 全機能完成) ここまで
   後続予定: 実機テスト → デプロイ
   ============================================================ */

// 試合カードのスロットから実参加者を動的解決(blocksの最新状態を見る)
function resolveTournamentSlot(slot, matchesMap, blocks, losersResults) {
  if (!slot) return null;
  if (slot.sourceType === 'block') {
    const block = (blocks || []).find(b => b.id === slot.blockId);
    if (!block) {
      return { sourceType: 'block', blockId: slot.blockId, rank: slot.rank, pair: null, confirmed: false };
    }
    if (!block.confirmed) {
      return { sourceType: 'block', blockId: slot.blockId, rank: slot.rank, pair: null, confirmed: false };
    }
    const ranked = computeBlockRanking(block);
    const advancer = ranked[slot.rank - 1];
    if (!advancer) {
      return { sourceType: 'block', blockId: slot.blockId, rank: slot.rank, pair: null, confirmed: false };
    }
    return { sourceType: 'block', blockId: slot.blockId, rank: slot.rank, pair: advancer.pair, confirmed: true };
  }
  if (slot.sourceType === 'losers') {
    const result = (losersResults || []).find(r => r.idx === slot.losersIdx);
    if (result && result.pair) {
      return { sourceType: 'losers', losersIdx: slot.losersIdx, pair: result.pair, confirmed: true };
    }
    return { sourceType: 'losers', losersIdx: slot.losersIdx, pair: null, confirmed: false };
  }
  if (slot.sourceType === 'winner') {
    const parent = matchesMap[slot.fromMatchId];
    if (!parent) return null;
    if (parent.winner === 'top') return resolveTournamentSlot(parent.top, matchesMap, blocks, losersResults);
    if (parent.winner === 'bottom') return resolveTournamentSlot(parent.bottom, matchesMap, blocks, losersResults);
    return null;
  }
  return null;
}

// ラウンド名取得(末尾から逆算)
function getTournamentRoundName(round, totalRounds) {
  const fromEnd = totalRounds - round;
  if (fromEnd === 0) return '決勝';
  if (fromEnd === 1) return '準決勝';
  if (fromEnd === 2) return '準々決勝';
  return `${round}回戦`;
}

/* ============================================================
   ロゴ
   ============================================================ */
function HeaderLogo() {
  return <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="HTS" className="header-logo" />;
}

function LargeLogo() {
  return (
    <div className="auth-logo-large">
      <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="HTS" />
    </div>
  );
}

/* ============================================================
   認証フック
   ============================================================ */
function useAuthUser() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shopInfo, setShopInfo] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) { setShopInfo(null); return; }
    const q = query(collection(db, 'users'), where('uid', '==', user.uid));
    const unsub = onSnapshot(q, (snap) => {
      if (snap.docs.length > 0) {
        setShopInfo(snap.docs[0].data());
      }
    });
    return () => unsub();
  }, [user]);

  return {
    user,
    loading,
    isAdmin: user && ADMIN_EMAILS.includes(user.email),
    shopName: shopInfo?.shopName || user?.email || ''
  };
}

/* ============================================================
   ハンバーガーメニュー（汎用）
   ============================================================ */
function HamburgerDrawer({ open, onClose, shopName, email, isAdmin, onNavigate, onLogout, extraItems }) {
  return (
    <>
      <div className={`drawer-overlay no-print ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`drawer no-print ${open ? 'open' : ''}`}>
        <div className="drawer-header">
          <h2>メニュー</h2>
          <button className="drawer-close" onClick={onClose} aria-label="閉じる">
            <X size={20} />
          </button>
        </div>

        <div className="drawer-user">
          <div className="drawer-user-shop" translate="no">{shopName || '-'}</div>
          {email && <div className="drawer-user-email" translate="no">{email}</div>}
        </div>

        <div className="drawer-items">
          {Array.isArray(extraItems) && extraItems.length > 0 && (
            <>
              {extraItems.map((item, idx) => (
                <button
                  key={idx}
                  className={`drawer-item ${item.accent ? 'drawer-item-accent' : ''}`}
                  onClick={() => { item.onClick(); onClose(); }}
                >
                  {item.icon} {item.label}
                </button>
              ))}
              <div className="drawer-separator"></div>
            </>
          )}
          <button className="drawer-item" onClick={() => { onNavigate('/'); onClose(); }}>
            <Home size={18} /> ホーム
          </button>
          <button className="drawer-item" onClick={() => { onNavigate('/help'); onClose(); }}>
            <HelpCircle size={18} /> 使い方
          </button>
          {isAdmin && (
            <button className="drawer-item" onClick={() => { onNavigate('/admin'); onClose(); }}>
              <Shield size={18} /> Admin 受付
            </button>
          )}
          <button className="drawer-item danger" onClick={() => { onClose(); onLogout(); }}>
            <LogOut size={18} /> ログアウト
          </button>
        </div>
      </div>
    </>
  );
}

/* ============================================================
   ログイン画面
   ============================================================ */
function LoginScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const emailName = useMemo(rndName, []);
  const pwName = useMemo(rndName, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!email || !password) {
      setErrorMsg('メールアドレスとパスワードを入力してください');
      return;
    }
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/');
    } catch (err) {
      const code = err.code || '';
      if (code.includes('invalid-email')) setErrorMsg('メールアドレスの形式が正しくありません');
      else if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
        setErrorMsg('メールアドレスまたはパスワードが違います');
      }
      else setErrorMsg('エラー：' + (err.message || '不明なエラー'));
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setErrorMsg('メールアドレスを入力してください');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      alert('パスワードリセット用のメールを送信しました。メールをご確認ください。');
    } catch (err) {
      setErrorMsg('リセットメール送信に失敗しました：' + err.message);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <LargeLogo />
        <h1 className="auth-title">House Tournament</h1>
        <p className="auth-subtitle">店舗ログイン</p>

        <form onSubmit={handleSubmit} className="auth-form" autoComplete="off">
          <div className="auth-field">
            <label>メールアドレス</label>
            <input
              type="email" name={emailName} value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@example.com"
              autoComplete="off" translate="no" data-form-type="other"
            />
          </div>
          <div className="auth-field">
            <label>パスワード</label>
            <input
              type="password" name={pwName} value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••"
              autoComplete="new-password" translate="no" data-form-type="other"
            />
          </div>
          {errorMsg && <div className="auth-error">{errorMsg}</div>}
          <button type="submit" className="btn-primary btn-large" disabled={busy}>
            {busy ? '処理中...' : 'ログイン'}
          </button>
        </form>

        <div className="auth-actions">
          <button className="auth-link" onClick={handleResetPassword}>パスワードを忘れた</button>
          <Link to="/admin-login" className="auth-link-admin">
            <Shield size={14} /> Admin ログイン
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Adminログイン画面
   ============================================================ */
function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const emailName = useMemo(rndName, []);
  const pwName = useMemo(rndName, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!email || !password) {
      setErrorMsg('メールアドレスとパスワードを入力してください');
      return;
    }
    if (!ADMIN_EMAILS.includes(email)) {
      setErrorMsg('Admin権限のないメールアドレスです');
      return;
    }
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/admin');
    } catch (err) {
      const code = err.code || '';
      if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
        setErrorMsg('メールアドレスまたはパスワードが違います');
      } else {
        setErrorMsg('エラー：' + (err.message || '不明なエラー'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card admin-card">
        <LargeLogo />
        <h1 className="auth-title"><Shield size={20} color="#c0392b" /> Admin Login</h1>
        <p className="auth-subtitle">管理者ログイン</p>

        <form onSubmit={handleSubmit} className="auth-form" autoComplete="off">
          <div className="auth-field">
            <label>Adminメールアドレス</label>
            <input
              type="email" name={emailName} value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off" translate="no" data-form-type="other"
            />
          </div>
          <div className="auth-field">
            <label>パスワード</label>
            <input
              type="password" name={pwName} value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password" translate="no" data-form-type="other"
            />
          </div>
          {errorMsg && <div className="auth-error">{errorMsg}</div>}
          <button type="submit" className="btn-danger-large" disabled={busy}>
            {busy ? '処理中...' : 'Admin ログイン'}
          </button>
        </form>

        <div className="auth-actions">
          <Link to="/login" className="auth-link">通常のログイン画面に戻る</Link>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Admin受付画面
   ============================================================ */
function AdminPanel() {
  const { user, isAdmin, loading } = useAuthUser();
  const navigate = useNavigate();
  const [shopName, setShopName] = useState('');
  const [shopEmail, setShopEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState([]);
  const [allShops, setAllShops] = useState([]);
  const nameField1 = useMemo(rndName, []);
  const nameField2 = useMemo(rndName, []);
  const nameField3 = useMemo(rndName, []);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/admin-login');
    else if (!isAdmin) {
      alert('Admin権限がありません');
      navigate('/');
    }
  }, [user, isAdmin, loading, navigate]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    const unsub = onSnapshot(collection(db, 'users'), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setAllShops(list);
    });
    return () => unsub();
  }, [user, isAdmin]);

  const generateTempPassword = () => Math.floor(100000 + Math.random() * 900000).toString();
  const handleAutoGen = () => setTempPassword(generateTempPassword());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    if (!shopName.trim() || !shopEmail.trim() || !tempPassword.trim()) {
      setErrorMsg('すべての項目を入力してください');
      return;
    }
    if (!/^\d{6}$/.test(tempPassword)) {
      setErrorMsg('仮パスワードは6桁の数字で入力してください');
      return;
    }
    setBusy(true);
    try {
      const createShopFn = httpsCallable(functions, 'createShopAccount');
      await createShopFn({
        email: shopEmail.trim(),
        password: tempPassword,
        shopName: shopName.trim(),
      });
      setRegistered([
        { shopName: shopName.trim(), email: shopEmail.trim(), password: tempPassword, time: new Date() },
        ...registered
      ]);
      setSuccessMsg(`「${shopName}」を登録しました！`);
      setShopName('');
      setShopEmail('');
      setTempPassword('');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('email-already-in-use')) setErrorMsg('このメールアドレスは既に登録されています');
      else if (msg.includes('invalid-email')) setErrorMsg('メールアドレスの形式が正しくありません');
      else setErrorMsg('エラー：' + (msg || '不明なエラー'));
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('Adminログアウトしますか?')) return;
    await signOut(auth);
    navigate('/login');
  };

  const copyInfo = (r) => {
    const text = `店舗：${r.shopName}\nメール：${r.email}\n仮パスワード：${r.password}\n\n※初回ログイン後にパスワードを変更してください。`;
    navigator.clipboard.writeText(text).then(() => {
      alert('クリップボードにコピーしました');
    }).catch(() => {
      alert(text);
    });
  };

  const deleteShopAccount = async (shop) => {
    if (!window.confirm(`「${shop.shopName}」を削除しますか?\n\nこの操作は元に戻せません。`)) return;
    try {
      if (shop.uid) {
        // Cloud Function で Auth アカウント + /users ドキュメントを両方削除
        // （同じメールアドレスで再登録できるようにするため）
        const callDelete = httpsCallable(functions, 'deleteShopAccount');
        await callDelete({ targetUid: shop.uid });
      } else {
        // 旧データ等で uid が無い場合のフォールバック：Firestore のみ削除
        // （Auth は別途 Console で手動削除が必要）
        await deleteDoc(doc(db, 'users', shop.id));
      }
      alert('店舗を削除しました。');
    } catch (err) {
      const code = err?.code || '';
      const msg = err?.message || '不明なエラー';
      alert('削除に失敗しました：' + (code ? `[${code}] ` : '') + msg);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
  };

  if (loading || !user || !isAdmin) return <div className="page"><div className="loading">読み込み中...</div></div>;

  return (
    <div className="page admin-page no-select">
      <header className="header admin-header">
        <h1><Shield size={20} /> Admin 受付</h1>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => navigate('/')}>通常画面へ</button>
          <button className="btn-secondary" onClick={handleLogout}>
            <LogOut size={14} /> ログアウト
          </button>
        </div>
      </header>

      <div className="container">
        <div className="admin-section">
          <h2 className="section-title">店舗の新規登録</h2>
          <form onSubmit={handleSubmit} className="admin-form" autoComplete="off">
            <div className="auth-field">
              <label>店舗名</label>
              <input
                type="text" name={nameField1} value={shopName}
                onChange={(e) => setShopName(e.target.value)}
                placeholder="ダーツバー○○"
                autoComplete="off" translate="no" data-form-type="other"
              />
            </div>
            <div className="auth-field">
              <label>店舗オーナーのメールアドレス</label>
              <input
                type="email" name={nameField2} value={shopEmail}
                onChange={(e) => setShopEmail(e.target.value)}
                placeholder="owner@example.com"
                autoComplete="off" translate="no" data-form-type="other"
              />
            </div>
            <div className="auth-field">
              <label>仮パスワード(6桁数字)</label>
              <div className="temp-pw-row">
                <input
                  type="text" name={nameField3} value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  placeholder="例：123456"
                  autoComplete="off" translate="no" data-form-type="other"
                  inputMode="numeric" maxLength={6}
                />
                <button type="button" onClick={handleAutoGen}>6桁数字で自動生成</button>
              </div>
              <small className="field-note">※6桁数字で発行。店舗側に「初回ログイン後にパスワードを変更してください」と伝えてください。</small>
            </div>
            {errorMsg && <div className="auth-error">{errorMsg}</div>}
            {successMsg && <div className="auth-success">{successMsg}</div>}
            <button type="submit" className="btn-primary btn-large" disabled={busy}>
              {busy ? '登録中...' : '店舗を登録する'}
            </button>
          </form>
        </div>

        {registered.length > 0 && (
          <div className="admin-section">
            <h2 className="section-title">本日登録した店舗({registered.length}件)</h2>
            <p className="field-note" style={{ marginBottom: 12 }}>※仮パスワード付き。ページを閉じると消えます。</p>
            <ul className="registered-list">
              {registered.map((r, i) => (
                <li key={i} className="registered-item">
                  <div className="registered-info">
                    <div className="reg-shop" translate="no">{r.shopName}</div>
                    <div className="reg-detail" translate="no">
                      <span>メール：{r.email}</span>
                      <span>仮PW：<strong>{r.password}</strong></span>
                    </div>
                  </div>
                  <button className="btn-primary" style={{ padding: '7px 13px', fontSize: 13, width: 'auto', margin: 0 }} onClick={() => copyInfo(r)}>コピー</button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="admin-section">
          <h2 className="section-title">これまでの登録店舗(累計 {allShops.length}件)</h2>
          {allShops.length === 0 ? (
            <div className="empty">登録された店舗はまだありません</div>
          ) : (
            <ul className="all-shops-list">
              {allShops.map((s) => (
                <li key={s.id} className="all-shops-item">
                  <div className="all-shop-info">
                    <div className="all-shop-name" translate="no">{s.shopName || '(店舗名未設定)'}</div>
                    <div className="all-shop-detail" translate="no">
                      <span>{s.email}</span>
                      <span className="all-shop-date">登録: {formatDate(s.createdAt)}</span>
                    </div>
                  </div>
                  <button className="btn-danger-small" onClick={() => deleteShopAccount(s)}>削除</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PublicRobinView : 参加客向けロビン表 閲覧専用ページ (/view/:id)
// - publicEvents/{id} を onSnapshot 購読 (ログイン不要)
// - 順位表 + 対戦結果 を表示 (通過予測・編集・書き込みは持たない)
// - computeStats は本体と同一ロジックを移植 (順位を本体と完全一致させる)
// - schemaVersion で将来形式差を吸収 (v1: NEXT 非表示)
// ============================================================
function PublicRobinView() {
  const { id } = useParams();
  const [pub, setPub] = useState(undefined); // undefined=読込中 / null=無し / obj=取得
  const [blockIdx, setBlockIdx] = useState(0);

  useEffect(() => {
    if (!id) { setPub(null); return; }
    const ref = doc(db, 'publicEvents', id);
    const unsub = onSnapshot(
      ref,
      (snap) => { setPub(snap.exists() ? snap.data() : null); },
      (err) => { console.warn('publicEvents read failed:', id, err); setPub(null); } // 読めない(未公開/権限)は「無し」扱い
    );
    return () => unsub();
  }, [id]);

  // 公開用 順位計算 (本体 computeStats と同一ロジック / 入力は block.pairs.length と block.scores のみ)
  const computePublicStats = (block) => {
    if (!block || !block.pairs) return [];
    const n = block.pairs.length;
    const stats = block.pairs.map((_, i) => ({
      teamNo: i + 1, setsWon: 0, legsWon: 0, legsLost: 0,
    }));
    const scores = block.scores || {};
    Object.entries(scores).forEach(([key, value]) => {
      if (typeof value !== 'string') return;
      const [t1, t2] = key.split('-').map((s) => parseInt(s, 10));
      const [a, b] = value.split('-').map((s) => parseInt(s, 10));
      if (isNaN(t1) || isNaN(t2) || isNaN(a) || isNaN(b)) return;
      const i1 = t1 - 1, i2 = t2 - 1;
      if (i1 < 0 || i2 < 0 || i1 >= n || i2 >= n) return;
      stats[i1].legsWon += a; stats[i1].legsLost += b;
      stats[i2].legsWon += b; stats[i2].legsLost += a;
      if (a === 2 && b < 2) stats[i1].setsWon += 1;
      if (b === 2 && a < 2) stats[i2].setsWon += 1;
    });
    const directWinDelta = (teamA, teamB) => {
      const x = Math.min(teamA, teamB), y = Math.max(teamA, teamB);
      const val = scores[`${x}-${y}`];
      if (!val) return 0;
      const [s1, s2] = String(val).split('-').map((s) => parseInt(s, 10));
      if (isNaN(s1) || isNaN(s2)) return 0;
      const teamAisSmaller = (teamA === x);
      const aScore = teamAisSmaller ? s1 : s2;
      const bScore = teamAisSmaller ? s2 : s1;
      if (aScore === 2 && bScore < 2) return 1;
      if (bScore === 2 && aScore < 2) return -1;
      return 0;
    };
    const ranked = [...stats].sort((a, b) => {
      if (b.setsWon !== a.setsWon) return b.setsWon - a.setsWon;
      const diffA = a.legsWon - a.legsLost;
      const diffB = b.legsWon - b.legsLost;
      if (diffB !== diffA) return diffB - diffA;
      return a.legsLost - b.legsLost;
    });
    const resolvedPairs = new Set();
    for (let i = 0; i < ranked.length - 1; i++) {
      const A = ranked[i], B = ranked[i + 1];
      const sameAll = (A.setsWon === B.setsWon) &&
        ((A.legsWon - A.legsLost) === (B.legsWon - B.legsLost)) &&
        (A.legsLost === B.legsLost);
      if (!sameAll) continue;
      const prevTie = i > 0 && (
        ranked[i - 1].setsWon === A.setsWon &&
        (ranked[i - 1].legsWon - ranked[i - 1].legsLost) === (A.legsWon - A.legsLost) &&
        ranked[i - 1].legsLost === A.legsLost
      );
      const nextTie = i + 2 < ranked.length && (
        ranked[i + 2].setsWon === B.setsWon &&
        (ranked[i + 2].legsWon - ranked[i + 2].legsLost) === (B.legsWon - B.legsLost) &&
        ranked[i + 2].legsLost === B.legsLost
      );
      if (prevTie || nextTie) continue;
      const delta = directWinDelta(A.teamNo, B.teamNo);
      if (delta === -1) { ranked[i] = B; ranked[i + 1] = A; }
      if (delta !== 0) {
        const winner = delta === 1 ? A.teamNo : B.teamNo;
        const loser = delta === 1 ? B.teamNo : A.teamNo;
        resolvedPairs.add(`${winner}-${loser}`);
      }
    }
    let lastRank = 0, lastKey = null, lastTeamNo = null;
    ranked.forEach((r, idx) => {
      const key = `${r.setsWon}|${r.legsWon - r.legsLost}|${r.legsLost}`;
      const prevResolved = lastTeamNo !== null && (
        resolvedPairs.has(`${lastTeamNo}-${r.teamNo}`) ||
        resolvedPairs.has(`${r.teamNo}-${lastTeamNo}`)
      );
      if (key === lastKey && !prevResolved) {
        r.rank = lastRank; r.isTie = true;
      } else {
        r.rank = idx + 1; r.isTie = false; lastRank = idx + 1; lastKey = key;
      }
      lastTeamNo = r.teamNo;
    });
    for (let i = 0; i < ranked.length; i++) {
      if (ranked[i].isTie && i > 0) ranked[i - 1].isTie = true;
    }
    return ranked; // 順位順 (rank昇順)
  };

  // ----- 表示 -----
  if (pub === undefined) {
    return (
      <div className="public-view-wrap">
        <div className="public-view-loading">読み込み中…</div>
      </div>
    );
  }
  // Docが本当に無い場合のみ、ヘッダー無しの準備中
  if (pub === null) {
    return (
      <div className="public-view-wrap">
        <div className="public-view-empty">
          <div>ロビン表は準備中です</div>
          <div className="public-view-empty-sub">ペア発表後に自動更新されます</div>
        </div>
      </div>
    );
  }

  // ペアがあるブロックだけ表示
  const allBlocks = Array.isArray(pub.blocks) ? pub.blocks : [];
  const blocks = allBlocks.filter((b) => Array.isArray(b.pairs) && b.pairs.length > 0);

  // 非公開(準備中Doc) または 公開だが中身が無い: ヘッダー(大会名/日付)付きの準備中
  // houseName/date はペアバレにならず、正しいページに来ている確認になる
  if (pub.isPublic === false || blocks.length === 0) {
    return (
      <div className="public-view-wrap">
        <div className="public-view-header">
          <div className="public-view-title" translate="no">{pub.houseName || ''}</div>
          <div className="public-view-sub">{pub.date || ''} ・ 閲覧専用</div>
        </div>
        <div className="public-view-empty">
          <div>ロビン表は準備中です</div>
          <div className="public-view-empty-sub">ペア発表後に自動更新されます</div>
        </div>
      </div>
    );
  }

  const safeIdx = Math.max(0, Math.min(blockIdx, blocks.length - 1));
  const block = blocks[safeIdx];
  const stats = computePublicStats(block);
  const pairName = (p) => {
    const n1 = (p && p.player1 && p.player1.name) || '';
    const n2 = (p && p.player2 && p.player2.name) || '';
    return n1 && n2 ? `${n1}・${n2}` : (n1 || n2 || '—');
  };
  // 各ペアの対戦済み数 (有効な勝敗スコアのみ / 敗 = 対戦数 - 勝)
  const playedCount = (teamNo) => {
    const scores = block.scores || {};
    let c = 0;
    Object.entries(scores).forEach(([key, value]) => {
      if (typeof value !== 'string') return;
      const [t1, t2] = key.split('-').map((s) => parseInt(s, 10));
      if (t1 !== teamNo && t2 !== teamNo) return;
      const [a, b] = value.split('-').map((s) => parseInt(s, 10));
      if (isNaN(a) || isNaN(b)) return;
      // 有効な勝敗 (2-0 / 2-1 / 0-2 / 1-2) のみ対戦済みに数える
      const valid = (a === 2 && b < 2) || (b === 2 && a < 2);
      if (valid) c += 1;
    });
    return c;
  };
  // マトリクス用: 行ペア(rowTeam)から見た 列ペア(colTeam) との対戦セル
  // scores は小さい番号視点("1-2":"2-0"=ペア1が2勝)。rowTeam視点に変換する
  const cellFor = (rowTeam, colTeam) => {
    if (rowTeam === colTeam) return { self: true };
    const x = Math.min(rowTeam, colTeam), y = Math.max(rowTeam, colTeam);
    const val = (block.scores || {})[`${x}-${y}`];
    if (!val) return { empty: true };
    const [s1, s2] = String(val).split('-').map((s) => parseInt(s, 10));
    if (isNaN(s1) || isNaN(s2)) return { empty: true };
    // s1-s2 は (x,y)=小さい番号視点。rowTeam視点のスコアに並べ替え
    const rowIsSmaller = (rowTeam === x);
    const rowScore = rowIsSmaller ? s1 : s2;
    const colScore = rowIsSmaller ? s2 : s1;
    // 有効な勝敗 (2-0 / 2-1 / 0-2 / 1-2) のみ表示。壊れ値(1-1/3-0等)は空セル扱い
    const valid = (rowScore === 2 && colScore < 2) || (colScore === 2 && rowScore < 2);
    if (!valid) return { empty: true };
    const win = rowScore === 2 && colScore < 2;
    const lose = colScore === 2 && rowScore < 2;
    return { text: `${rowScore}-${colScore}`, win, lose };
  };
  const statByTeam = {};
  stats.forEach((s) => { statByTeam[s.teamNo] = s; });
  // ペアの2名 (2行表示用)
  const pairNames = (p) => ({
    n1: (p && p.player1 && p.player1.name) || '',
    n2: (p && p.player2 && p.player2.name) || '',
  });

  return (
    <div className="public-view-wrap">
      <div className="public-view-header">
        <div className="public-view-title" translate="no">{pub.houseName || ''}</div>
        <div className="public-view-sub">{pub.date || ''} ・ 閲覧専用</div>
      </div>

      {/* ブロックタブ */}
      <div className="public-view-tabs">
        {blocks.map((b, i) => (
          <button
            key={b.id || i}
            className={`public-view-tab${i === safeIdx ? ' is-active' : ''}`}
            onClick={() => setBlockIdx(i)}
          >
            {b.id || (i + 1)}
          </button>
        ))}
      </div>

      {/* 順位表 */}
      <div className="public-view-card">
        <div className="public-view-card-title">ブロック{block.id || (safeIdx + 1)}・順位</div>
        {stats.map((s) => {
          const played = playedCount(s.teamNo);
          // ダーツロビン戦は引き分け無し前提 (敗 = 対戦済み数 - 勝数)
          // 将来 没収試合/不戦勝/中断試合 が入ると崩れるので注意
          const losses = Math.max(0, played - s.setsWon);
          const p = block.pairs[s.teamNo - 1];
          return (
            <div className="public-rank-row" key={s.teamNo}>
              <span className={`public-rank-badge${s.rank === 1 ? ' top' : ''}`}>{s.rank}</span>
              <span className="public-rank-name" translate="no">{pairName(p)}</span>
              <span className="public-rank-record">{s.setsWon}勝{losses}敗</span>
            </div>
          );
        })}
      </div>

      {/* 総当たりマトリクス (本体と同じ列・横スクロール) */}
      <div className="public-view-card">
        <div className="public-view-card-title">ブロック{block.id || (safeIdx + 1)}・対戦表</div>
        <div className="public-matrix-hint">← 横にスクロールできます →</div>
        <div className="public-matrix-scroll">
          <table className="public-matrix">
            <thead>
              <tr>
                <th className="pm-namecol">ペア</th>
                {block.pairs.map((_, j) => (
                  <th key={j} className="pm-num">{j + 1}</th>
                ))}
                <th className="pm-stat">Sets<br />won</th>
                <th className="pm-stat">①Legs<br />won</th>
                <th className="pm-stat">②Legs<br />Lost</th>
                <th className="pm-stat">①-②</th>
                <th className="pm-rank">RANK</th>
              </tr>
            </thead>
            <tbody>
              {block.pairs.map((p, ri) => {
                const rowTeam = ri + 1;
                const st = statByTeam[rowTeam] || { setsWon: 0, legsWon: 0, legsLost: 0, rank: ri + 1 };
                const nm = pairNames(p);
                const diff = st.legsWon - st.legsLost;
                return (
                  <tr key={rowTeam}>
                    <td className="pm-namecol">
                      <div className="pm-name-inner">
                        <span className="pm-name-no">{rowTeam}</span>
                        <span className="pm-name-text" translate="no">{nm.n1}<br />{nm.n2}</span>
                      </div>
                    </td>
                    {block.pairs.map((_, ci) => {
                      const colTeam = ci + 1;
                      const cell = cellFor(rowTeam, colTeam);
                      if (cell.self) return <td key={ci} className="pm-cell pm-self">—</td>;
                      if (cell.empty) return <td key={ci} className="pm-cell"></td>;
                      const cls = cell.win ? ' pm-win' : (cell.lose ? ' pm-lose' : '');
                      return (
                        <td key={ci} className={`pm-cell${cls}`}>
                          {cell.win ? '○' : (cell.lose ? '×' : '')}{cell.text}
                        </td>
                      );
                    })}
                    <td className="pm-stat pm-stat-sets">{st.setsWon}</td>
                    <td className="pm-stat">{st.legsWon}</td>
                    <td className="pm-stat">{st.legsLost}</td>
                    <td className="pm-stat">{diff > 0 ? `+${diff}` : diff}</td>
                    <td className="pm-rank">{st.rank}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {pub.schemaVersion >= 2 && Array.isArray(pub.nextSchedule) && pub.nextSchedule.length > 0 && (
        <div className="public-view-card public-next-card">
          <div className="public-view-card-title">次の試合予定</div>
          {pub.nextSchedule.map((ns) => (
            <div className="public-next-row" key={`${ns.blockId}-${ns.matchKey}`}>
              <span className="public-next-order">{ns.order}</span>
              <span className="public-next-block">ブロック{ns.blockId}</span>
              <span className="public-next-pairs" translate="no">
                {ns.teamAName || `ペア${ns.teamA}`} vs {ns.teamBName || `ペア${ns.teamB}`}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="public-view-foot">自動更新されます</div>
    </div>
  );
}

/* ============================================================
   認証ガード
   ============================================================ */
function RequireAuth({ children }) {
  const { user, loading } = useAuthUser();
  if (loading) return <div className="page"><div className="loading">読み込み中...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/* ============================================================
   ① イベント一覧画面(トップ)
   ============================================================ */
function TestModeBanner({ event, navigate }) {
  if (!event?.isTest) return null;
  const handleEnd = async () => {
    if (!event?.isTest) return;
    if (!window.confirm('テストモードを終了して、このテストイベントを削除しますか?\n元には戻せません。')) return;
    try {
      await deleteDoc(doc(db, 'events', event.id));
      const back = event.testOriginId || '';
      navigate(back ? `/event/${back}/pairing` : '/');
    } catch (err) {
      console.error('テスト終了失敗', err);
      alert('テストイベントの削除に失敗しました。');
    }
  };
  return (
    <div className="test-mode-banner no-print">
      <span className="test-mode-banner-text">
        <AlertTriangle size={16} /> テストモード・本番には反映されません
      </span>
      <button className="test-mode-banner-btn" onClick={handleEnd}>テスト終了</button>
    </div>
  );
}

function EventList() {
  const { user, isAdmin, shopName } = useAuthUser();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  // Admin 用: 全店の {uid: shopName} マップ（店舗名バッジ・フィルタ用）
  const [shopMap, setShopMap] = useState({});
  const [selectedShopUid, setSelectedShopUid] = useState('all');

  useEffect(() => {
    if (!user || !isAdmin) return;
    const unsub = onSnapshot(collection(db, 'users'), (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.uid) map[data.uid] = data.shopName || '(店舗名未設定)';
      });
      setShopMap(map);
    });
    return () => unsub();
  }, [user, isAdmin]);

  // Admin の店舗フィルタ適用（店舗ユーザーは events をそのまま使う）
  const displayEvents = isAdmin && selectedShopUid !== 'all'
    ? events.filter((ev) => ev.ownerUid === selectedShopUid)
    : events;

  // フィルタ用の店舗選択肢（実際にイベントを持つ店舗のみ + 全店）
  const eventOwnerUids = new Set(events.map((ev) => ev.ownerUid).filter(Boolean));
  const shopFilterOptions = isAdmin
    ? Object.keys(shopMap)
        .filter((uid) => eventOwnerUids.has(uid))
        .map((uid) => ({ uid, name: shopMap[uid] }))
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];

  useEffect(() => {
    if (!user) return;
    // Admin は全店、店舗ユーザーは自店のみ（店舗側の振る舞いは従来通り）
    const eventsQuery = isAdmin
      ? query(collection(db, 'events'))
      : query(collection(db, 'events'), where('ownerUid', '==', user.uid));
    const unsub = onSnapshot(
      eventsQuery,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setEvents(list);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user, isAdmin]);

  const [createEventModal, setCreateEventModal] = useState(null);

  const openCreateEventModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    setCreateEventModal({ date: today, houseName: '' });
  };

  const submitCreateEvent = async () => {
    const houseName = (createEventModal?.houseName || '').trim();
    if (!houseName) return;
    const date = createEventModal?.date || new Date().toISOString().slice(0, 10);
    setCreateEventModal(null);
    try {
      const ref = await addDoc(collection(db, 'events'), {
        ownerUid: user.uid,
        date: date,
        houseName: houseName,
        entries: [],
        savedPairings: [],
        isPublic: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      navigate(`/event/${ref.id}`);
    } catch (err) {
      console.error('イベント作成失敗:', err);
      alert('イベントの作成に失敗しました。通信状況を確認してください。');
    }
  };

  // STEP6d: 公開ON/OFF (events.isPublic を反転)。Admin限定でUI表示
  const [togglingId, setTogglingId] = useState(null);
  const toggleEventPublic = async (ev, e) => {
    e.stopPropagation();
    if (togglingId) return;
    const next = ev.isPublic !== true;
    if (next) {
      if (!window.confirm('【公開】「' + (ev.houseName || '') + '」を公開しますか?\nQRを読んだ全員にペア・順位・スコアが見えるようになります。\n開会式でペア発表を済ませたことを確認してください。')) return;
    } else {
      if (!window.confirm('【非公開】「' + (ev.houseName || '') + '」を非公開に戻しますか?\nQRを読んでも「準備中」表示になります。')) return;
    }
    setTogglingId(ev.id);
    try {
      await updateDoc(doc(db, 'events', ev.id), { isPublic: next, updatedAt: serverTimestamp() });
    } catch (err) {
      console.error('公開状態の変更失敗', err);
      alert('公開状態の変更に失敗しました: [' + (err.code || '') + '] ' + err.message);
    } finally {
      setTogglingId(null);
    }
  };

  // STEP6d: QR画像生成 (本番URLをPNG化)。Admin限定
  const [qrModal, setQrModal] = useState(null);
  const showEventQr = async (ev, e) => {
    e.stopPropagation();
    try {
      const snap = await getDoc(doc(db, 'publicEvents', ev.id));
      if (!snap.exists()) {
        alert('このイベントはまだ公開準備ができていません。\nロビン表を一度開いてスコアを更新してください(公開データが生成されます)。');
        return;
      }
    } catch (err) {
      alert('公開データの確認に失敗しました: ' + err.message);
      return;
    }
    const url = 'https://hts.actcreation-lab.com/#/view/' + ev.id;
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        width: 1024,
        margin: 2,
        errorCorrectionLevel: 'M',
        color: { dark: '#1a1a2e', light: '#ffffff' },
      });
      setQrModal({ dataUrl, url, houseName: ev.houseName || '' });
    } catch (err) {
      alert('QR生成に失敗しました: ' + err.message);
    }
  };
  const downloadQr = () => {
    if (!qrModal) return;
    const a = document.createElement('a');
    a.href = qrModal.dataUrl;
    a.download = 'QR_' + (qrModal.houseName || 'event') + '.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const deleteEvent = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('このイベントを削除しますか?元に戻せません。')) return;
    try {
      await deleteDoc(doc(db, 'events', id));
    } catch (err) {
      console.error('イベント削除失敗:', err);
      alert('イベントの削除に失敗しました。通信状況を確認してください。');
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('ログアウトしますか?')) return;
    await signOut(auth);
    navigate('/login');
  };

  return (
    <div className="page no-select">
      <header className="header">
        <h1><HeaderLogo /> House Tournament</h1>
        <div className="header-actions header-actions-desktop">
          {isAdmin && (
            <button className="btn-secondary" onClick={() => navigate('/admin')}>
              <Shield size={14} /> Admin
            </button>
          )}
          <Link to="/help" className="btn-secondary">
            <HelpCircle size={14} /> 使い方
          </Link>
          <button className="btn-secondary" onClick={handleLogout}>
            <LogOut size={14} /> ログアウト
          </button>
        </div>
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
          <Menu size={20} />
        </button>
      </header>

      <div className="status-bar">
        <span className="status-bar-label" translate="no">
          <strong>{shopName}</strong> としてログイン中
        </span>
      </div>

      <HamburgerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        shopName={shopName}
        email={user?.email}
        isAdmin={isAdmin}
        onNavigate={navigate}
        onLogout={handleLogout}
      />

      <div className="container">
        <button className="btn-primary btn-large" onClick={openCreateEventModal}>
          <Plus size={18} /> 新規イベント作成
        </button>

        <h2 className="section-title">イベント一覧</h2>
        {loading ? (
          <div className="loading">読み込み中...</div>
        ) : events.length === 0 ? (
          <div className="empty">イベントがまだありません。「新規イベント作成」から始めてください。</div>
        ) : (
          <>
            {isAdmin && (
              <div className="admin-shop-filter">
                <Store size={14} />
                <select
                  value={selectedShopUid}
                  onChange={(e) => setSelectedShopUid(e.target.value)}
                >
                  <option value="all">すべての店舗</option>
                  {shopFilterOptions.map((s) => (
                    <option key={s.uid} value={s.uid}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <ul className="event-list">
              {displayEvents.map((ev) => (
                <li key={ev.id} className="event-item" onClick={() => navigate(ev.isTest ? `/event/${ev.id}/progress-setting` : `/event/${ev.id}`)}>
                  <div className="event-info">
                    {ev.isTest && <span className="test-badge">TEST</span>}
                    {isAdmin && <span className="admin-shop-badge" translate="no"><Store size={11} /> {shopMap[ev.ownerUid] || '(不明な店舗)'}</span>}
                    <div className="event-date"><Calendar size={12} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />{ev.date || '(日付未設定)'}</div>
                    <div className="event-name" translate="no">{ev.houseName || '(ハウス名未設定)'}</div>
                    <div className="event-meta">エントリー {(ev.entries || []).filter(e => e.name && e.name.trim()).length}人</div>
                  </div>
                  {isAdmin && !ev.isTest && (
                    <div className="event-admin-actions" onClick={(e) => e.stopPropagation()}>
                      <span className={`event-public-badge ${ev.isPublic === true ? 'on' : 'off'}`}>
                        {ev.isPublic === true ? '公開中' : '非公開'}
                      </span>
                      <button
                        className={`btn-public-toggle ${ev.isPublic === true ? 'on' : 'off'}`}
                        onClick={(e) => toggleEventPublic(ev, e)}
                        disabled={togglingId === ev.id}
                      >
                        {ev.isPublic === true
                          ? <><EyeOff size={13} /> 非公開にする</>
                          : <><Eye size={13} /> 公開する</>}
                      </button>
                      <button className="btn-qr" onClick={(e) => showEventQr(ev, e)}>
                        <QrCode size={13} /> QR
                      </button>
                    </div>
                  )}
                  <button className="btn-danger-small" onClick={(e) => deleteEvent(ev.id, e)}>削除</button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {createEventModal && (
        <div className="shop-reg-modal-overlay" onClick={() => setCreateEventModal(null)}>
          <div className="shop-reg-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="shop-reg-title"><Plus size={18} /> 新規イベント作成</h3>
            <p className="shop-reg-note">年月日とハウス名を入力してください。</p>
            <div className="create-event-field">
              <label>年月日</label>
              <input
                type="date"
                value={createEventModal.date || ''}
                onChange={(e) => setCreateEventModal(m => ({ ...m, date: e.target.value }))}
                autoComplete="off" data-form-type="other"
              />
            </div>
            <div className="create-event-field">
              <label>ハウス名</label>
              <input
                type="text"
                value={createEventModal.houseName || ''}
                onChange={(e) => setCreateEventModal(m => ({ ...m, houseName: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing) return;
                  if (e.key === 'Enter' && createEventModal.houseName.trim()) {
                    submitCreateEvent();
                  }
                }}
                placeholder="例：5周年ハウス"
                autoComplete="off" translate="no" data-form-type="other"
                autoFocus
              />
            </div>
            <div className="shop-reg-actions">
              <button className="shop-reg-cancel" onClick={() => setCreateEventModal(null)}>キャンセル</button>
              <button className="shop-reg-submit" onClick={submitCreateEvent} disabled={!createEventModal.houseName.trim()}>作成</button>
            </div>
          </div>
        </div>
      )}

      {qrModal && (
        <div className="qr-modal-overlay" onClick={() => setQrModal(null)}>
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="qr-modal-title" translate="no">{qrModal.houseName}</h3>
            <p className="qr-modal-note">このQRを読むと閲覧ページが開きます</p>
            <div className="qr-modal-img-wrap">
              <img src={qrModal.dataUrl} alt="QR" className="qr-modal-img" />
            </div>
            <div className="qr-modal-url" translate="no">{qrModal.url}</div>
            <div className="qr-modal-actions">
              <button className="shop-reg-cancel" onClick={() => setQrModal(null)}>閉じる</button>
              <button className="shop-reg-submit" onClick={downloadQr}>画像を保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   ② エントリーシート画面
   ============================================================ */
const MIN_ROWS_PC = 20;
const MIN_ROWS_SP = 10;
const MIN_INVITED_ROWS = 1;

const ENTRY_PDF_FONT_NAME = 'NotoSansJP';
const ENTRY_PDF_FONT_FILE = 'NotoSansJP-Bold.ttf';
let entryPdfFontPromise = null;

const hasEntryContent = (entry) => !!(entry?.name?.trim() || entry?.shop || entry?.rating);

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function loadEntryPdfFontBase64() {
  if (!entryPdfFontPromise) {
    const publicUrl = process.env.PUBLIC_URL || '';
    entryPdfFontPromise = fetch(`${publicUrl}/fonts/${ENTRY_PDF_FONT_FILE}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load PDF font: ${response.status}`);
        }
        return response.blob();
      })
      .then(blobToBase64);
  }
  return entryPdfFontPromise;
}

async function registerEntryPdfFont(doc) {
  const fontBase64 = await loadEntryPdfFontBase64();
  doc.addFileToVFS(ENTRY_PDF_FONT_FILE, fontBase64);
  doc.addFont(ENTRY_PDF_FONT_FILE, ENTRY_PDF_FONT_NAME, 'normal');
  doc.setFont(ENTRY_PDF_FONT_NAME, 'normal');
}

function toEntryPdfRows(rows) {
  return (rows || []).filter(hasEntryContent).map((entry, i) => [
    String(i + 1),
    String(entry?.name || ''),
    String(entry?.shop || ''),
    entry?.rating ? String(entry.rating) : '',
  ]);
}

function drawEntryPdfTable(doc, title, rows, startY) {
  let tableY = startY;
  if (title) {
    if (tableY > 260) {
      doc.addPage();
      tableY = 14;
    }
    doc.setFontSize(12);
    doc.text(title, 10, tableY);
    tableY += 4;
  }

  autoTable(doc, {
    startY: tableY,
    head: [['No', '名前', '店舗', 'Rt']],
    body: rows,
    theme: 'grid',
    margin: { left: 10, right: 10 },
    tableWidth: 190,
    styles: {
      font: ENTRY_PDF_FONT_NAME,
      fontStyle: 'normal',
      fontSize: 9.5,
      cellPadding: { top: 1.6, right: 1.8, bottom: 1.6, left: 1.8 },
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.4,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      font: ENTRY_PDF_FONT_NAME,
      fontStyle: 'normal',
      fillColor: [26, 26, 46],
      textColor: [255, 255, 255],
      lineColor: [0, 0, 0],
      lineWidth: 0.4,
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      1: { cellWidth: 72 },
      2: { cellWidth: 88 },
      3: { cellWidth: 18, halign: 'center' },
    },
    rowPageBreak: 'avoid',
  });

  return doc.lastAutoTable?.finalY || tableY;
}

function toPairingPdfRows(pairs) {
  return (pairs || []).map((pair, i) => {
    const n1 = String(pair.player1?.name || '');
    const n2 = String(pair.player2?.name || '');
    const s1 = (pair.player1?.shop || '').trim();
    const s2 = (pair.player2?.shop || '').trim();
    const cell1 = s1 ? `${n1}\n${s1}` : n1;
    const cell2 = s2 ? `${n2}\n${s2}` : n2;
    return [
      String(i + 1),
      cell1,
      pair.player1?.rating ? String(pair.player1.rating) : '',
      cell2,
      pair.player2?.rating ? String(pair.player2.rating) : '',
    ];
  });
}

function drawPairingPdfTable(doc, rows, startY) {
  autoTable(doc, {
    startY,
    head: [['No', 'プレイヤー1', 'Rt', 'プレイヤー2', 'Rt']],
    body: rows,
    theme: 'grid',
    margin: { left: 10, right: 10 },
    tableWidth: 190,
    styles: {
      font: ENTRY_PDF_FONT_NAME,
      fontStyle: 'normal',
      fontSize: 9.5,
      cellPadding: { top: 1.6, right: 1.8, bottom: 1.6, left: 1.8 },
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.4,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      font: ENTRY_PDF_FONT_NAME,
      fontStyle: 'normal',
      fillColor: [26, 26, 46],
      textColor: [255, 255, 255],
      lineColor: [0, 0, 0],
      lineWidth: 0.4,
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 14, halign: 'center' },
      1: { cellWidth: 70 },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 70 },
      4: { cellWidth: 18, halign: 'center' },
    },
    rowPageBreak: 'avoid',
  });
  return doc.lastAutoTable?.finalY || startY;
}

async function buildPairingPdfBlob({ event, pairing }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await registerEntryPdfFont(doc);
  doc.setProperties({ title: 'ペア表' });

  doc.setFontSize(16);
  doc.text('ペア表', 10, 14);
  doc.setFontSize(10.5);
  doc.text(`日付: ${event?.date || ''}`, 10, 24);
  doc.text(`ハウス名: ${event?.houseName || ''}`, 10, 31);
  if (pairing?.name) {
    doc.text(`ペア表: ${pairing.name}`, 10, 38);
  }

  const rows = toPairingPdfRows(pairing?.pairs || []);
  drawPairingPdfTable(doc, rows, 45);

  return doc.output('blob');
}

async function openPairingPdf({ event, pairing }) {
  const pdfWindow = typeof window !== 'undefined' ? window.open('', '_blank') : null;
  if (pdfWindow?.document) {
    pdfWindow.document.write('<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>PDF作成中</title></head><body>PDFを作成しています...</body></html>');
    pdfWindow.document.close();
  }

  try {
    const blob = await buildPairingPdfBlob({ event, pairing });
    const pdfUrl = URL.createObjectURL(blob);

    if (pdfWindow) {
      pdfWindow.location.href = pdfUrl;
    } else {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
  } catch (error) {
    if (pdfWindow) pdfWindow.close();
    throw error;
  }
}

async function buildEntrySheetPdfBlob({ event, normalEntries, invitedEntries }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await registerEntryPdfFont(doc);
  doc.setProperties({ title: 'エントリーシート' });

  const normalRows = toEntryPdfRows(normalEntries);
  const invitedRows = toEntryPdfRows(invitedEntries);

  doc.setFontSize(16);
  doc.text('エントリーシート', 10, 14);
  doc.setFontSize(10.5);
  doc.text(`日付: ${event?.date || ''}`, 10, 24);
  doc.text(`ハウス名: ${event?.houseName || ''}`, 10, 31);

  const normalFinalY = drawEntryPdfTable(doc, '', normalRows, 38);

  if (invitedRows.length > 0) {
    drawEntryPdfTable(doc, '招待選手', invitedRows, normalFinalY + 8);
  }

  return doc.output('blob');
}

async function openEntrySheetPdf({ event, normalEntries, invitedEntries }) {
  const pdfWindow = typeof window !== 'undefined' ? window.open('', '_blank') : null;
  if (pdfWindow?.document) {
    pdfWindow.document.write('<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>PDF作成中</title></head><body>PDFを作成しています...</body></html>');
    pdfWindow.document.close();
  }

  try {
    const blob = await buildEntrySheetPdfBlob({ event, normalEntries, invitedEntries });
    const pdfUrl = URL.createObjectURL(blob);

    if (pdfWindow) {
      pdfWindow.location.href = pdfUrl;
    } else {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
  } catch (error) {
    if (pdfWindow) pdfWindow.close();
    throw error;
  }
}

function EventSheet() {
  const { user, isAdmin, shopName } = useAuthUser();
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState('order');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const houseNameInputName = useMemo(rndName, []);

  const [shopRegisterModal, setShopRegisterModal] = useState(null); // null | { realEntriesIndex, isInvited }
  const [newShopName, setNewShopName] = useState('');
  const [shopRegBusy, setShopRegBusy] = useState(false);
  const [shopRegError, setShopRegError] = useState('');
  const shopRegInputName = useMemo(rndName, []);

  const [pendingEntries, setPendingEntries] = useState(null);
  const pendingEntriesRef = useRef(null);
  const eventRef = useRef(null);
  const idRef = useRef(id);
  const flushTimerRef = useRef(null);
  const flushIdRef = useRef(0);

  useEffect(() => { eventRef.current = event; }, [event]);
  useEffect(() => { idRef.current = id; }, [id]);

  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'events', id), (snap) => {
      if (!snap.exists()) {
        alert('イベントが見つかりません');
        navigate('/');
        return;
      }
      const data = snap.data();
      if (user && !isAdmin && data.ownerUid !== user.uid) {
        alert('このイベントへのアクセス権がありません');
        navigate('/');
        return;
      }
      setEvent({ id: snap.id, ...data });
      setLoading(false);
    }, (err) => {
      console.error('イベント購読エラー', err);
      alert('イベントを読み込めませんでした（権限がないか、通信エラーです）');
      navigate('/');
    });
    return () => unsub();
  }, [id, navigate, user]);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      query(collection(db, 'shops'), where('ownerUid', '==', user.uid)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'));
        setShops(list);
      }
    );
    return () => unsub();
  }, [user]);

  const flushToFirestore = useCallback((entriesToWrite) => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    const myId = ++flushIdRef.current;
    flushTimerRef.current = setTimeout(async () => {
      try {
        await updateDoc(doc(db, 'events', idRef.current), {
          entries: entriesToWrite,
          updatedAt: serverTimestamp()
        });
        if (flushIdRef.current === myId) {
          pendingEntriesRef.current = null;
          setPendingEntries(null);
        }
      } catch (err) {
        console.error('Firestore保存失敗:', err);
      }
    }, 500);
  }, []);

  const updateHeader = useCallback(async (field, value) => {
    try {
      await updateDoc(doc(db, 'events', idRef.current), {
        [field]: value,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Firestore保存失敗:', err);
      alert('保存に失敗しました。通信状況を確認して、もう一度お試しください。');
    }
  }, []);

  // ===== ハウス名 IME対応ローカルバッファ (B: IMEバグ根治・案2) =====
  // 入力中の表示値はローカルstateで持ち、Firestore round-trip(onSnapshot)が
  // composition中のvalueを上書きしないようにする。保存はdebounce。
  const [houseNameInput, setHouseNameInput] = useState('');
  const houseNameComposingRef = useRef(false);  // IME変換中フラグ
  const houseNameFocusedRef = useRef(false);     // フォーカス中フラグ
  const houseNameSaveTimerRef = useRef(null);    // debounceタイマー
  const houseNamePendingRef = useRef(null);      // 未保存値 (null=保存待ちなし)

  // Firestore値 → ローカル同期。フォーカス中・変換中は上書きしない(これが根治の核)
  useEffect(() => {
    if (!houseNameFocusedRef.current && !houseNameComposingRef.current) {
      setHouseNameInput(event?.houseName || '');
    }
  }, [event?.houseName]);

  // debounce保存をスケジュール (既存エントリー欄と同じ500ms思想)
  const scheduleHouseNameSave = useCallback((value) => {
    houseNamePendingRef.current = value;
    if (houseNameSaveTimerRef.current) clearTimeout(houseNameSaveTimerRef.current);
    houseNameSaveTimerRef.current = setTimeout(() => {
      houseNameSaveTimerRef.current = null;
      const v = houseNamePendingRef.current;
      if (v !== null) {
        houseNamePendingRef.current = null;
        updateHeader('houseName', v);  // 既存の保存関数を再利用(alertも踏襲)
      }
    }, 500);
  }, [updateHeader]);

  // 未保存分を即flush (blur時 / unmount時)
  const flushHouseNameSave = useCallback(() => {
    if (houseNameSaveTimerRef.current) {
      clearTimeout(houseNameSaveTimerRef.current);
      houseNameSaveTimerRef.current = null;
    }
    const v = houseNamePendingRef.current;
    if (v !== null) {
      houseNamePendingRef.current = null;
      updateHeader('houseName', v);
    }
  }, [updateHeader]);

  const handleHouseNameChange = useCallback((e) => {
    const value = e.target.value;
    setHouseNameInput(value);  // 表示は常に同期更新(自分の入力値なのでIMEは踏まない)
    if (!houseNameComposingRef.current) {
      scheduleHouseNameSave(value);  // 変換中でなければ保存予約
    }
  }, [scheduleHouseNameSave]);

  const handleHouseNameCompositionStart = useCallback(() => {
    houseNameComposingRef.current = true;
  }, []);

  const handleHouseNameCompositionEnd = useCallback((e) => {
    houseNameComposingRef.current = false;
    const value = e.target.value;
    setHouseNameInput(value);
    scheduleHouseNameSave(value);  // 変換確定 → 保存予約
  }, [scheduleHouseNameSave]);

  const handleHouseNameFocus = useCallback(() => {
    houseNameFocusedRef.current = true;
  }, []);

  const handleHouseNameBlur = useCallback(() => {
    houseNameFocusedRef.current = false;
    flushHouseNameSave();  // フォーカスを外したら未保存分を即保存
  }, [flushHouseNameSave]);

  // 最新のflush関数をrefに保持 (unmount cleanupが依存更新で走らないように)
  const flushHouseNameSaveRef = useRef(flushHouseNameSave);
  useEffect(() => {
    flushHouseNameSaveRef.current = flushHouseNameSave;
  }, [flushHouseNameSave]);

  // unmount時のみ未保存分をflush (依存配列は空 → cleanupはunmount時だけ)
  useEffect(() => {
    return () => { flushHouseNameSaveRef.current(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entries = useMemo(() => {
    if (pendingEntries !== null) return pendingEntries;
    return event?.entries || [];
  }, [event?.entries, pendingEntries]);

  const sortedEntries = useMemo(() => {
    const list = entries.map((e, i) => ({ ...e, originalIndex: i }));
    if (sortMode === 'rating') {
      list.sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0));
    } else if (sortMode === 'shop') {
      const counts = {};
      list.forEach(e => {
        const shop = e.shop || '';
        counts[shop] = (counts[shop] || 0) + 1;
      });
      list.sort((a, b) => {
        const shopA = a.shop || '';
        const shopB = b.shop || '';
        const cntDiff = (counts[shopB] || 0) - (counts[shopA] || 0);
        if (cntDiff !== 0) return cntDiff;
        const nameDiff = shopA.localeCompare(shopB, 'ja');
        if (nameDiff !== 0) return nameDiff;
        return a.originalIndex - b.originalIndex;
      });
    }
    return list;
  }, [entries, sortMode]);

  const duplicateNames = useMemo(() => {
    const counts = {};
    entries.forEach((e) => {
      if (e.name && e.name.trim()) {
        counts[e.name.trim()] = (counts[e.name.trim()] || 0) + 1;
      }
    });
    return new Set(Object.keys(counts).filter((n) => counts[n] > 1));
  }, [entries]);

  const updateRow = useCallback((realEntriesIndex, field, value) => {
    const base = pendingEntriesRef.current || eventRef.current?.entries || [];
    const next = [...base];

    if (realEntriesIndex === -1) {
      next.push({ name: '', shop: '', rating: '', isInvited: false, isAttended: false, [field]: value });
    } else {
      while (next.length <= realEntriesIndex) {
        next.push({ name: '', shop: '', rating: '', isInvited: false, isAttended: false });
      }
      next[realEntriesIndex] = { ...next[realEntriesIndex], [field]: value };
    }

    pendingEntriesRef.current = next;
    setPendingEntries(next);
    flushToFirestore(next);
  }, [flushToFirestore]);

  const deleteRow = useCallback((realEntriesIndex) => {
    if (realEntriesIndex < 0) return;
    const currentEntries = pendingEntriesRef.current || eventRef.current?.entries || [];
    const target = currentEntries[realEntriesIndex];
    if (!target) return;
    if (!target.name?.trim() && !target.shop && !target.rating) return;
    const label = target.name?.trim() || `${realEntriesIndex + 1}番目の行`;
    if (!window.confirm(`「${label}」を削除しますか?`)) return;
    const next = currentEntries.filter((_, i) => i !== realEntriesIndex);
    pendingEntriesRef.current = next;
    setPendingEntries(next);
    flushToFirestore(next);
  }, [flushToFirestore]);

  const updateInvitedRow = useCallback((realEntriesIndex, field, value) => {
    const base = pendingEntriesRef.current || eventRef.current?.entries || [];
    const next = [...base];

    if (realEntriesIndex === -1) {
      next.push({ name: '', shop: '', rating: '', isInvited: true, isAttended: false, [field]: value });
    } else {
      next[realEntriesIndex] = { ...next[realEntriesIndex], [field]: value };
    }

    pendingEntriesRef.current = next;
    setPendingEntries(next);
    flushToFirestore(next);
  }, [flushToFirestore]);

  const deleteInvitedRow = useCallback((realEntriesIndex) => {
    if (realEntriesIndex < 0) return;
    const currentEntries = pendingEntriesRef.current || eventRef.current?.entries || [];
    const target = currentEntries[realEntriesIndex];
    if (!target) return;
    if (!target.name?.trim() && !target.shop && !target.rating) return;
    const label = target.name?.trim() || '招待選手';
    if (!window.confirm(`「${label}」を削除しますか?`)) return;
    const next = currentEntries.filter((_, i) => i !== realEntriesIndex);
    pendingEntriesRef.current = next;
    setPendingEntries(next);
    flushToFirestore(next);
  }, [flushToFirestore]);

  const handleOpenShopRegister = useCallback((realEntriesIndex, isInvited) => {
    setNewShopName('');
    setShopRegError('');
    setShopRegisterModal({ realEntriesIndex, isInvited });
  }, []);

  const handleRegisterShop = async () => {
    const name = newShopName.trim();
    if (!name) { setShopRegError('店舗名を入力してください'); return; }
    if (shops.some((s) => s.name === name)) {
      setShopRegError('同じ名前の店舗が既に登録されています');
      return;
    }
    setShopRegBusy(true);
    try {
      await addDoc(collection(db, 'shops'), {
        ownerUid: user.uid,
        name,
        createdAt: serverTimestamp(),
      });
      const ctx = shopRegisterModal;
      if (ctx && ctx.realEntriesIndex >= 0) {
        if (ctx.isInvited) updateInvitedRow(ctx.realEntriesIndex, 'shop', name);
        else updateRow(ctx.realEntriesIndex, 'shop', name);
      }
      setShopRegisterModal(null);
      setNewShopName('');
      setShopRegError('');
    } catch (err) {
      setShopRegError('登録に失敗しました: ' + (err.message || '不明なエラー'));
    } finally {
      setShopRegBusy(false);
    }
  };

  const handleLogout = useCallback(async () => {
    if (!window.confirm('ログアウトしますか?')) return;
    await signOut(auth);
    navigate('/login');
  }, [navigate]);

  const normalEntries = useMemo(() => sortedEntries.filter((e) => !e.isInvited), [sortedEntries]);
  const invitedEntries = useMemo(() => sortedEntries.filter((e) => e.isInvited), [sortedEntries]);
  const printableNormalEntries = useMemo(
    () => normalEntries.filter(hasEntryContent),
    [normalEntries]
  );
  const printableInvitedEntries = useMemo(
    () => invitedEntries.filter(hasEntryContent),
    [invitedEntries]
  );

  const EXTRA_ROWS = isMobile ? 3 : 5;
  const MIN_ROWS = isMobile ? MIN_ROWS_SP : MIN_ROWS_PC;
  const filledCount = normalEntries.filter(e => e.name?.trim()).length;
  const normalDisplayCount = Math.max(MIN_ROWS, filledCount + EXTRA_ROWS);

  const invitedFilledCount = invitedEntries.filter(e => e.name?.trim()).length;
  const invitedDisplayCount = Math.max(MIN_INVITED_ROWS, invitedFilledCount + 1);

  // 通常選手は1つの連続リスト(ページ分割なし)
  const displayRows = useMemo(() => {
    const arr = [];
    for (let i = 0; i < normalDisplayCount; i++) arr.push(normalEntries[i] || null);
    return arr;
  }, [normalEntries, normalDisplayCount]);

  // PC は2カラム(前半/後半)、スマホは1カラム
  const halfPoint = isMobile ? normalDisplayCount : Math.ceil(normalDisplayCount / 2);
  const leftRows = displayRows.slice(0, halfPoint);
  const rightRows = displayRows.slice(halfPoint);

  const handleShowEntryPdf = useCallback(() => {
    openEntrySheetPdf({
      event,
      normalEntries: printableNormalEntries,
      invitedEntries: printableInvitedEntries,
    }).catch((error) => {
      console.error('PDF creation failed:', error);
      window.alert('PDFの作成に失敗しました。時間をおいて再度お試しください。');
    });
  }, [event, printableNormalEntries, printableInvitedEntries]);

  const attendedCount = useMemo(() => entries.filter(e => e.name?.trim() && e.isAttended).length, [entries]);
  const totalNamedCount = useMemo(() => entries.filter(e => e.name?.trim()).length, [entries]);

  if (loading || !event) return <div className="page"><div className="loading">読み込み中...</div></div>;

  return (
    <div className="page no-select">
      <header className="header no-print">
        <h1>
          <button className="btn-back" onClick={() => navigate('/')}>
            <ArrowLeft size={14} /> 戻る
          </button>
          <span style={{ marginLeft: 8 }}>エントリーシート</span>
        </h1>
        <div className="header-actions header-actions-desktop">
          <Link to="/shops" className="btn-secondary"><Store size={14} /> 登録店舗の管理</Link>
          <button className="btn-accent" onClick={() => navigate(`/event/${id}/pairing`)}>
            ペアリング →
          </button>
        </div>
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
          <Menu size={20} />
        </button>
      </header>

      <HamburgerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        shopName={shopName}
        email={user?.email}
        isAdmin={isAdmin}
        onNavigate={navigate}
        onLogout={handleLogout}
        extraItems={[
          { icon: <Store size={18} />, label: '登録店舗の管理', onClick: () => navigate('/shops') },
          { icon: <List size={18} />, label: 'ペアリングへ', accent: true, onClick: () => navigate(`/event/${id}/pairing`) },
        ]}
      />

      <div className="container">
        <ActiveRobinBar event={event} eventId={id} navigate={navigate} />

        <div className="sheet-header">
          <div className="input-group">
            <label>年月日</label>
            <input
              type="date" value={event.date || ''}
              onChange={(e) => updateHeader('date', e.target.value)}
              autoComplete="off" data-form-type="other"
            />
          </div>
          <div className="input-group">
            <label>ハウス名</label>
            <input
              type="text" name={houseNameInputName} value={houseNameInput}
              onChange={handleHouseNameChange}
              onCompositionStart={handleHouseNameCompositionStart}
              onCompositionEnd={handleHouseNameCompositionEnd}
              onFocus={handleHouseNameFocus}
              onBlur={handleHouseNameBlur}
              placeholder="ハウス名"
              autoComplete="off" translate="no" data-form-type="other"
            />
          </div>
        </div>

        <div className="pairing-band no-print">
          <button
            className="pairing-band-btn"
            onClick={() => navigate(`/event/${id}/pairing`)}
          >
            <List size={16} /> ペアリングへ
          </button>
        </div>

        <div className="toolbar no-print">
          <div className="sort-buttons">
            <span>並び替え：</span>
            <button className={sortMode === 'order' ? 'active' : ''} onClick={() => setSortMode('order')}>登録順</button>
            <button className={sortMode === 'rating' ? 'active' : ''} onClick={() => setSortMode('rating')}>Rt.順</button>
            <button className={sortMode === 'shop' ? 'active' : ''} onClick={() => setSortMode('shop')}>店舗順</button>
          </div>
          <div className="attend-summary">
            <span>来場：<strong>{attendedCount}</strong> / {totalNamedCount}人</span>
          </div>
          <div className="action-buttons">
            <button onClick={handleShowEntryPdf}><Printer size={14} /> PDF表示</button>
          </div>
        </div>

        <div className="sheet-page">
          <div className="entry-grid">
            <EntryColumn
              start={1}
              rows={leftRows}
              duplicateNames={duplicateNames}
              shops={shops}
              onUpdate={updateRow}
              onDelete={deleteRow}
              onRegister={handleOpenShopRegister}
            />
            {!isMobile && (
              <EntryColumn
                start={halfPoint + 1}
                rows={rightRows}
                duplicateNames={duplicateNames}
                shops={shops}
                onUpdate={updateRow}
                onDelete={deleteRow}
                onRegister={handleOpenShopRegister}
              />
            )}
          </div>

          <div className="invited-section">
            <h3><AlertTriangle size={16} /> 招待選手</h3>
            <table className="entry-table">
              <thead>
                <tr>
                  <th className="col-no">#</th>
                  <th className="col-name">名前</th>
                  <th className="col-shop">店舗</th>
                  <th className="col-rt">Rt.</th>
                  <th className="col-trash"></th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: invitedDisplayCount }).map((_, i) => {
                  const entry = invitedEntries[i] || null;
                  const realEntriesIndex = entry ? entry.originalIndex : -1;
                  const isDup = entry?.name?.trim() ? duplicateNames.has(entry.name.trim()) : false;
                  return (
                    <InvitedRow
                      key={`inv-${i}`}
                      no={i + 1}
                      entry={entry}
                      realEntriesIndex={realEntriesIndex}
                      isDup={isDup}
                      shops={shops}
                      onUpdate={updateInvitedRow}
                      onDelete={deleteInvitedRow}
                      onRegister={handleOpenShopRegister}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {shopRegisterModal && (
        <div className="shop-reg-modal-overlay" onClick={() => !shopRegBusy && setShopRegisterModal(null)}>
          <div className="shop-reg-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="shop-reg-title"><Store size={18} /> 店舗を登録</h3>
            <p className="shop-reg-note">この大会の店舗リストに追加します。</p>
            <input
              type="text"
              name={shopRegInputName}
              value={newShopName}
              onChange={(e) => { setNewShopName(e.target.value); if (shopRegError) setShopRegError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !shopRegBusy) handleRegisterShop(); }}
              placeholder="店舗名を入力"
              autoComplete="off" translate="no" data-form-type="other"
              autoFocus
            />
            {shopRegError && <div className="shop-reg-error">{shopRegError}</div>}
            <div className="shop-reg-actions">
              <button className="shop-reg-cancel" onClick={() => setShopRegisterModal(null)} disabled={shopRegBusy}>キャンセル</button>
              <button className="shop-reg-submit" onClick={handleRegisterShop} disabled={shopRegBusy}>
                {shopRegBusy ? '登録中...' : '登録する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EntryColumn({ start, rows, duplicateNames, shops, onUpdate, onDelete, onRegister }) {
  return (
    <table className="entry-table">
      <thead>
        <tr>
          <th className="col-no">#</th>
          <th className="col-name">名前</th>
          <th className="col-shop">店舗</th>
          <th className="col-rt">Rt.</th>
          <th className="col-trash"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((entry, i) => {
          const realEntriesIndex = entry ? entry.originalIndex : -1;
          const isDup = entry?.name?.trim() ? duplicateNames.has(entry.name.trim()) : false;
          return (
            <EntryRow
              key={`row-${start - 1 + i}`}
              no={start + i}
              entry={entry}
              realEntriesIndex={realEntriesIndex}
              isDup={isDup}
              shops={shops}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onRegister={onRegister}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function areEntryRowEqual(prev, next) {
  if (prev.no !== next.no) return false;
  if (prev.realEntriesIndex !== next.realEntriesIndex) return false;
  if (prev.isDup !== next.isDup) return false;
  if (prev.shops !== next.shops) return false;
  if (prev.onUpdate !== next.onUpdate) return false;
  if (prev.onDelete !== next.onDelete) return false;
  if (prev.onRegister !== next.onRegister) return false;
  const a = prev.entry, b = next.entry;
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.name === b.name &&
    a.shop === b.shop &&
    a.rating === b.rating &&
    a.isInvited === b.isInvited &&
    a.isAttended === b.isAttended
  );
}

const EntryRow = React.memo(function EntryRow({ no, entry, realEntriesIndex, isDup, shops, onUpdate, onDelete, onRegister }) {
  const [name, setName] = useState(entry?.name || '');
  const [rating, setRating] = useState(entry?.rating || '');
  const nameInputName = useMemo(rndName, []);
  const ratingInputName = useMemo(rndName, []);

  useEffect(() => setName(entry?.name || ''), [entry?.name]);
  useEffect(() => setRating(entry?.rating || ''), [entry?.rating]);

  const hasContent = !!(entry?.name?.trim() || entry?.shop || entry?.rating);
  const isAttended = !!entry?.isAttended;

  return (
    <tr className={`${hasContent ? 'has-content' : ''} ${isAttended ? 'is-attended' : ''}`}>
      <td className="col-no">{no}</td>
      <td className="col-name">
        <div className="name-wrap">
          <input
            type="checkbox"
            className="attend-checkbox"
            checked={isAttended}
            onChange={(e) => onUpdate(realEntriesIndex, 'isAttended', e.target.checked)}
            disabled={!hasContent}
            aria-label="来場チェック"
          />
          <div className="name-input-wrap">
            {isDup && <span className="dup-mark" title="同名あり"><AlertTriangle size={12} color="#f39c12" /></span>}
            <input
              type="text" name={nameInputName} value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name !== (entry?.name || '') && onUpdate(realEntriesIndex, 'name', name)}
              autoComplete="off" translate="no" data-form-type="other"
            />
          </div>
        </div>
      </td>
      <td className="col-shop">
        <ShopSelect
          value={entry?.shop || ''}
          shops={shops}
          onChange={(value) => onUpdate(realEntriesIndex, 'shop', value)}
          onRegister={() => onRegister(realEntriesIndex, false)}
        />
      </td>
      <td className="col-rt">
        <input
          type="number" name={ratingInputName} step="1" min="0" value={rating}
          onChange={(e) => { const v = e.target.value; if (v === '' || /^\d+$/.test(v)) setRating(v); }}
          onBlur={() => rating !== (entry?.rating || '') && onUpdate(realEntriesIndex, 'rating', rating)}
          autoComplete="off" data-form-type="other"
        />
      </td>
      <td className="col-trash">
        <button
          type="button"
          className={`trash-btn ${!hasContent ? 'empty-row' : ''}`}
          onClick={() => onDelete(realEntriesIndex)}
          disabled={!hasContent}
          aria-label="削除"
        >
          <Trash2 size={20} />
        </button>
      </td>
    </tr>
  );
}, areEntryRowEqual);

function areInvitedRowEqual(prev, next) {
  if (prev.no !== next.no) return false;
  if (prev.realEntriesIndex !== next.realEntriesIndex) return false;
  if (prev.isDup !== next.isDup) return false;
  if (prev.shops !== next.shops) return false;
  if (prev.onUpdate !== next.onUpdate) return false;
  if (prev.onDelete !== next.onDelete) return false;
  if (prev.onRegister !== next.onRegister) return false;
  const a = prev.entry, b = next.entry;
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.name === b.name &&
    a.shop === b.shop &&
    a.rating === b.rating &&
    a.isInvited === b.isInvited &&
    a.isAttended === b.isAttended
  );
}

const InvitedRow = React.memo(function InvitedRow({ no, entry, realEntriesIndex, isDup, shops, onUpdate, onDelete, onRegister }) {
  const [name, setName] = useState(entry?.name || '');
  const [rating, setRating] = useState(entry?.rating || '');
  const nameInputName = useMemo(rndName, []);
  const ratingInputName = useMemo(rndName, []);

  useEffect(() => setName(entry?.name || ''), [entry?.name]);
  useEffect(() => setRating(entry?.rating || ''), [entry?.rating]);

  const hasContent = !!(entry?.name?.trim() || entry?.shop || entry?.rating);
  const isAttended = !!entry?.isAttended;

  return (
    <tr className={`invited-row ${hasContent ? 'has-content' : ''} ${isAttended ? 'is-attended' : ''}`}>
      <td className="col-no">{no}</td>
      <td className="col-name">
        <div className="name-wrap">
          <input
            type="checkbox"
            className="attend-checkbox"
            checked={isAttended}
            onChange={(e) => onUpdate(realEntriesIndex, 'isAttended', e.target.checked)}
            disabled={!hasContent}
            aria-label="来場チェック"
          />
          <div className="name-input-wrap">
            {isDup && <span className="dup-mark" title="同名あり"><AlertTriangle size={12} color="#f39c12" /></span>}
            <input
              type="text" name={nameInputName} value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name !== (entry?.name || '') && onUpdate(realEntriesIndex, 'name', name)}
              autoComplete="off" translate="no" data-form-type="other"
            />
          </div>
        </div>
      </td>
      <td className="col-shop">
        <ShopSelect
          value={entry?.shop || ''}
          shops={shops}
          onChange={(value) => onUpdate(realEntriesIndex, 'shop', value)}
          onRegister={() => onRegister(realEntriesIndex, true)}
        />
      </td>
      <td className="col-rt">
        <input
          type="number" name={ratingInputName} step="1" min="0" value={rating}
          onChange={(e) => { const v = e.target.value; if (v === '' || /^\d+$/.test(v)) setRating(v); }}
          onBlur={() => rating !== (entry?.rating || '') && onUpdate(realEntriesIndex, 'rating', rating)}
          autoComplete="off" data-form-type="other"
        />
      </td>
      <td className="col-trash">
        <button
          type="button"
          className={`trash-btn ${!hasContent ? 'empty-row' : ''}`}
          onClick={() => onDelete(realEntriesIndex)}
          disabled={!hasContent}
          aria-label="削除"
        >
          <Trash2 size={20} />
        </button>
      </td>
    </tr>
  );
}, areInvitedRowEqual);

function ShopSelect({ value, shops, onChange, onRegister }) {
  const REGISTER_VALUE = '__register_shop__';
  const handleChange = (e) => {
    const v = e.target.value;
    if (v === REGISTER_VALUE) {
      if (onRegister) onRegister();
      return; // onChange を呼ばない -> 選択値は元の value に戻る
    }
    onChange(v);
  };
  return (
    <div className="shop-select-wrap">
      <select value={value} onChange={handleChange} translate="no">
        {onRegister && (
          <option value={REGISTER_VALUE} className="shop-register-option">＋ 店舗を登録</option>
        )}
        <option value="">(未選択)</option>
        {shops.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
      </select>
    </div>
  );
}

/* ============================================================
   ③ ペアリング画面（ハイ&ロー厳守）
   ============================================================ */
function PairingScreen() {
  const { user, isAdmin, shopName } = useAuthUser();
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [currentPairs, setCurrentPairs] = useState(null);
  const [currentLeftover, setCurrentLeftover] = useState(null);
  const [selectedSavedId, setSelectedSavedId] = useState(null);
  const [swapMode, setSwapMode] = useState(false);
  const [swapFirst, setSwapFirst] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [testPasscodeModal, setTestPasscodeModal] = useState(null);
  const [testPasscodeInput, setTestPasscodeInput] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'events', id), (snap) => {
      if (!snap.exists()) {
        alert('イベントが見つかりません');
        navigate('/');
        return;
      }
      const data = snap.data();
      if (user && !isAdmin && data.ownerUid !== user.uid) {
        alert('このイベントへのアクセス権がありません');
        navigate('/');
        return;
      }
      setEvent({ id: snap.id, ...data });
    }, (err) => {
      console.error('イベント購読エラー', err);
      alert('イベントを読み込めませんでした（権限がないか、通信エラーです）');
      navigate('/');
    });
    return () => unsub();
  }, [id, navigate, user]);

  useEffect(() => {
    if (!event) return;
    if (selectedSavedId !== null) return;
    if (currentPairs !== null) return;

    const allEntries = (event.entries || []).filter(e => e.name && e.name.trim());
    const missingRating = allEntries.filter(e => !e.rating || isNaN(Number(e.rating)));
    if (missingRating.length > 0) return;
    if (allEntries.length < 2) return;

    const { pairs, leftover } = buildHighLow(allEntries);
    setCurrentPairs(pairs);
    setCurrentLeftover(leftover);
    setIsDirty(false);
  }, [event, currentPairs, selectedSavedId]);

  const handleLogout = async () => {
    if (!window.confirm('ログアウトしますか?')) return;
    await signOut(auth);
    navigate('/login');
  };

  const openSaved = (savedId) => {
    const saved = (event.savedPairings || []).find(p => p.id === savedId);
    if (!saved) return;
    if (isDirty) {
      if (!window.confirm('現在のペア表は保存されていません。読み込むと破棄されます。続行しますか?')) return;
    }
    setCurrentPairs(saved.pairs || []);
    setCurrentLeftover(saved.leftover || null);
    setSelectedSavedId(savedId);
    setSwapMode(false);
    setSwapFirst(null);
    setShowSavedList(false);
    setIsDirty(false);
  };

  const saveCurrent = async () => {
    if (!currentPairs || currentPairs.length === 0) {
      alert('保存するペア表がありません');
      return;
    }

    if (selectedSavedId) {
      const target = (event.savedPairings || []).find(p => p.id === selectedSavedId);
      if (!target) return;
      if (!window.confirm(`「${target.name}」を上書き保存しますか?`)) return;
      const next = (event.savedPairings || []).map(p =>
        p.id === selectedSavedId
          ? { ...p, pairs: currentPairs, leftover: currentLeftover, updatedAt: new Date().toISOString() }
          : p
      );
      await updateDoc(doc(db, 'events', id), { savedPairings: next, updatedAt: serverTimestamp() });
      setIsDirty(false);
      alert('上書き保存しました');
    } else {
      const now = new Date();
      const hh = now.getHours().toString().padStart(2, '0');
      const mm = now.getMinutes().toString().padStart(2, '0');
      const defaultName = `ペア表 ${hh}:${mm}`;
      const name = window.prompt('ペア表の名前を入力してください', defaultName);
      if (name === null) return;
      const finalName = name.trim() || defaultName;

      const newSaved = {
        id: `saved_${Date.now()}`,
        name: finalName,
        pairs: currentPairs,
        leftover: currentLeftover,
        createdAt: new Date().toISOString(),
      };
      const next = [...(event.savedPairings || []), newSaved];
      await updateDoc(doc(db, 'events', id), { savedPairings: next, updatedAt: serverTimestamp() });
      setSelectedSavedId(newSaved.id);
      setIsDirty(false);
      alert(`「${finalName}」として保存しました`);
    }
  };

  const deleteSaved = async (savedId, e) => {
    if (e) e.stopPropagation();
    const target = (event.savedPairings || []).find(p => p.id === savedId);
    if (!target) return;
    if (!window.confirm(`「${target.name}」を削除しますか?\n元に戻せません。`)) return;
    const next = (event.savedPairings || []).filter(p => p.id !== savedId);
    await updateDoc(doc(db, 'events', id), { savedPairings: next, updatedAt: serverTimestamp() });
    if (selectedSavedId === savedId) {
      setSelectedSavedId(null);
      setCurrentPairs(null);
      setCurrentLeftover(null);
    }
  };

  const renameSaved = async (savedId, e) => {
    if (e) e.stopPropagation();
    const target = (event.savedPairings || []).find(p => p.id === savedId);
    if (!target) return;
    const newName = window.prompt('新しい名前を入力してください', target.name);
    if (!newName || newName.trim() === '') return;
    const next = (event.savedPairings || []).map(p =>
      p.id === savedId ? { ...p, name: newName.trim() } : p
    );
    await updateDoc(doc(db, 'events', id), { savedPairings: next, updatedAt: serverTimestamp() });
  };

  const handleSwapClick = (pairIndex, playerKey) => {
    const target = { pairIndex, playerKey };
    if (!swapFirst) {
      setSwapFirst(target);
    } else {
      const newPairs = currentPairs.map(pair => ({ ...pair }));
      const tmp = newPairs[swapFirst.pairIndex][swapFirst.playerKey];
      newPairs[swapFirst.pairIndex][swapFirst.playerKey] = newPairs[pairIndex][playerKey];
      newPairs[pairIndex][playerKey] = tmp;
      setCurrentPairs(newPairs);
      setSwapFirst(null);
      setIsDirty(true);
    }
  };

  // 「このペアリングで決定」: 4パターン警告対応
  const decidePairing = async (pairing) => {
    if (!pairing) return;
    const status = getRobinStatus(event);
    const isCurrentActive = event.activePairingId === pairing.id;

    // パターン4: 既に決定済みのペア表
    if (isCurrentActive && status.hasBlocks) {
      if (status.hasScores) {
        if (!window.confirm('このペア表は既に決定済みです。\n進行中のロビン表に進みますか?')) return;
      } else {
        if (!window.confirm('このペア表は既に決定済みです。\n進行設定に進みますか?')) return;
      }
      setShowSavedList(false);
      navigate(status.hasScores ? `/event/${id}/robin` : `/event/${id}/progress-setting`);
      return;
    }

    // パターン3: スコア入力済み(最重要警告)
    if (status.hasScores) {
      const summary = status.confirmedBlocks.length > 0
        ? `\n・入力済スコア: ${status.scoreCount}試合\n・確定済ブロック: ${status.confirmedBlocks.join(', ')} (${status.confirmedBlocks.length}ブロック)`
        : `\n・入力済スコア: ${status.scoreCount}試合`;

      const choice = window.prompt(
        `警告: 進行中のロビン表があります:${summary}\n\n` +
        `別のペアリングで決定すると、これら全てのデータが完全に削除されます。\n` +
        `元に戻せません。\n\n` +
        `操作を選んでください:\n` +
        `  A : このまま既存のロビン表を続行する(推奨)\n` +
        `  B : 削除してこのペアリングで再スタート\n` +
        `  キャンセル : 何もしない\n\n` +
        `A または B を入力してください:`,
        'A'
      );
      if (choice === null) return;
      const trimmed = choice.trim().toUpperCase();
      if (trimmed === 'A') {
        setShowSavedList(false);
        navigate(`/event/${id}/robin`);
        return;
      }
      if (trimmed !== 'B') {
        alert('キャンセルされました');
        return;
      }
      // パターン3-B: ブロックとスコアをリセットして決定
      if (!window.confirm('本当に削除して再スタートしますか?\nこの操作は元に戻せません。')) return;
      await updateDoc(doc(db, 'events', id), {
        activePairingId: pairing.id,
        blocks: [],
        progressSettings: null,
        updatedAt: serverTimestamp(),
      });
      setShowSavedList(false);
      navigate(`/event/${id}/progress-setting`);
      return;
    }

    // パターン2: blocks有りスコア空(進行設定だけ済み)
    if (status.hasBlocks) {
      if (!window.confirm(
        `このペアリングで決定しますか?\n\n` +
        `ペア表: ${pairing.name}\n\n` +
        `※ 既存の進行設定があります(ブロック分け済み・スコア未入力)\n` +
        `別のペアリングで決定すると、ブロック分けがリセットされ\n` +
        `進行設定からやり直しになります。`
      )) return;
      await updateDoc(doc(db, 'events', id), {
        activePairingId: pairing.id,
        blocks: [],
        progressSettings: null,
        updatedAt: serverTimestamp(),
      });
      setShowSavedList(false);
      navigate(`/event/${id}/progress-setting`);
      return;
    }

    // パターン1: blocks無し(初回・通常)
    if (!window.confirm(
      `このペアリングで決定しますか?\n\n` +
      `ペア表: ${pairing.name}\n` +
      `※決定後、進行設定画面へ進みます`
    )) return;
    await updateDoc(doc(db, 'events', id), {
      activePairingId: pairing.id,
      updatedAt: serverTimestamp(),
    });
    setShowSavedList(false);
    navigate(`/event/${id}/progress-setting`);
  };

  // テストモード: 本番を複製した使い捨てイベントを作り、進行設定へ
  //   本番イベント(id)には一切書き込まない(addDoc で別ドキュメントを作るだけ)
  const startTestMode = async (pairing) => {
    if (!pairing) return;
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const mmdd = `${pad(now.getMonth() + 1)}/${pad(now.getDate())}`;
    const hhmm = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    try {
      // 1イベント1テスト: 既存テストがあれば開く(addDocしない)
      const snap = await getDocs(query(collection(db, 'events'), where('ownerUid', '==', user.uid)));
      const existing = snap.docs.find(d => {
        const data = d.data();
        return data.isTest === true && data.testOriginId === id;
      });
      if (existing) {
        navigate(`/event/${existing.id}/robin`);
        return;
      }
      const ref = await addDoc(collection(db, 'events'), {
        ownerUid: user.uid,
        isTest: true,
        testOriginId: id,
        date: event.date || '',
        houseName: `【TEST】${event.houseName || ''} ${mmdd} ${hhmm}`,
        entries: event.entries || [],
        savedPairings: event.savedPairings || [],
        progressSettings: event.progressSettings || {},
        activePairingId: pairing.id,
        blocks: [],
        tournament: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      navigate(`/event/${ref.id}/robin`);
    } catch (err) {
      console.error('テストモード作成失敗', err);
      alert('テストモードの作成に失敗しました。通信状況を確認してください。');
    }
  };

  // テストモード: パスコード認証(5478)を通過したら生成
  const submitTestPasscode = () => {
    const pairing = testPasscodeModal;
    if (!pairing) return;
    if (testPasscodeInput.trim() !== PASSCODE_PROGRESS) {
      alert('パスコードが違います');
      return;
    }
    setTestPasscodeModal(null);
    setTestPasscodeInput('');
    startTestMode(pairing);
  };

  if (!event) return <div className="page"><div className="loading">読み込み中...</div></div>;

  const allEntries = (event.entries || []).filter((e) => e.name && e.name.trim());
  const missingRating = allEntries.filter((e) => !e.rating || isNaN(Number(e.rating)));
  const savedPairings = event.savedPairings || [];
  const currentPatternName = selectedSavedId
    ? savedPairings.find(p => p.id === selectedSavedId)?.name || '保存済みペア表'
    : '生成中のペア表（未保存）';

  const handleShowPairingPdf = () => {
    const pairing = {
      name: currentPatternName,
      pairs: currentPairs || [],
    };
    openPairingPdf({ event, pairing }).catch((error) => {
      console.error('Pairing PDF creation failed:', error);
      window.alert('PDFの作成に失敗しました。時間をおいて再度お試しください。');
    });
  };

  return (
    <div className="page no-select">
      <header className="header no-print">
        <h1>
          <button className="btn-back" onClick={() => navigate(`/event/${id}`)}>
            <ArrowLeft size={14} /> 戻る
          </button>
          <span style={{ marginLeft: 8 }}>ペアリング</span>
        </h1>
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
          <Menu size={20} />
        </button>
      </header>

      <HamburgerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        shopName={shopName}
        email={user?.email}
        isAdmin={isAdmin}
        onNavigate={navigate}
        onLogout={handleLogout}
        extraItems={[
          { icon: <List size={18} />, label: 'エントリー表に戻る', onClick: () => navigate(`/event/${id}`) },
        ]}
      />

      <div className="container">
        <ActiveRobinBar event={event} eventId={id} navigate={navigate} />

        <div className="event-info-bar">
          <span><Calendar size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />{event.date}</span>
          <span translate="no"><Home size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />{event.houseName}</span>
          <span>エントリー {allEntries.length}人</span>
        </div>

        {missingRating.length > 0 && (
          <div className="warning-box">
            <AlertTriangle size={16} /> Rt.未入力の方がいます：<span translate="no">{missingRating.map((e) => e.name).join('、')}</span>
          </div>
        )}

        <div className="toolbar no-print">
          <button className="btn-primary" style={{ width: 'auto', margin: 0 }} onClick={() => setShowSavedList(true)}>
            <List size={16} /> ペア表一覧 ({savedPairings.length})
          </button>
        </div>

        {!currentPairs || currentPairs.length === 0 ? (
          <div className="empty">
            {missingRating.length > 0
              ? 'エントリーシートで全員のRt.を入力してください'
              : allEntries.length < 2
                ? '2人以上のエントリーが必要です'
                : 'ペア表が自動生成されます...'}
          </div>
        ) : (
          <>
            <div className="pattern-header">
              <h2 translate="no">
                {currentPatternName}
                {isDirty && <span className="dirty-mark"> *未保存の変更あり</span>}
              </h2>
              <div className="pattern-actions no-print">
                <button className={swapMode ? 'btn-warning active' : ''} onClick={() => { setSwapMode(!swapMode); setSwapFirst(null); }}>
                  <ArrowLeftRight size={14} /> {swapMode ? '入れ替え終了' : '手動入れ替え'}
                </button>
                <button className="btn-save" onClick={saveCurrent}>
                  <Save size={14} /> {selectedSavedId ? '上書き保存' : '保存'}
                </button>
                <button onClick={handleShowPairingPdf}><Printer size={14} /> PDF表示</button>
              </div>
            </div>

            {swapMode && (
              <div className="info-box">
                入れ替えたい2人をタップして交換できます {swapFirst && `(1人目選択中：${swapFirst.playerKey === 'player1' ? '左' : '右'})`}
              </div>
            )}

            <table className="pairing-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>プレイヤー1</th>
                  <th>Rt.</th>
                  <th></th>
                  <th>プレイヤー2</th>
                  <th>Rt.</th>
                </tr>
              </thead>
              <tbody>
                {currentPairs.map((pair, i) => {
                  const s1 = (pair.player1.shop || '').trim();
                  const s2 = (pair.player2.shop || '').trim();
                  const sameShop = !!(s1 && s1 === s2);
                  const isSwap1 = swapFirst && swapFirst.pairIndex === i && swapFirst.playerKey === 'player1';
                  const isSwap2 = swapFirst && swapFirst.pairIndex === i && swapFirst.playerKey === 'player2';
                  return (
                    <tr key={i} className={sameShop ? 'same-shop' : ''}>
                      <td>{i + 1}</td>
                      <td
                        className={`player-cell ${pair.player1.isInvited ? 'invited-player' : ''} ${isSwap1 ? 'swap-selected' : ''} ${swapMode ? 'swap-clickable' : ''}`}
                        onClick={() => swapMode && handleSwapClick(i, 'player1')}
                        translate="no"
                      >
                        {pair.player1.isInvited && <span className="invited-badge">招</span>}
                        <span className="player-name">{pair.player1.name}</span>
                        {s1 && <span className="player-shop">{s1}</span>}
                      </td>
                      <td>{pair.player1.rating}</td>
                      <td className="vs">-</td>
                      <td
                        className={`player-cell ${pair.player2.isInvited ? 'invited-player' : ''} ${isSwap2 ? 'swap-selected' : ''} ${swapMode ? 'swap-clickable' : ''}`}
                        onClick={() => swapMode && handleSwapClick(i, 'player2')}
                        translate="no"
                      >
                        {pair.player2.isInvited && <span className="invited-badge">招</span>}
                        <span className="player-name">{pair.player2.name}</span>
                        {s2 && <span className="player-shop">{s2}</span>}
                      </td>
                      <td>{pair.player2.rating}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {currentLeftover && (
              <div className="leftover-box">
                <AlertTriangle size={16} /> <strong translate="no">{currentLeftover.isInvited ? '(招待)' : ''}{currentLeftover.name}</strong>さんが余っています(Rt. {currentLeftover.rating})
              </div>
            )}

            {currentPairs.some((p) => {
              const s1 = (p.player1.shop || '').trim();
              const s2 = (p.player2.shop || '').trim();
              return s1 && s1 === s2;
            }) && (
                <div className="warning-box">
                  <AlertTriangle size={16} /> 同店舗ペアが含まれています(ピンク背景の行)。必要に応じて「手動入れ替え」で調整してください。
                </div>
              )}
          </>
        )}

        {showSavedList && (
          <div className="saved-list-modal-overlay" onClick={() => setShowSavedList(false)}>
            <div className="saved-list-modal" onClick={(e) => e.stopPropagation()}>
              <div className="saved-list-header">
                <h2>保存済みペア表 ({savedPairings.length}件)</h2>
                <button className="saved-list-close" onClick={() => setShowSavedList(false)} aria-label="閉じる">
                  <X size={20} />
                </button>
              </div>
              <div className="saved-list-body">
                {savedPairings.length === 0 ? (
                  <div className="empty">保存されたペア表はありません</div>
                ) : (
                  [...savedPairings]
                    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
                    .map(p => {
                      const date = p.createdAt ? new Date(p.createdAt) : null;
                      const dateStr = date
                        ? `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`
                        : '';
                      const isActive = selectedSavedId === p.id;
                      const isCurrentActive = event.activePairingId === p.id;
                      return (
                        <div key={p.id} className={`saved-item ${isActive ? 'active' : ''} ${isCurrentActive ? 'current-active' : ''}`}>
                          <div className="saved-item-info">
                            <div className="saved-item-name" translate="no">
                              {p.name}
                              {isCurrentActive && <span className="active-pairing-badge">決定済</span>}
                            </div>
                            <div className="saved-item-date">保存: {dateStr} ／ {p.pairs?.length || 0}ペア</div>
                          </div>
                          <div className="saved-item-actions">
                            <button className="open-btn" onClick={() => openSaved(p.id)}>開く</button>
                            <button className="rename-btn" onClick={(e) => renameSaved(p.id, e)}><Edit2 size={12} /> リネーム</button>
                            <button className="delete-btn" onClick={(e) => deleteSaved(p.id, e)}><Trash2 size={12} /> 削除</button>
                          </div>
                          <div className="saved-item-decide-row">
                            <button className="btn-decide-pairing" onClick={() => decidePairing(p)}>
                              <PlayCircle size={14} /> このペアリングで決定
                            </button>
                            <button className="btn-test-mode" onClick={() => { setTestPasscodeInput(''); setTestPasscodeModal(p); }}>
                              <FlaskConical size={14} /> テストモードで試す
                            </button>
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          </div>
        )}

      {testPasscodeModal && (
        <div className="score-input-overlay" onClick={() => { setTestPasscodeModal(null); setTestPasscodeInput(''); }}>
          <div className="score-input-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360, color: '#222' }}>
            <div className="passcode-card">
              <Lock size={32} color="#1a1a2e" />
              <h2>テストモードを開始</h2>
              <p className="passcode-note">パスコードを入力してください。</p>
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={testPasscodeInput}
                onChange={(e) => setTestPasscodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.nativeEvent.isComposing && submitTestPasscode()}
                className="passcode-input"
                placeholder="••••"
                autoFocus
              />
              <button className="btn-accent btn-large" onClick={submitTestPasscode}>開始</button>
              <button className="btn-secondary" style={{ marginTop: 12 }} onClick={() => { setTestPasscodeModal(null); setTestPasscodeInput(''); }}>キャンセル</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

/* ============================================================
   進行管理用 定数・ヘルパー
   ============================================================ */
const PASSCODE_PROGRESS = '5478';

const LEG_FORMATS = [
  { value: '501', label: '501' },
  { value: '301', label: '301' },
  { value: '701', label: '701' },
  { value: 'cricket', label: 'クリケット' },
  { value: 'cricket10R', label: 'クリケット(10R)' },
];

// NOTE: STEP Cでは既存 ROBIN_ORDER を残す。
// nextSchedule 以外の触禁ロジック (matchOrder 等) が参照しているため、削除・移動しない。
// computeNextSchedule / Cloud Functions では shared/robinScheduleConfig.js の方を正とする。
// 完全一元化 (このフロント定義の削除) は第2段階の本番安定後、別フェーズ・別commitで実施。
const ROBIN_ORDER = {

  3: [[1, 2], [1, 3], [2, 3]],
  4: [[1, 2], [3, 4], [1, 3], [2, 4], [1, 4], [2, 3]],
  5: [[1, 2], [3, 4], [1, 5], [2, 3], [4, 5], [1, 3], [2, 4], [3, 5], [1, 4], [2, 5]],
  6: [
    [1, 2], [5, 6], [1, 3], [4, 6], [3, 5], [1, 4],
    [2, 6], [1, 5], [3, 6], [2, 4], [4, 5], [2, 3],
  ],
};

function buildBlocks(pairs, blockCount) {
  if (!pairs || pairs.length === 0 || blockCount < 1) return [];

  const sortedPairs = [...pairs].map((p, idx) => ({
    ...p,
    originalIdx: idx,
    sumRt: (Number(p.player1?.rating) || 0) + (Number(p.player2?.rating) || 0),
  })).sort((a, b) => b.sumRt - a.sumRt);

  const blocks = [];
  for (let i = 0; i < blockCount; i++) {
    blocks.push({
      id: String.fromCharCode(65 + i),
      pairs: [],
    });
  }

  let direction = 1;
  let blockIdx = 0;
  for (let i = 0; i < sortedPairs.length; i++) {
    const pair = sortedPairs[i];
    blocks[blockIdx].pairs.push({
      player1: pair.player1,
      player2: pair.player2,
    });
    blockIdx += direction;
    if (blockIdx >= blockCount) {
      blockIdx = blockCount - 1;
      direction = -1;
    } else if (blockIdx < 0) {
      blockIdx = 0;
      direction = 1;
    }
  }

  for (let attempt = 0; attempt < 3; attempt++) {
    let swapped = false;
    for (let bi = 0; bi < blocks.length; bi++) {
      const block = blocks[bi];
      const playerSet = [];
      block.pairs.forEach(p => {
        if (p.player1?.shop) playerSet.push(p.player1.shop);
        if (p.player2?.shop) playerSet.push(p.player2.shop);
      });
      const counts = {};
      playerSet.forEach(s => {
        if (s && s.trim()) counts[s.trim()] = (counts[s.trim()] || 0) + 1;
      });
      const overShop = Object.entries(counts).find(([_, c]) => c >= 3);
      if (!overShop) continue;
      for (let bj = 0; bj < blocks.length; bj++) {
        if (bi === bj) continue;
        if (blocks[bj].pairs.length === 0) continue;
        const lastIdx = blocks[bj].pairs.length - 1;
        const candidate = blocks[bj].pairs[lastIdx];
        const candShops = [candidate.player1?.shop, candidate.player2?.shop].filter(s => s && s.trim());
        if (!candShops.includes(overShop[0])) {
          const myLastIdx = block.pairs.length - 1;
          const tmp = block.pairs[myLastIdx];
          block.pairs[myLastIdx] = candidate;
          blocks[bj].pairs[lastIdx] = tmp;
          swapped = true;
          break;
        }
      }
      if (swapped) break;
    }
    if (!swapped) break;
  }

  return blocks;
}

function describeBlockDistribution(totalPairs, blockCount) {
  if (blockCount < 1 || totalPairs < 1) return '';
  const base = Math.floor(totalPairs / blockCount);
  const remainder = totalPairs % blockCount;
  if (remainder === 0) {
    return `${base}ペア × ${blockCount}ブロック`;
  }
  return `${base + 1}ペア × ${remainder}ブロック、${base}ペア × ${blockCount - remainder}ブロック`;
}

/* ============================================================
   進行状態判定 ヘルパー
   ============================================================ */
function getRobinStatus(event) {
  // 戻り値: { hasBlocks, hasScores, scoreCount, confirmedBlocks, totalBlocks }
  const blocks = event?.blocks || [];
  if (blocks.length === 0) {
    return { hasBlocks: false, hasScores: false, scoreCount: 0, confirmedBlocks: [], totalBlocks: 0 };
  }
  let scoreCount = 0;
  const confirmedBlocks = [];
  blocks.forEach(b => {
    const scoreEntries = Object.keys(b.scores || {});
    scoreCount += scoreEntries.length;
    if (b.confirmed) confirmedBlocks.push(b.id);
  });
  return {
    hasBlocks: true,
    hasScores: scoreCount > 0,
    scoreCount,
    confirmedBlocks,
    totalBlocks: blocks.length,
  };
}

/* ============================================================
   進行中バー(共通)
   進行中のロビン表がある時、各画面の上部に表示
   ============================================================ */
function ActiveRobinBar({ event, eventId, navigate }) {
  const status = getRobinStatus(event);
  if (!status.hasScores) return null; // スコア1つ以上入力済みの時のみ表示

  const summary = status.confirmedBlocks.length > 0
    ? `確定済: ${status.confirmedBlocks.join('/')} (${status.confirmedBlocks.length}/${status.totalBlocks})`
    : `${status.scoreCount}試合 入力済`;

  return (
    <div className="active-robin-bar" onClick={() => navigate(`/event/${eventId}/robin`)}>
      <div className="active-robin-bar-icon">
        <PlayCircle size={18} />
      </div>
      <div className="active-robin-bar-text">
        <div className="active-robin-bar-title">進行中のロビン表があります</div>
        <div className="active-robin-bar-summary">{summary}</div>
      </div>
      <div className="active-robin-bar-action">
        ロビン表へ <ChevronRight size={16} />
      </div>
    </div>
  );
}

/* ============================================================
   ⑤ 進行設定画面
   ============================================================ */
function ProgressSettingScreen() {
  const { user, isAdmin, shopName } = useAuthUser();
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [blockCount, setBlockCount] = useState(4);
  const [advancePerSize, setAdvancePerSize] = useState({ 3: 1, 4: 2, 5: 2, 6: 3 });
  const [losersBracket, setLosersBracket] = useState(false);
  const [losersBracketAdvanceCount, setLosersBracketAdvanceCount] = useState(0);
  const [legFormats, setLegFormats] = useState(['501', '501', '501']);
  const [minutesPerMatch, setMinutesPerMatch] = useState(15);
  const [startTime, setStartTime] = useState('13:30');
  const [tournamentStartTime, setTournamentStartTime] = useState('18:00');
  const [machineCount, setMachineCount] = useState(4);

  const [passcodeInput, setPasscodeInput] = useState('');
  const [showPasscode, setShowPasscode] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'events', id), (snap) => {
      if (!snap.exists()) {
        alert('イベントが見つかりません');
        navigate('/');
        return;
      }
      const data = snap.data();
      if (user && !isAdmin && data.ownerUid !== user.uid) {
        alert('このイベントへのアクセス権がありません');
        navigate('/');
        return;
      }
      setEvent({ id: snap.id, ...data });
      if (data.progressSettings) {
        const ps = data.progressSettings;
        if (ps.blockCount) setBlockCount(ps.blockCount);
        if (ps.advancePerSize) setAdvancePerSize({ ...{ 3: 1, 4: 2, 5: 2, 6: 3 }, ...ps.advancePerSize });
        if (typeof ps.losersBracket === 'boolean') setLosersBracket(ps.losersBracket);
        if (typeof ps.losersBracketAdvanceCount === 'number') setLosersBracketAdvanceCount(ps.losersBracketAdvanceCount);
        if (Array.isArray(ps.legFormats)) setLegFormats(ps.legFormats);
        if (typeof ps.minutesPerMatch === 'number') setMinutesPerMatch(ps.minutesPerMatch);
        if (typeof ps.startTime === 'string') setStartTime(ps.startTime);
        if (typeof ps.tournamentStartTime === 'string') setTournamentStartTime(ps.tournamentStartTime);
        if (typeof ps.machineCount === 'number') setMachineCount(ps.machineCount);
      }
    }, (err) => {
      console.error('イベント購読エラー', err);
      alert('イベントを読み込めませんでした（権限がないか、通信エラーです）');
      navigate('/');
    });
    return () => unsub();
  }, [id, navigate, user]);

  const handleLogout = async () => {
    if (!window.confirm('ログアウトしますか?')) return;
    await signOut(auth);
    navigate('/login');
  };

  const activePairing = useMemo(() => {
    if (!event) return null;
    const saved = event.savedPairings || [];
    if (saved.length === 0) return null;
    if (event.activePairingId) {
      const target = saved.find(p => p.id === event.activePairingId);
      if (target) return target;
    }
    return saved[saved.length - 1];
  }, [event]);

  const totalPairs = activePairing?.pairs?.length || 0;

  const distribution = useMemo(() => describeBlockDistribution(totalPairs, blockCount), [totalPairs, blockCount]);

  const blockSizes = useMemo(() => {
    if (totalPairs < 1 || blockCount < 1) return [];
    const base = Math.floor(totalPairs / blockCount);
    const remainder = totalPairs % blockCount;
    const sizes = [];
    if (remainder > 0) sizes.push({ size: base + 1, count: remainder });
    if ((blockCount - remainder) > 0) sizes.push({ size: base, count: blockCount - remainder });
    return sizes;
  }, [totalPairs, blockCount]);

  // 試合数計算: 各ブロックサイズの試合数 × ブロック数
  // ROBIN_ORDER参照: 3ペア=3試合, 4ペア=6試合, 5ペア=10試合, 6ペア=12試合(変則)
  const MATCHES_PER_BLOCK = { 3: 3, 4: 6, 5: 10, 6: 12 };
  const totalMatches = useMemo(() => {
    if (totalPairs < 1 || blockCount < 1 || blockSizes.length === 0) return 0;
    let total = 0;
    blockSizes.forEach(({ size, count }) => {
      const matchesInBlock = MATCHES_PER_BLOCK[size] || 0;
      total += matchesInBlock * count;
    });
    return total;
  }, [blockSizes, totalPairs, blockCount]);

  // 所要時間と終了時刻(マシン台数で並列処理として計算)
  const timeCalc = useMemo(() => {
    const minutes = parseInt(minutesPerMatch, 10) || 15;
    const machines = parseInt(machineCount, 10) || 1;
    if (totalMatches === 0 || machines < 1) return { totalMin: 0, hours: 0, mins: 0, endTime: '' };

    // 全試合 ÷ マシン台数 = 1台あたりの試合数(切り上げ)
    const matchesPerMachine = Math.ceil(totalMatches / machines);
    const totalMin = matchesPerMachine * minutes;
    const hours = Math.floor(totalMin / 60);
    const mins = totalMin % 60;

    // 開始時刻+所要時間 = 終了時刻
    let endTime = '';
    if (startTime && /^\d{1,2}:\d{2}$/.test(startTime)) {
      const [sh, sm] = startTime.split(':').map(s => parseInt(s, 10));
      if (!isNaN(sh) && !isNaN(sm)) {
        const startMin = sh * 60 + sm;
        const endMin = startMin + totalMin;
        const eh = Math.floor(endMin / 60) % 24;
        const em = endMin % 60;
        endTime = `${eh.toString().padStart(2, '0')}:${em.toString().padStart(2, '0')}`;
      }
    }
    return { totalMin, hours, mins, endTime, matchesPerMachine };
  }, [totalMatches, minutesPerMatch, startTime, machineCount]);

  // 予選通過者数(各ブロックの通過数を合計)
  const advancersCount = useMemo(() => {
    if (blockSizes.length === 0) return 0;
    let count = 0;
    blockSizes.forEach(({ size, count: blockCnt }) => {
      const advanceTo = parseInt(advancePerSize[size], 10) || 1;
      count += advanceTo * blockCnt;
    });
    return count;
  }, [blockSizes, advancePerSize]);

  // 決勝トーナメント計算
  const tournamentCalc = useMemo(() => {
    const losersCount = losersBracket ? (parseInt(losersBracketAdvanceCount, 10) || 0) : 0;
    const participants = advancersCount + losersCount;

    // P4: 設定警告 (settingWarnings) を計算
    // blockSizes + advancePerSize から擬似 advancers を構築して validateBracketComposition に渡す
    // ※ブロックIDは A, B, C, ... を順に割り当てる (仮)。本番のブロックIDとは別物だが、
    //   rank分布の計算には影響しない。
    const pseudoAdvancers = [];
    let blockIdx = 0;
    blockSizes.forEach(({ size, count: blockCnt }) => {
      const advanceTo = parseInt(advancePerSize[size], 10) || 1;
      for (let b = 0; b < blockCnt; b++) {
        const blockId = String.fromCharCode(65 + blockIdx); // 'A','B','C',...
        for (let r = 1; r <= advanceTo; r++) {
          pseudoAdvancers.push({ blockId, rank: r });
        }
        blockIdx++;
      }
    });
    const validation = validateBracketComposition(pseudoAdvancers, losersCount);

    if (participants < 2) {
      return {
        participants, advancersCount, losersCount,
        totalMatches: 0, durationMin: 0, endTime: '', totalRounds: 0,
        settingWarnings: validation.settingWarnings,
      };
    }
    // シングルエリミ: 全試合数 = N - 1
    const tMatches = participants - 1;
    const totalRounds = Math.ceil(Math.log2(participants));

    const minutes = parseInt(minutesPerMatch, 10) || 15;
    const machines = parseInt(machineCount, 10) || 1;
    const matchesPerMachine = Math.ceil(tMatches / machines);
    const durationMin = matchesPerMachine * minutes;

    let endTime = '';
    if (tournamentStartTime && /^\d{1,2}:\d{2}$/.test(tournamentStartTime)) {
      const [sh, sm] = tournamentStartTime.split(':').map(s => parseInt(s, 10));
      if (!isNaN(sh) && !isNaN(sm)) {
        const startMin = sh * 60 + sm;
        const endMin = startMin + durationMin;
        const eh = Math.floor(endMin / 60) % 24;
        const em = endMin % 60;
        endTime = `${eh.toString().padStart(2, '0')}:${em.toString().padStart(2, '0')}`;
      }
    }
    return {
      participants, advancersCount, losersCount,
      totalMatches: tMatches, durationMin, endTime, totalRounds,
      settingWarnings: validation.settingWarnings,
    };
  }, [advancersCount, losersBracket, losersBracketAdvanceCount, minutesPerMatch, machineCount, tournamentStartTime, blockSizes, advancePerSize]);

  const handlePasscodeSubmit = () => {
    if (passcodeInput !== PASSCODE_PROGRESS) {
      alert('パスコードが違います');
      return;
    }
    if (!window.confirm('進行画面に進みますか?\n\n進行設定が確定し、ブロック分け・ロビン表の作成に進みます。')) return;
    setShowPasscode(false);
  };

  const startProgress = async () => {
    if (!activePairing) {
      alert('ペア表がありません');
      return;
    }
    if (totalPairs < blockCount) {
      alert('ペア数がブロック数より少ないため、ブロック分けできません');
      return;
    }

    // 既存blocksがあるかチェック
    const status = getRobinStatus(event);
    if (status.hasScores) {
      const summary = status.confirmedBlocks.length > 0
        ? `・入力済スコア: ${status.scoreCount}試合\n・確定済ブロック: ${status.confirmedBlocks.join(', ')} (${status.confirmedBlocks.length}ブロック)`
        : `・入力済スコア: ${status.scoreCount}試合`;
      if (!window.confirm(
        `警告: 既に試合スコアが入力されています:\n${summary}\n\n` +
        `新しく進行を開始すると、これら全てのデータが完全に削除されます。\n` +
        `元に戻せません。\n\n` +
        `本当に最初からやり直しますか?`
      )) return;
      if (!window.confirm(`念のため確認します。\n${status.scoreCount}試合分のスコアと進行データを全て削除して、最初から開始します。\nよろしいですか?`)) return;
    } else if (status.hasBlocks) {
      if (!window.confirm(
        `既存のブロック分けがあります(スコア未入力)。\n` +
        `これを破棄して再作成します。よろしいですか?`
      )) return;
    }

    // ブロック分けを実行(buildBlocksは pairs のみ返す)
    const freshBlocks = buildBlocks(activePairing.pairs, blockCount);
    // 各ブロックに scores と confirmed を必ず初期化(古いデータが混入しないよう厳格化)
    const blocksWithScores = freshBlocks.map(b => ({
      id: b.id,
      pairs: b.pairs.map(p => ({
        player1: p.player1,
        player2: p.player2,
      })),
      scores: {}, // 必ず空オブジェクト
      confirmed: false,
    }));

    const settings = {
      blockCount,
      advancePerSize,
      losersBracket,
      losersBracketAdvanceCount: parseInt(losersBracketAdvanceCount, 10) || 0,
      legFormats,
      minutesPerMatch: parseInt(minutesPerMatch, 10) || 15,
      startTime: startTime || '13:30',
      tournamentStartTime: tournamentStartTime || '18:00',
      machineCount: parseInt(machineCount, 10) || 4,
      activePairingId: activePairing.id || null,
      activePairingName: activePairing.name || '',
    };

    // P5a: 配置に影響する設定変更があれば placementDirty を立てる
    // (既存ブラケットがある場合のみ。tournament 未生成時は dirty化不要)
    const existingTournament = event?.tournament;
    const updates = {
      progressSettings: settings,
      blocks: blocksWithScores,
      updatedAt: serverTimestamp(),
    };
    if (existingTournament && existingTournament.matches && existingTournament.matches.length > 0) {
      const prev = event.progressSettings || {};
      const placementAffectingChanged =
        (prev.losersBracketAdvanceCount || 0) !== (parseInt(losersBracketAdvanceCount, 10) || 0) ||
        prev.losersBracket !== losersBracket ||
        JSON.stringify(prev.advancePerSize || {}) !== JSON.stringify(advancePerSize);
      if (placementAffectingChanged) {
        updates.tournament = {
          ...existingTournament,
          placementDirty: true,
          placementDirtyReason: 'settings_changed',
          placementDirtyAt: serverTimestamp(),
          // 設定変更で override も解除 (再判断を強制)
          placementOverride: false,
        };
      }
    }

    try {
      await updateDoc(doc(db, 'events', id), updates);
      navigate(`/event/${id}/robin`);
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    }
  };

  if (!event) return <div className="page"><div className="loading">読み込み中...</div></div>;

  if (showPasscode) {
    return (
      <div className="page no-select">
        <header className="header no-print">
          <h1>
            <button className="btn-back" onClick={() => navigate(`/event/${id}/pairing`)}>
              <ArrowLeft size={14} /> 戻る
            </button>
            <span style={{ marginLeft: 8 }}>進行設定 - パスコード</span>
          </h1>
          <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
            <Menu size={20} />
          </button>
        </header>

        <HamburgerDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          shopName={shopName}
          email={user?.email}
          isAdmin={isAdmin}
          onNavigate={navigate}
          onLogout={handleLogout}
        />

        <div className="container">
          <div className="passcode-card">
            <Lock size={32} color="#1a1a2e" />
            <h2>進行設定に進む</h2>
            <p className="passcode-note">
              パスコードを入力してください。<br />
              <small>(誤タップ防止のため)</small>
            </p>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={passcodeInput}
              onChange={(e) => setPasscodeInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePasscodeSubmit()}
              className="passcode-input"
              placeholder="••••"
              autoFocus
            />
            <button className="btn-accent btn-large" onClick={handlePasscodeSubmit}>
              進む
            </button>
            <button className="btn-secondary" style={{ marginTop: 12 }} onClick={() => navigate(`/event/${id}/pairing`)}>
              キャンセル
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page no-select">
      <header className="header no-print">
        <h1>
          <button className="btn-back" onClick={() => navigate(`/event/${id}/pairing`)}>
            <ArrowLeft size={14} /> 戻る
          </button>
          <span style={{ marginLeft: 8 }}>進行設定</span>
        </h1>
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
          <Menu size={20} />
        </button>
      </header>

      <TestModeBanner event={event} navigate={navigate} />

      <HamburgerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        shopName={shopName}
        email={user?.email}
        isAdmin={isAdmin}
        onNavigate={navigate}
        onLogout={handleLogout}
        extraItems={[
          { icon: <List size={18} />, label: 'エントリー表に戻る', onClick: () => navigate(`/event/${id}`) },
          { icon: <List size={18} />, label: 'ペア一覧に戻る', onClick: () => navigate(`/event/${id}/pairing`) },
        ]}
      />

      <div className="container">
        <ActiveRobinBar event={event} eventId={id} navigate={navigate} />

        <div className="event-info-bar">
          <span><Calendar size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />{event.date}</span>
          <span translate="no"><Home size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />{event.houseName}</span>
          <span>ペア表: <strong translate="no">{activePairing?.name || '(なし)'}</strong> ({totalPairs}ペア)</span>
        </div>

        {!activePairing && (
          <div className="warning-box">
            <AlertTriangle size={16} /> ペア表が保存されていません。先にペアリング画面でペア表を作成してください。
          </div>
        )}

        {activePairing && (
          <>
            <div className="setting-section">
              <h2 className="section-title">1. ブロック数</h2>
              <div className="setting-row">
                <label>ブロック数</label>
                <div className="number-input-wrap">
                  <input
                    type="number"
                    min={1}
                    max={Math.max(1, totalPairs)}
                    value={blockCount}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // 空欄を許可(編集中)
                      if (raw === '') {
                        setBlockCount('');
                        return;
                      }
                      const v = parseInt(raw, 10);
                      if (isNaN(v)) return;
                      if (v < 1) setBlockCount(1);
                      else if (v > totalPairs) setBlockCount(totalPairs);
                      else setBlockCount(v);
                    }}
                    onBlur={(e) => {
                      // フォーカスが外れた時に空欄なら1に戻す
                      if (e.target.value === '' || blockCount === '' || blockCount < 1) {
                        setBlockCount(1);
                      }
                    }}
                    className="setting-input-num with-spinner"
                  />
                  <div className="number-spinner">
                    <button
                      type="button"
                      className="spin-up"
                      onClick={() => {
                        const cur = parseInt(blockCount, 10) || 1;
                        if (cur < totalPairs) setBlockCount(cur + 1);
                      }}
                      aria-label="増やす"
                    >▲</button>
                    <button
                      type="button"
                      className="spin-down"
                      onClick={() => {
                        const cur = parseInt(blockCount, 10) || 1;
                        if (cur > 1) setBlockCount(cur - 1);
                      }}
                      aria-label="減らす"
                    >▼</button>
                  </div>
                </div>
                <span className="setting-suffix">ブロック</span>
              </div>
              <div className="distribution-preview">
                <div className="dist-line">
                  <strong>分配:</strong> <span>{distribution}</span>
                </div>
                <div className="dist-line">
                  <strong>全試合数:</strong> <span className="dist-highlight">{totalMatches} 試合</span> <small>(3レッグ1セット)</small>
                </div>
              </div>
            </div>

            <div className="setting-section">
              <h2 className="section-title">2. ダーツマシン台数 / 試合時間</h2>
              <p className="setting-note">マシン台数と1試合時間から予選所要時間を自動計算します</p>

              <div className="setting-row">
                <label>マシン台数</label>
                <div className="number-input-wrap">
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={machineCount}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') { setMachineCount(''); return; }
                      const v = parseInt(raw, 10);
                      if (isNaN(v)) return;
                      if (v < 1) setMachineCount(1);
                      else if (v > 99) setMachineCount(99);
                      else setMachineCount(v);
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || machineCount === '' || machineCount < 1) setMachineCount(1);
                    }}
                    className="setting-input-num with-spinner"
                  />
                  <div className="number-spinner">
                    <button type="button" className="spin-up" onClick={() => {
                      const cur = parseInt(machineCount, 10) || 1;
                      if (cur < 99) setMachineCount(cur + 1);
                    }} aria-label="増やす">▲</button>
                    <button type="button" className="spin-down" onClick={() => {
                      const cur = parseInt(machineCount, 10) || 1;
                      if (cur > 1) setMachineCount(cur - 1);
                    }} aria-label="減らす">▼</button>
                  </div>
                </div>
                <span className="setting-suffix">台</span>
              </div>

              <div className="setting-row">
                <label>1試合あたり</label>
                <div className="number-input-wrap">
                  <input
                    type="number"
                    min={1}
                    max={120}
                    value={minutesPerMatch}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') { setMinutesPerMatch(''); return; }
                      const v = parseInt(raw, 10);
                      if (isNaN(v)) return;
                      if (v < 1) setMinutesPerMatch(1);
                      else if (v > 120) setMinutesPerMatch(120);
                      else setMinutesPerMatch(v);
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || minutesPerMatch === '' || minutesPerMatch < 1) setMinutesPerMatch(15);
                    }}
                    className="setting-input-num with-spinner"
                  />
                  <div className="number-spinner">
                    <button type="button" className="spin-up" onClick={() => {
                      const cur = parseInt(minutesPerMatch, 10) || 15;
                      if (cur < 120) setMinutesPerMatch(cur + 1);
                    }} aria-label="増やす">▲</button>
                    <button type="button" className="spin-down" onClick={() => {
                      const cur = parseInt(minutesPerMatch, 10) || 15;
                      if (cur > 1) setMinutesPerMatch(cur - 1);
                    }} aria-label="減らす">▼</button>
                  </div>
                </div>
                <span className="setting-suffix">分</span>
              </div>

              <div className="setting-row">
                <label>開始時刻</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="setting-input-time"
                />
              </div>

              <div className="distribution-preview time-preview">
                <div className="dist-line">
                  <strong>予選所要時間:</strong> <span className="dist-highlight">
                    {timeCalc.hours > 0 ? `${timeCalc.hours}時間` : ''}{timeCalc.mins}分
                  </span>
                  <small>(1台あたり {timeCalc.matchesPerMachine || 0} 試合)</small>
                </div>
                <div className="dist-line">
                  <strong>予選終了予定:</strong> <span className="dist-highlight-end">{timeCalc.endTime || '-'}</span>
                </div>
              </div>
            </div>

            <div className="setting-section">
              <h2 className="section-title">3. 予選通過数</h2>
              <p className="setting-note">ブロックのペア数ごとに「上位何位までが予選通過」を設定</p>
              {blockSizes.map(({ size }) => {
                const curValue = advancePerSize[size];
                return (
                  <div className="setting-row" key={size}>
                    <label>{size}ペアのブロック</label>
                    <div className="number-input-wrap">
                      <input
                        type="number"
                        min={1}
                        max={size}
                        value={curValue === '' || curValue === undefined ? '' : curValue}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setAdvancePerSize({ ...advancePerSize, [size]: '' });
                            return;
                          }
                          const v = parseInt(raw, 10);
                          if (isNaN(v)) return;
                          if (v < 1) setAdvancePerSize({ ...advancePerSize, [size]: 1 });
                          else if (v > size) setAdvancePerSize({ ...advancePerSize, [size]: size });
                          else setAdvancePerSize({ ...advancePerSize, [size]: v });
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '' || curValue === '' || curValue < 1) {
                            setAdvancePerSize({ ...advancePerSize, [size]: 1 });
                          }
                        }}
                        className="setting-input-num with-spinner"
                      />
                      <div className="number-spinner">
                        <button
                          type="button"
                          className="spin-up"
                          onClick={() => {
                            const cur = parseInt(curValue, 10) || 1;
                            if (cur < size) setAdvancePerSize({ ...advancePerSize, [size]: cur + 1 });
                          }}
                          aria-label="増やす"
                        >▲</button>
                        <button
                          type="button"
                          className="spin-down"
                          onClick={() => {
                            const cur = parseInt(curValue, 10) || 1;
                            if (cur > 1) setAdvancePerSize({ ...advancePerSize, [size]: cur - 1 });
                          }}
                          aria-label="減らす"
                        >▼</button>
                      </div>
                    </div>
                    <span className="setting-suffix">位まで通過</span>
                  </div>
                );
              })}
            </div>

            <div className="setting-section">
              <h2 className="section-title">4. ゲームフォーマット</h2>
              <p className="setting-note">3レッグマッチ(2勝で勝利)。各レッグのフォーマットを選択</p>
              {[0, 1, 2].map(legIdx => (
                <div className="setting-row" key={legIdx}>
                  <label>第{legIdx + 1}レッグ</label>
                  <select
                    value={legFormats[legIdx] || '501'}
                    onChange={(e) => {
                      const next = [...legFormats];
                      next[legIdx] = e.target.value;
                      setLegFormats(next);
                    }}
                    className="setting-select"
                  >
                    {LEG_FORMATS.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="setting-section">
              <h2 className="section-title">5. 敗者復活</h2>
              <div className="setting-row">
                <label className="setting-checkbox-label">
                  <input
                    type="checkbox"
                    checked={losersBracket}
                    onChange={(e) => setLosersBracket(e.target.checked)}
                    className="setting-checkbox"
                  />
                  <span>敗者復活戦を有効にする (ロビン落選チームから一発勝負で枠争奪)</span>
                </label>
              </div>
              {losersBracket && (
                <div className="setting-row">
                  <label>復活枠</label>
                  <div className="number-input-wrap">
                    <input
                      type="number"
                      min={0}
                      max={32}
                      value={losersBracketAdvanceCount}
                      onChange={(e) => {
                        if (e.target.value === '') {
                          setLosersBracketAdvanceCount('');
                          return;
                        }
                        const v = parseInt(e.target.value, 10);
                        if (isNaN(v)) return;
                        if (v < 0) setLosersBracketAdvanceCount(0);
                        else if (v > 32) setLosersBracketAdvanceCount(32);
                        else setLosersBracketAdvanceCount(v);
                      }}
                      onBlur={() => {
                        if (losersBracketAdvanceCount === '' || losersBracketAdvanceCount === null) setLosersBracketAdvanceCount(0);
                      }}
                      className="setting-input-num with-spinner"
                    />
                    <div className="number-spinner">
                      <button
                        type="button"
                        className="spin-up"
                        onClick={() => {
                          const cur = parseInt(losersBracketAdvanceCount, 10) || 0;
                          if (cur < 32) setLosersBracketAdvanceCount(cur + 1);
                        }}
                        aria-label="増やす"
                      >▲</button>
                      <button
                        type="button"
                        className="spin-down"
                        onClick={() => {
                          const cur = parseInt(losersBracketAdvanceCount, 10) || 0;
                          if (cur > 0) setLosersBracketAdvanceCount(cur - 1);
                        }}
                        aria-label="減らす"
                      >▼</button>
                    </div>
                  </div>
                  <span className="setting-suffix">組が決勝トーナメントに進出</span>
                </div>
              )}

              {/* P4: 設定警告 (settingWarnings) の表示 */}
              {tournamentCalc.settingWarnings && tournamentCalc.settingWarnings.length > 0 && (
                <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                  {tournamentCalc.settingWarnings.map((w, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '6px',
                        background: w.critical ? '#fee' : '#fffbe6',
                        border: w.critical ? '1px solid #f5a' : '1px solid #fc3',
                        color: w.critical ? '#a00' : '#a60',
                        fontSize: '0.92em',
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>{w.critical ? '⛔ ' : '⚠ '}</strong>
                      {w.detail || w.code}
                    </div>
                  ))}
                  <p className="setting-note" style={{ marginTop: '4px', fontSize: '0.85em', color: '#888' }}>
                    ※ 復活通過チームは決勝T 1回戦で2位通過と対戦します。同ブロック同士はなるべく反対の山に配置されます。
                  </p>
                </div>
              )}
            </div>

            <div className="setting-section">
              <h2 className="section-title">6. 決勝トーナメント</h2>
              <p className="setting-note">シングルエリミネーション方式(1回負けたら終わり)</p>
              <div className="setting-row">
                <label>開始時刻</label>
                <input
                  type="time"
                  value={tournamentStartTime}
                  onChange={(e) => setTournamentStartTime(e.target.value)}
                  className="setting-input-time"
                />
              </div>
              <div className="tournament-preview">
                <div className="tournament-preview-row">
                  <span className="tournament-preview-label">参加チーム</span>
                  <span className="tournament-preview-value">
                    <strong>{tournamentCalc.participants}</strong>組
                    {losersBracket && tournamentCalc.losersCount > 0 && (
                      <span className="tournament-preview-breakdown">
                        （予選通過 {tournamentCalc.advancersCount} + 復活 {tournamentCalc.losersCount}）
                      </span>
                    )}
                  </span>
                </div>
                <div className="tournament-preview-row">
                  <span className="tournament-preview-label">ラウンド数</span>
                  <span className="tournament-preview-value">
                    <strong>{tournamentCalc.totalRounds}</strong>ラウンド
                  </span>
                </div>
                <div className="tournament-preview-row">
                  <span className="tournament-preview-label">全試合数</span>
                  <span className="tournament-preview-value">
                    <strong>{tournamentCalc.totalMatches}</strong>試合
                  </span>
                </div>
                <div className="tournament-preview-row">
                  <span className="tournament-preview-label">所要時間</span>
                  <span className="tournament-preview-value">
                    約<strong>{Math.floor(tournamentCalc.durationMin / 60)}</strong>時間
                    <strong>{tournamentCalc.durationMin % 60}</strong>分
                  </span>
                </div>
                <div className="tournament-preview-row tournament-preview-end">
                  <span className="tournament-preview-label">終了予定</span>
                  <span className="tournament-preview-value">
                    <strong>{tournamentCalc.endTime || '--:--'}</strong>
                  </span>
                </div>
              </div>
            </div>

            <div className="setting-actions setting-actions-dual">
              {(() => {
                const status = getRobinStatus(event);
                return (
                  <>
                    {status.hasBlocks ? (
                      <button
                        className="btn-accent btn-large btn-go-robin"
                        onClick={() => navigate(`/event/${id}/robin`)}
                      >
                        <PlayCircle size={18} /> 進行中のロビン表に進む
                        {status.hasScores && <span className="btn-sub-info">({status.scoreCount}試合入力済)</span>}
                      </button>
                    ) : (
                      <button className="btn-large btn-disabled-robin" disabled>
                        <PlayCircle size={18} /> 進行中のロビン表に進む
                        <span className="btn-sub-info">(まだありません)</span>
                      </button>
                    )}
                    <button
                      className={`btn-large ${status.hasScores ? 'btn-restart-warning' : 'btn-accent'}`}
                      onClick={startProgress}
                    >
                      <Play size={16} /> {status.hasBlocks ? '新しく進行を開始(やり直し)' : '進行を開始 (ロビン表作成)'}
                    </button>
                  </>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   ⑥ ロビン表画面 — v4レイアウト完全再現版
   ============================================================ */
function RobinTableScreen() {
  const { user, isAdmin, shopName } = useAuthUser();
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentBlockIdx, setCurrentBlockIdx] = useState(0);
  const [scoreInput, setScoreInput] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSelected, setEditSelected] = useState(null); // { blockIdx, pairIdx }

  // 1b: 選手名・店舗の直接編集（純リラベル・A→B当日交代用）
  const [editPlayerModal, setEditPlayerModal] = useState(null); // { blockIdx, pairIdx, playerKey }
  const [editPlayerName, setEditPlayerName] = useState('');
  const [editPlayerShop, setEditPlayerShop] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'events', id), (snap) => {
      if (!snap.exists()) {
        alert('イベントが見つかりません');
        navigate('/');
        return;
      }
      const data = snap.data();
      if (user && !isAdmin && data.ownerUid !== user.uid) {
        alert('このイベントへのアクセス権がありません');
        navigate('/');
        return;
      }
      setEvent({ id: snap.id, ...data });
    }, (err) => {
      console.error('イベント購読エラー', err);
      alert('イベントを読み込めませんでした（権限がないか、通信エラーです）');
      navigate('/');
    });
    return () => unsub();
  }, [id, navigate, user]);

  const handleLogout = async () => {
    if (!window.confirm('ログアウトしますか?')) return;
    await signOut(auth);
    navigate('/login');
  };

  const blocks = event?.blocks || [];
  const currentBlock = blocks[currentBlockIdx];
  const progressSettings = event?.progressSettings || {};

  // Step2: 進行中試合の制御
  //   machineCount: 進行設定で指定されたマシン台数 (デフォルト 4)
  //   matchStartModal: 試合開始モーダル ({ matchKey, rowTeam, colTeam } or null)
  //   cancelInProgressModal: 進行中キャンセル確認モーダル ({ matchKey, blockIdx } or null)
  const machineCount = Math.max(1, parseInt(progressSettings.machineCount, 10) || 4);
  const [matchStartModal, setMatchStartModal] = useState(null);
  const [cancelInProgressModal, setCancelInProgressModal] = useState(null);

  // Step2+6: 全ブロックを横断して「現在使われているマシン番号集合」を計算
  //   同番号同時使用禁止のため、開始モーダルで disabled 判定に使う
  //   Step6: 壊れデータ耐性のため Number.isFinite チェック追加
  const usedMachineNos = useMemo(() => {
    const set = new Set();
    blocks.forEach(b => {
      if (!b.activeMatches) return;
      Object.values(b.activeMatches).forEach(e => {
        if (e && Number.isFinite(e.machineNo)) set.add(e.machineNo);
      });
    });
    return set;
  }, [blocks]);

  // Step3: 進行中の試合に含まれているペアの集合 (blockId + teamNo で識別)
  //   同じペアが2試合同時進行は物理的に不可能なので、新規開始時にこの集合と被るペアを含む試合は弾く
  const inProgressPairs = useMemo(() => {
    const set = new Set();
    blocks.forEach(b => {
      if (!b.activeMatches) return;
      Object.keys(b.activeMatches).forEach(key => {
        const [x, y] = key.split('-').map(s => parseInt(s, 10));
        if (!isNaN(x)) set.add(`${b.id}:${x}`);
        if (!isNaN(y)) set.add(`${b.id}:${y}`);
      });
    });
    return set;
  }, [blocks]);

  // Step3: 「指定試合が進行中ペアを含むか」判定
  const isMatchPairInUse = (teamA, teamB) => {
    if (!currentBlock) return false;
    return inProgressPairs.has(`${currentBlock.id}:${teamA}`) ||
      inProgressPairs.has(`${currentBlock.id}:${teamB}`);
  };

  // Step6+7: 進行順スケジューラ v2 (3 つの NEXT 試合を先読み計算)
  //   仕様詳細は SCHEDULER_V2_DESIGN.md 参照
  //   - 進行率 (完了/総数) の低いブロックから順に NEXT 候補を取り出す
  //   - 1つ NEXT を取り出すごとに virtualDone で仮想進行率を上げて再ソート
  //   - 結果: 全ブロック同時終了を目指す自然な NEXT 並び
  //   Step7 変更点:
  //     - NEXT 表示数を「machineCount - 進行中数」から「常に3つ固定」に変更
  //     - 4台全部進行中でも NEXT が消えないようにし、運営者が次の試合者に
  //       事前に声掛けできるようにする
  //     - 進行中試合は引き続き候補から除外 (同じ試合が NEXT に出ない)
  const nextSchedule = useMemo(() => {
    // STEP C: 本体ロジックを shared/nextSchedule.js (computeNextSchedule) に委譲。
    // STEP B 並走検証で legacy useMemo と出力バイト一致を全エッジケースで確認済み
    // (空/進行中1個/進行中複数&soft回避&横断virtualDone/スコア確定、全て差分0)。
    // ROBIN_ORDER/MATCHES_PER_BLOCK は shared/robinScheduleConfig.js を正とし、
    // Cloud Functions の syncPublicEvent でも同じ computeNextSchedule を使う。
    return computeNextSchedule(blocks, {
      ROBIN_ORDER: SHARED_ROBIN_ORDER,
      MATCHES_PER_BLOCK: SHARED_MATCHES_PER_BLOCK,
    });
  }, [blocks]);



  // Step6: ChatGPT should②: nextSchedule を Map 化して render 時の find を O(1) に
  const nextMap = useMemo(() => {
    const map = {};
    nextSchedule.forEach(ns => {
      map[`${ns.blockId}:${ns.matchKey}`] = ns.order;
    });
    return map;
  }, [nextSchedule]);
  // STEP9: nextSchedule からブロックID単位の NEXT 順位を配列で抽出
  //   { [blockId]: [order1, order2, ...] } (進行中バッジと同仕様、並列表記用)
  //   例: { 'H': [1], 'G': [2], 'A': [3] }
  //   同ブロックに複数のNEXT試合がある場合は配列に全て入る (進行中バッジと統一)
  //   用途: ブロックタブ上に NEXT バッジ表示 (右パネル・セル内NEXTと完全一致)
  const nextOrdersByBlock = useMemo(() => {
    const map = {};
    nextSchedule.forEach(ns => {
      if (!map[ns.blockId]) map[ns.blockId] = [];
      map[ns.blockId].push(ns.order);
    });
    // 各ブロックの NEXT 順位配列を昇順ソート
    Object.keys(map).forEach(blockId => {
      map[blockId].sort((a, b) => a - b);
    });
    return map;
  }, [nextSchedule]);

  // HOTFIX01: 左下セル(行>列)から入力された時の勝敗逆転バグ修正
  //   従来: matchKey だけ持って writeScore(a,b) を `${a}-${b}` で min-max キーに保存
  //         → クリック元の「行視点」が失われ、行>列のセルから入力すると逆になっていた
  //   修正: rowTeam/colTeam を scoreInput に保持し、writeScore で行視点→min-max視点に変換
  const openScoreInput = (rowTeam, colTeam) => {
    if (!currentBlock) return;
    if (currentBlock.confirmed) return;
    const key = matchKey(rowTeam, colTeam);
    const current = (currentBlock.scores && currentBlock.scores[key]) || null;
    setScoreInput({ blockIdx: currentBlockIdx, matchKey: key, rowTeam, colTeam, currentValue: current });
  };

  const writeScore = async (a, b) => {
    if (!scoreInput) return;
    const { blockIdx, matchKey, rowTeam, colTeam } = scoreInput;
    const block = blocks[blockIdx];
    if (!block || block.confirmed) return;

    const newScores = { ...(block.scores || {}) };
    if (a === null && b === null) {
      delete newScores[matchKey];
    } else {
      // HOTFIX01: (a, b) は「行視点」のスコア (rowTeam=a 点, colTeam=b 点)
      // 保存形式は matchKey=(min-max) に対して「min視点」の値が入る必要がある
      // rowTeam が小さい番号なら そのまま、大きい番号なら反転して保存
      const small = Math.min(rowTeam, colTeam);
      const [s1, s2] = (rowTeam === small) ? [a, b] : [b, a];
      newScores[matchKey] = `${s1}-${s2}`;
    }

    // Step2: スコア確定 (a,b が両方 null じゃない) かつ進行中だった場合、
    //   activeMatches から該当試合を解除し、所要時間を matchDurations に記録
    let newActiveMatches = { ...(block.activeMatches || {}) };
    let newMatchDurations = { ...(block.matchDurations || {}) };
    const wasInProgress = newActiveMatches[matchKey];
    if (wasInProgress && a !== null && b !== null) {
      // 開始時刻が serverTimestamp の場合、まだ確定してない値の可能性があるため安全に処理
      const startedAt = wasInProgress.startedAt;
      const finishedAt = new Date();
      // startedAt が Firestore Timestamp かどうかで分岐
      let durationSec = null;
      try {
        const startMs = startedAt?.toDate ? startedAt.toDate().getTime() : null;
        if (startMs) durationSec = Math.max(0, Math.round((finishedAt.getTime() - startMs) / 1000));
      } catch (_) { /* 計測失敗時は durationSec=null */ }
      newMatchDurations[matchKey] = {
        machineNo: wasInProgress.machineNo,
        startedAt: startedAt || null,
        finishedAt: finishedAt,
        durationSec,
      };
      delete newActiveMatches[matchKey];
    }

    const newBlocks = blocks.map((b2, i) => i === blockIdx ? {
      ...b2,
      scores: newScores,
      activeMatches: newActiveMatches,
      matchDurations: newMatchDurations,
    } : b2);

    try {
      await updateDoc(doc(db, 'events', id), {
        blocks: newBlocks,
        updatedAt: serverTimestamp(),
      });
      setScoreInput(null);
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    }
  };

  // Step2: 試合開始処理
  //   進行順番カードのタップで matchStartModal を開く → マシン番号選択 → ここで保存
  const startMatch = async (machineNo) => {
    if (!matchStartModal || !currentBlock) return;
    if (!machineNo || machineNo < 1 || machineNo > machineCount) {
      alert('マシン番号を選択してください');
      return;
    }

    // Step6: ChatGPT 指摘の race condition 対策
    //   モーダル表示中に他の運営者が同じ試合を開始した場合の二重起動を防ぐ
    //   最新の blocks state を参照して再チェック
    const latestBlock = blocks.find(b => b.id === currentBlock.id);
    if (latestBlock?.activeMatches?.[matchStartModal.matchKey]) {
      alert('この試合は既に他の運営者が開始しました。');
      setMatchStartModal(null);
      return;
    }
    // 他で使用中の番号は弾く (UI 側 disabled + handler 側でも再チェック)
    if (usedMachineNos.has(machineNo)) {
      alert(`マシン${['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧'][machineNo - 1]} は他の試合で使用中です`);
      return;
    }

    const newActiveMatches = {
      ...(currentBlock.activeMatches || {}),
      [matchStartModal.matchKey]: {
        machineNo,
        startedAt: new Date(),  // ローカル時刻で記録 (Firestore保存時に serverTimestamp に置換できないため Date 使用)
      },
    };
    const newBlocks = blocks.map((b2, i) => i === currentBlockIdx ? {
      ...b2,
      activeMatches: newActiveMatches,
    } : b2);

    try {
      await updateDoc(doc(db, 'events', id), {
        blocks: newBlocks,
        updatedAt: serverTimestamp(),
      });
      setMatchStartModal(null);
    } catch (err) {
      alert('試合開始の保存に失敗しました: ' + err.message);
    }
  };

  // Step2: 進行中キャンセル (スタート取り消し)
  //   activeMatches から該当試合を削除するだけ、matchDurations は触らない
  const cancelInProgress = async () => {
    if (!cancelInProgressModal) return;
    const { matchKey: ck, blockIdx } = cancelInProgressModal;
    const block = blocks[blockIdx];
    if (!block) return;
    const newActiveMatches = { ...(block.activeMatches || {}) };
    delete newActiveMatches[ck];
    const newBlocks = blocks.map((b2, i) => i === blockIdx ? {
      ...b2,
      activeMatches: newActiveMatches,
    } : b2);
    try {
      await updateDoc(doc(db, 'events', id), {
        blocks: newBlocks,
        updatedAt: serverTimestamp(),
      });
      setCancelInProgressModal(null);
    } catch (err) {
      alert('キャンセルに失敗しました: ' + err.message);
    }
  };

  const computeStats = (block) => {
    if (!block || !block.pairs) return [];
    const n = block.pairs.length;
    const stats = block.pairs.map((_, i) => ({
      teamNo: i + 1,
      setsWon: 0,
      legsWon: 0,
      legsLost: 0,
    }));

    const scores = block.scores || {};
    Object.entries(scores).forEach(([key, value]) => {
      if (typeof value !== 'string') return;
      const [t1, t2] = key.split('-').map(s => parseInt(s, 10));
      const [a, b] = value.split('-').map(s => parseInt(s, 10));
      if (isNaN(t1) || isNaN(t2) || isNaN(a) || isNaN(b)) return;
      const i1 = t1 - 1, i2 = t2 - 1;
      if (i1 < 0 || i2 < 0 || i1 >= n || i2 >= n) return;

      stats[i1].legsWon += a;
      stats[i1].legsLost += b;
      stats[i2].legsWon += b;
      stats[i2].legsLost += a;
      if (a === 2 && b < 2) stats[i1].setsWon += 1;
      if (b === 2 && a < 2) stats[i2].setsWon += 1;
    });

    // 直接対決の勝者判定ヘルパー
    // teamA視点で、teamBに勝ってたら+1、負けてたら-1、不明/未対戦なら0
    const directWinDelta = (teamA, teamB) => {
      const x = Math.min(teamA, teamB), y = Math.max(teamA, teamB);
      const val = scores[`${x}-${y}`];
      if (!val) return 0;
      const [s1, s2] = val.split('-').map(s => parseInt(s, 10));
      if (isNaN(s1) || isNaN(s2)) return 0;
      // s1-s2 は (x, y) 視点(=小さい番号視点)
      const teamAisSmaller = (teamA === x);
      const aScore = teamAisSmaller ? s1 : s2;
      const bScore = teamAisSmaller ? s2 : s1;
      if (aScore === 2 && bScore < 2) return 1;
      if (bScore === 2 && aScore < 2) return -1;
      return 0;
    };

    // 第一ソート: 既存3項目
    const ranked = [...stats].sort((a, b) => {
      if (b.setsWon !== a.setsWon) return b.setsWon - a.setsWon;
      const diffA = a.legsWon - a.legsLost;
      const diffB = b.legsWon - b.legsLost;
      if (diffB !== diffA) return diffB - diffA;
      return a.legsLost - b.legsLost;
    });

    // 第二ソート: 隣接する2チームが既存3項目で完全同点なら、直接対決の勝者を上位に
    // 直接対決で順位が決まった場合は「resolved」フラグを立て、タイ判定から除外
    const resolvedPairs = new Set(); // "teamA-teamB"形式で記録
    for (let i = 0; i < ranked.length - 1; i++) {
      const A = ranked[i], B = ranked[i + 1];
      const sameAll = (A.setsWon === B.setsWon) &&
        ((A.legsWon - A.legsLost) === (B.legsWon - B.legsLost)) &&
        (A.legsLost === B.legsLost);
      if (!sameAll) continue;
      // 前後にもタイがいるなら触らない(3チーム以上同順位)
      const prevTie = i > 0 && (
        ranked[i - 1].setsWon === A.setsWon &&
        (ranked[i - 1].legsWon - ranked[i - 1].legsLost) === (A.legsWon - A.legsLost) &&
        ranked[i - 1].legsLost === A.legsLost
      );
      const nextTie = i + 2 < ranked.length && (
        ranked[i + 2].setsWon === B.setsWon &&
        (ranked[i + 2].legsWon - ranked[i + 2].legsLost) === (B.legsWon - B.legsLost) &&
        ranked[i + 2].legsLost === B.legsLost
      );
      if (prevTie || nextTie) continue;
      // 2チーム同順位: 直接対決の勝者を上に
      const delta = directWinDelta(A.teamNo, B.teamNo);
      if (delta === -1) {
        // BがAに勝ってる → 入れ替え
        ranked[i] = B;
        ranked[i + 1] = A;
      }
      // 直接対決で順位確定したペアを記録(delta!=0なら勝敗確定)
      if (delta !== 0) {
        const winner = delta === 1 ? A.teamNo : B.teamNo;
        const loser = delta === 1 ? B.teamNo : A.teamNo;
        resolvedPairs.add(`${winner}-${loser}`);
      }
    }

    // RANK割り当て: 完全同点はタイ表示(同じ順位、次を飛ばす)
    // ただし直接対決で順位確定したペアはタイ扱いしない
    let lastRank = 0;
    let lastKey = null;
    let lastTeamNo = null;
    ranked.forEach((r, idx) => {
      const key = `${r.setsWon}|${r.legsWon - r.legsLost}|${r.legsLost}`;
      // 直前のチームと統計値が同じでも、直接対決で順位確定してたらタイ扱いしない
      const prevResolved = lastTeamNo !== null && (
        resolvedPairs.has(`${lastTeamNo}-${r.teamNo}`) ||
        resolvedPairs.has(`${r.teamNo}-${lastTeamNo}`)
      );
      if (key === lastKey && !prevResolved) {
        r.rank = lastRank;
        r.isTie = true;
      } else {
        r.rank = idx + 1;
        r.isTie = false;
        lastRank = idx + 1;
        lastKey = key;
      }
      lastTeamNo = r.teamNo;
    });
    // タイの相方にもisTie=trueをマーク
    for (let i = 0; i < ranked.length; i++) {
      if (ranked[i].isTie && i > 0) ranked[i - 1].isTie = true;
    }

    const byTeam = {};
    ranked.forEach(r => { byTeam[r.teamNo] = r; });
    return stats.map(s => byTeam[s.teamNo]);
  };
  // ============================================================
  // 予選通過予測 (Phase 1)
  // ・各ペアに 'ok' / 'warn' / 'out' のいずれかを返す
  // ・タイブレーカー(得失差・直接対決) は加味しない (注釈バーで明示)
  // ・activeMatches は残り試合扱い (まだスコア未確定)
  // ・block.confirmed 時は空オブジェクト (予測表示しない)
  // ・options.enabled で将来 ON/OFF 切替に対応 (Phase 1 は常時 ON)
  // 戻り値: { [teamNo]: 'ok' | 'warn' | 'out' }
  // ============================================================
  const computePredictionForBlock = (block, advanceTo, options = {}) => {
    const { enabled = true } = options;
    if (!enabled) return {};
    if (!block || !block.pairs || !advanceTo) return {};
    if (block.confirmed) return {};

    const n = block.pairs.length;
    if (n < 3) return {};

    // 各ペアの総試合数
    //   3ペア: 各ペア 2試合 (3試合 / 3ペア)
    //   4ペア: 各ペア 3試合 (6試合 / 4ペア)
    //   5ペア: 各ペア 4試合 (10試合 / 5ペア)
    //   6ペア: 各ペア 4試合 (12試合 / 6ペア、対戦不可ペアあり)
    const TOTAL_PER_PAIR = { 3: 2, 4: 3, 5: 4, 6: 4 };
    const totalPerPair = TOTAL_PER_PAIR[n] || 0;
    if (!totalPerPair) return {};

    const stats = computeStats(block);
    const scores = block.scores || {};

    // 各ペアの消化済み試合数を、scores に記録されている対戦からカウント
    //   (activeMatches はスコア未確定なので「残り試合」扱い、ここではカウントしない)
    const playedCount = (teamNo) => {
      let count = 0;
      Object.keys(scores).forEach(key => {
        const val = scores[key];
        if (!val || typeof val !== 'string') return;
        const [t1, t2] = key.split('-').map(s => parseInt(s, 10));
        if (isNaN(t1) || isNaN(t2)) return;
        if (t1 === teamNo || t2 === teamNo) count += 1;
      });
      return count;
    };

    // 各ペアの projection
    //   min = 現在の Sets Won (残り全敗想定)
    //   max = 現在 + 残り試合数 (残り全勝想定)
    const projections = stats.map(s => {
      const played = playedCount(s.teamNo);
      const remaining = Math.max(0, totalPerPair - played);
      return {
        teamNo: s.teamNo,
        min: s.setsWon,
        max: s.setsWon + remaining,
      };
    });

    // 判定
    //   advanceTo - 1 は「他ペアだけを並べた配列」に対する 0-index 参照
    //   (= 自分以外で advanceTo 位に相当する位置)
    const idx = Math.max(0, Math.min(advanceTo - 1, n - 2));
    const result = {};
    projections.forEach(p => {
      const others = projections.filter(o => o.teamNo !== p.teamNo);
      const othersMaxDesc = others.map(o => o.max).sort((a, b) => b - a);
      const othersMinDesc = others.map(o => o.min).sort((a, b) => b - a);
      const cutoffPessimistic = othersMaxDesc[idx] ?? 0;
      const cutoffOptimistic = othersMinDesc[idx] ?? 0;

      if (p.min > cutoffPessimistic) {
        // 自分が全敗しても、他全員が全勝でも、通過ライン超え
        result[p.teamNo] = 'ok';
      } else if (p.max < cutoffOptimistic) {
        // 自分が全勝でも、他全員が全敗でも、通過ラインに届かない
        // (境界の同点ケース max === cutoff は 'warn' に倒す、安全側)
        result[p.teamNo] = 'out';
      } else {
        result[p.teamNo] = 'warn';
      }
    });
    return result;
  };

  const matchKey = (a, b) => {
    const x = Math.min(a, b), y = Math.max(a, b);
    return `${x}-${y}`;
  };

  const getMatchDisplay = (teamA, teamB, scores) => {
    const key = matchKey(teamA, teamB);
    const val = scores?.[key];
    // 空・null・undefinedはhasScore:false
    if (!val || typeof val !== 'string') return { hasScore: false, a: null, b: null, aWon: false, bWon: false };
    const parts = val.split('-');
    if (parts.length !== 2) return { hasScore: false, a: null, b: null, aWon: false, bWon: false };
    const [s1, s2] = parts.map(s => parseInt(s, 10));
    // 不正な数値はhasScore:false
    if (isNaN(s1) || isNaN(s2)) return { hasScore: false, a: null, b: null, aWon: false, bWon: false };
    const small = Math.min(teamA, teamB);
    let a, b;
    if (small === teamA) { a = s1; b = s2; }
    else { a = s2; b = s1; }
    const aWon = (a === 2 && b < 2);
    const bWon = (b === 2 && a < 2);
    return { hasScore: true, a, b, aWon, bWon };
  };

  const goToBlock = (idx) => {
    if (idx < 0 || idx >= blocks.length) return;
    programmaticScrollRef.current = true;   // smooth 中は onScroll 同期をスキップ
    setCurrentBlockIdx(idx);
    setScoreInput(null);
    const wrap = tableWrapRef.current;
    if (wrap) {
      wrap.scrollTo({ left: idx * wrap.clientWidth, behavior: 'smooth' });
    }
    if (programmaticScrollTimerRef.current) clearTimeout(programmaticScrollTimerRef.current);
    programmaticScrollTimerRef.current = setTimeout(() => {
      programmaticScrollRef.current = false; // scrollend 非対応環境の保険
    }, 450);
  };

  // ▼ 主同期: onScroll + requestAnimationFrame / 補正: scrollend（最終確定のみ）
  useEffect(() => {
    const wrap = tableWrapRef.current;
    if (!wrap) return;

    const computeIdx = () => {
      const w = wrap.clientWidth || 1;
      const idx = Math.round(wrap.scrollLeft / w);
      return Math.max(0, Math.min(blocks.length - 1, idx));
    };

    const onScroll = () => {
      if (programmaticScrollRef.current) return; // tab/nav の smooth 中はスキップ
      if (scrollSyncRafRef.current) return;
      scrollSyncRafRef.current = requestAnimationFrame(() => {
        scrollSyncRafRef.current = null;
        const clamped = computeIdx();
        setCurrentBlockIdx((prev) => (prev !== clamped ? clamped : prev));
      });
    };

    const onScrollEnd = () => {                  // 補助: 最終補正＋フラグ解除
      const clamped = computeIdx();
      setCurrentBlockIdx((prev) => (prev !== clamped ? clamped : prev));
      programmaticScrollRef.current = false;
    };

    wrap.addEventListener('scroll', onScroll, { passive: true });
    wrap.addEventListener('scrollend', onScrollEnd);
    return () => {
      wrap.removeEventListener('scroll', onScroll);
      wrap.removeEventListener('scrollend', onScrollEnd);
      if (scrollSyncRafRef.current) cancelAnimationFrame(scrollSyncRafRef.current);
    };
  }, [blocks.length]);

  const matchOrder = currentBlock ? (ROBIN_ORDER[currentBlock.pairs.length] || []) : [];

  const matchStatus = useMemo(() => {
    if (!currentBlock) return [];
    const scores = currentBlock.scores || {};
    return matchOrder.map(([a, b]) => {
      const key = `${Math.min(a, b)}-${Math.max(a, b)}`;
      return { a, b, key, done: !!scores[key] };
    });
  }, [currentBlock, matchOrder]);

  const nextMatchIdx = useMemo(() => matchStatus.findIndex(m => !m.done), [matchStatus]);
  // Step7: nextMatchKey は STEP6 で nextMap に置き換わって未使用、削除

  const allMatchesDone = matchOrder.length > 0 && matchStatus.every(m => m.done);

  // Step5+6: blockPriority は STEP5 で UI から削除済 (優先バッジ廃止)、
  //   さらに STEP6 で nextSchedule が完全に代替したため完全削除
  //   (進行率計算は nextSchedule 内で行うようになった)

  const confirmBlock = async () => {
    if (!currentBlock) return;
    if (!allMatchesDone) {
      alert('まだ未入力の試合があります');
      return;
    }
    if (currentBlock.confirmed) {
      if (!window.confirm(`ブロック${currentBlock.id}の予選確定を解除しますか?`)) return;
      const newBlocks = blocks.map((b, i) => i === currentBlockIdx ? { ...b, confirmed: false } : b);
      const updates = { blocks: newBlocks, updatedAt: serverTimestamp() };
      // 12a-route1: if tournament already built, mark placementDirty (prelim result changed)
      if (event.tournament?.matches?.length > 0) {
        updates.tournament = {
          ...event.tournament,
          placementDirty: true,
          placementDirtyReason: 'block_rescored',
          placementDirtyAt: serverTimestamp(),
          placementOverride: false,
        };
      }
      await updateDoc(doc(db, 'events', id), updates);
    } else {
      if (!window.confirm(`ブロック${currentBlock.id}の予選を確定しますか?\n通過ペアが決定し、決勝トーナメント枠に反映されます。`)) return;
      const newBlocks = blocks.map((b, i) => i === currentBlockIdx ? { ...b, confirmed: true } : b);
      await updateDoc(doc(db, 'events', id), { blocks: newBlocks, updatedAt: serverTimestamp() });
    }
  };

  // ペア入れ替え(ブロック横断対応)
  const executePairSwap = async (target) => {
    if (!editSelected || !target) return;
    const a = editSelected;
    const b = target;
    if (a.blockIdx === b.blockIdx && a.pairIdx === b.pairIdx) {
      setEditSelected(null);
      return;
    }

    // 影響範囲チェック: 入れ替えにより両方のブロックでスコアリセットが必要
    const blockA = blocks[a.blockIdx];
    const blockB = blocks[b.blockIdx];
    const scoresA = Object.keys(blockA.scores || {}).length;
    const scoresB = Object.keys(blockB.scores || {}).length;
    const totalScoresAffected = scoresA + scoresB;

    let confirmMsg = `ブロック${blockA.id}-${a.pairIdx + 1}番のペアと、ブロック${blockB.id}-${b.pairIdx + 1}番のペアを入れ替えますか?`;
    if (totalScoresAffected > 0) {
      confirmMsg += `\n\n警告: 影響するブロックのスコア(計${totalScoresAffected}試合)が全て削除されます。`;
      if (blockA.confirmed || blockB.confirmed) {
        confirmMsg += `\n確定済みブロックの予選確定も解除されます。`;
      }
    }
    if (!window.confirm(confirmMsg)) return;

    const newBlocks = blocks.map(b2 => ({ ...b2 }));
    // ペア入れ替え
    const newPairsA = [...newBlocks[a.blockIdx].pairs];
    const newPairsB = a.blockIdx === b.blockIdx ? newPairsA : [...newBlocks[b.blockIdx].pairs];
    const tmp = newPairsA[a.pairIdx];
    newPairsA[a.pairIdx] = newPairsB[b.pairIdx];
    newPairsB[b.pairIdx] = tmp;
    newBlocks[a.blockIdx].pairs = newPairsA;
    if (a.blockIdx !== b.blockIdx) newBlocks[b.blockIdx].pairs = newPairsB;
    // 該当ブロックのスコアと確定をリセット
    newBlocks[a.blockIdx].scores = {};
    newBlocks[a.blockIdx].confirmed = false;
    newBlocks[a.blockIdx].activeMatches = {};      // ★⑫b: 進行中試合の残留を解消
    newBlocks[a.blockIdx].matchDurations = {};     // ★⑫b: 所要時間記録の残留を解消
    if (a.blockIdx !== b.blockIdx) {
      newBlocks[b.blockIdx].scores = {};
      newBlocks[b.blockIdx].confirmed = false;
      newBlocks[b.blockIdx].activeMatches = {};    // ★⑫b
      newBlocks[b.blockIdx].matchDurations = {};   // ★⑫b
    }

    try {
      // ★⑫b: 決勝生成済みなら配置をdirty化（block_rescored）。
      //   executePairSwapはconfirmBlockを通らずupdateDoc直叩きのため、
      //   Phase2-4a(c429685)のblock_rescored発火がここでは効かない＝経路1すり抜け穴を手当て。
      const updatePayload = {
        blocks: newBlocks,
        updatedAt: serverTimestamp(),
      };
      if (event?.tournament?.matches?.length > 0) {
        updatePayload.tournament = {
          ...event.tournament,
          placementDirty: true,
          placementDirtyReason: 'block_rescored',
          placementDirtyAt: serverTimestamp(),
          placementOverride: false,
        };
      }
      await updateDoc(doc(db, 'events', id), updatePayload);
      setEditSelected(null);
      // モーダルは開いたまま(連続編集できる)
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    }
  };

  // ペア選択(編集モーダル内)
  const handleEditPairClick = (blockIdx, pairIdx) => {
    if (!editSelected) {
      setEditSelected({ blockIdx, pairIdx });
    } else {
      // 2つ目選択 → 入れ替え実行
      executePairSwap({ blockIdx, pairIdx });
    }
  };

  // 1b: 選手編集モーダルを開く（現在値をローカルstateに読み込む）
  const openPlayerEdit = (blockIdx, pairIdx, playerKey) => {
    const p = blocks[blockIdx]?.pairs?.[pairIdx]?.[playerKey] || {};
    setEditPlayerName(p.name || '');
    setEditPlayerShop(p.shop || '');
    setEditPlayerModal({ blockIdx, pairIdx, playerKey });
  };

  // 1b: 純リラベル保存。scores/confirmed/activeMatches/matchDurations/dirty化は一切触らない。
  const updatePairPlayer = async () => {
    if (!editPlayerModal) return;
    const name = editPlayerName.trim();
    if (!name) { alert('名前を入力してください'); return; }
    const { blockIdx, pairIdx, playerKey } = editPlayerModal;
    const newBlocks = blocks.map((b, bi) => {
      if (bi !== blockIdx) return b;
      const newPairs = b.pairs.map((p, pi) => {
        if (pi !== pairIdx) return p;
        return { ...p, [playerKey]: { ...p[playerKey], name, shop: editPlayerShop.trim() } };
      });
      return { ...b, pairs: newPairs };
    });
    try {
      await updateDoc(doc(db, 'events', id), { blocks: newBlocks, updatedAt: serverTimestamp() });
      setEditPlayerModal(null);
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    }
  };

  // ▼ scroll-snap 横スワイプ用（旧 touchStartX/Y + handleTouchStart/End を撤去して置換）
  const tableWrapRef = useRef(null);
  const programmaticScrollRef = useRef(false);     // tab/nav の smooth 移動中フラグ
  const programmaticScrollTimerRef = useRef(null); // scrollend 来ない環境のフォールバック
  const scrollSyncRafRef = useRef(null);           // onScroll の rAF スロットル

  if (!event) return <div className="page"><div className="loading">読み込み中...</div></div>;

  if (!blocks || blocks.length === 0) {
    return (
      <div className="page no-select">
        <header className="header">
          <h1>
            <button className="btn-back" onClick={() => navigate(`/event/${id}/progress-setting`)}>
              <ArrowLeft size={14} /> 戻る
            </button>
            <span style={{ marginLeft: 8 }}>ロビン表</span>
          </h1>
        </header>
        <div className="container">
          <div className="empty">ブロックが作成されていません。先に進行設定を行ってください。</div>
          <button className="btn-accent btn-large" onClick={() => navigate(`/event/${id}/progress-setting`)}>
            進行設定へ
          </button>
        </div>
      </div>
    );
  }

  const stats = currentBlock ? computeStats(currentBlock) : [];
  const advanceTo = (progressSettings.advancePerSize && progressSettings.advancePerSize[currentBlock?.pairs?.length]) || 1;
  const legFormatsArr = progressSettings.legFormats || ['501', '501', '501'];
  // Phase 1: 予選通過予測 (常時ON、ブロック確定時は空 {})
  const predictionMap = currentBlock ? computePredictionForBlock(currentBlock, advanceTo) : {};

  return (
    <div className="robin-page no-select">
      {/* ヘッダー: ロビン表-ブロックX / 編集 / 予選確定 / ハンバーガー */}
      <header className="robin-header">
        <h1 className="robin-title">ロビン表 - ブロック {currentBlock?.id}</h1>
        <div className="robin-header-actions">
          <button className="btn-edit-robin" onClick={() => setEditModalOpen(true)}>
            <Edit2 size={14} /> 編集
          </button>
          {!currentBlock?.confirmed && allMatchesDone && (
            <button className="btn-confirm" onClick={confirmBlock}>
              <CheckCircle size={14} /> 予選確定
            </button>
          )}
          {currentBlock?.confirmed && (
            <button className="btn-confirm confirmed" onClick={confirmBlock}>
              <Lock size={14} /> 確定解除
            </button>
          )}
          <button className="robin-hamburger" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
            <Menu size={20} />
          </button>
        </div>
      </header>

      <TestModeBanner event={event} navigate={navigate} />

      {/* ロビン用ハンバーガーメニュー(独自項目) */}
      <div className={`drawer-overlay ${drawerOpen ? 'open' : ''}`} onClick={() => setDrawerOpen(false)} />
      <div className={`drawer ${drawerOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h2>メニュー</h2>
          <button className="drawer-close" onClick={() => setDrawerOpen(false)} aria-label="閉じる">
            <X size={20} />
          </button>
        </div>
        <div className="drawer-user">
          <div className="drawer-user-shop" translate="no">{shopName || '-'}</div>
          {user?.email && <div className="drawer-user-email" translate="no">{user.email}</div>}
        </div>
        <div className="drawer-items">
          <button className="drawer-item" onClick={() => { setDrawerOpen(false); navigate('/'); }}>
            <Home size={18} /> イベント一覧に戻る
          </button>
          <button className="drawer-item" onClick={() => { setDrawerOpen(false); navigate(`/event/${id}`); }}>
            <List size={18} /> エントリー表に戻る
          </button>
          <button className="drawer-item" onClick={() => { setDrawerOpen(false); navigate(`/event/${id}/pairing`); }}>
            <List size={18} /> ペア一覧に戻る
          </button>
          <button className="drawer-item" onClick={() => { setDrawerOpen(false); navigate(`/event/${id}/progress-setting`); }}>
            <Settings size={18} /> 進行設定に戻る
          </button>
          <button className="drawer-item drawer-item-accent" onClick={() => { setDrawerOpen(false); navigate(`/event/${id}/tournament`); }}>
            <Trophy size={18} /> 決勝トーナメントへ
            {(() => {
              const confirmedCount = blocks.filter(b => b.confirmed).length;
              if (confirmedCount === 0) return null;
              return <span className="drawer-confirmed-badge">確定 {confirmedCount}/{blocks.length}</span>;
            })()}
          </button>
          <button className="drawer-item" onClick={() => { setDrawerOpen(false); navigate('/help'); }}>
            <HelpCircle size={18} /> 使い方
          </button>
          <button className="drawer-item danger" onClick={() => { setDrawerOpen(false); handleLogout(); }}>
            <LogOut size={18} /> ログアウト
          </button>
        </div>
      </div>

      {/* ブロック切替バー */}
      <div className="block-switcher">
        <button
          className="block-nav-btn"
          onClick={() => goToBlock(currentBlockIdx - 1)}
          disabled={currentBlockIdx === 0}
          aria-label="前のブロック"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="block-tabs">
          {blocks.map((b, i) => {
            // このブロックが全試合終了してるか判定
            const MATCHES_PER_BLOCK = { 3: 3, 4: 6, 5: 10, 6: 12 };
            const totalInBlock = MATCHES_PER_BLOCK[b.pairs.length] || 0;
            const doneInBlock = Object.keys(b.scores || {}).length;
            const isFinished = totalInBlock > 0 && doneInBlock >= totalInBlock;
            const isFinishedUnconfirmed = isFinished && !b.confirmed;

            // Step2+5: 進行中試合からマシン番号配列を構築
            //   2台以下: ①②          (全部並列表示)
            //   3台以上: ①②+         (3台目以降を + で省略)
            const activeMachineNos = (b.activeMatches ? Object.values(b.activeMatches) : [])
              .map(e => e?.machineNo)
              .filter(n => typeof n === 'number')
              .sort((x, y) => x - y);
            let activeBadgeText = null;
            if (activeMachineNos.length > 0) {
              const circles = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧'];
              if (activeMachineNos.length <= 2) {
                activeBadgeText = activeMachineNos.map(n => circles[n - 1] || `(${n})`).join('');
              } else {
                activeBadgeText = activeMachineNos.slice(0, 2).map(n => circles[n - 1] || `(${n})`).join('') + '+';
              }
            }
            // STEP9: NEXT バッジ判定 (進行中バッジと同仕様、数字並列)
            const nextOrders = nextOrdersByBlock[b.id] || [];
            let nextBadgeText = null;
            if (nextOrders.length > 0) {
              const nextCircles = ['①', '②', '③'];
              if (nextOrders.length <= 2) {
                nextBadgeText = nextOrders.map(n => nextCircles[n - 1] || `(${n})`).join('');
              } else {
                nextBadgeText = nextOrders.slice(0, 2).map(n => nextCircles[n - 1] || `(${n})`).join('') + '+';
              }
            }
            return (
              <button
                key={b.id}
                className={`block-tab ${i === currentBlockIdx ? 'active' : ''} ${b.confirmed ? 'confirmed' : ''} ${isFinishedUnconfirmed ? 'finished' : ''} ${activeBadgeText ? 'has-active-match' : ''} ${nextBadgeText ? 'has-next' : ''}`}
                onClick={() => goToBlock(i)}
              >
                {/* Step2+5: 進行中バッジ (マシン番号並列表示、3台以上は + 省略) */}
                {activeBadgeText && (
                  <span className="tab-active-match-badge">
                    <span className="tab-active-match-machine">{activeBadgeText}</span>
                  </span>
                )}
                {/* STEP9: NEXT バッジ (黄色、進行中バッジと共存) */}
                {nextBadgeText && (
                  <span className="tab-next-badge">{nextBadgeText}</span>
                )}
                {b.id}
                {b.confirmed && <CheckCircle size={11} />}
                {isFinishedUnconfirmed && <span className="tab-finished-badge">終了</span>}
              </button>
            );
          })}
        </div>
        <button
          className="block-nav-btn"
          onClick={() => goToBlock(currentBlockIdx + 1)}
          disabled={currentBlockIdx >= blocks.length - 1}
          aria-label="次のブロック"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* フォーマット表示 */}
      <div className="robin-format-bar">
        <span className="fmt-label">フォーマット:</span>
        {legFormatsArr.map((f, i) => {
          const label = LEG_FORMATS.find(x => x.value === f)?.label || f;
          return (
            <span key={i} className="format-leg">
              <span className="leg-num">Leg{i + 1}:</span> <strong>{label}</strong>
            </span>
          );
        })}
        <span className="advance-info">上位 <strong>{advanceTo}</strong> 位通過</span>
      </div>

      {/* メイン領域 */}
      <div className="robin-main">
        {/* ロビン表（横スクロール・scroll-snap・1ページ=1ブロック） */}
        <div className="robin-table-wrap" ref={tableWrapRef}>
          <div className="robin-blocks-track">
            {blocks.map((block, blockIdx) => {
              // ★ページごとに block スコープで個別算出（currentBlock 依存を断つ）
              const pageStats = computeStats(block);
              const pageAdvanceTo =
                (progressSettings.advancePerSize &&
                  progressSettings.advancePerSize[block.pairs?.length]) || 1;
              const pagePredictionMap = computePredictionForBlock(block, pageAdvanceTo);
              const isCurrentPage = blockIdx === currentBlockIdx;

              return (
                <div
                  key={block.id}
                  className={`robin-block-page ${isCurrentPage ? 'is-current' : ''}`}
                >
                  <table className="robin-table">
                    <thead>
                      <tr>
                        <th className="rb-col-no">番号</th>
                        <th className="rb-col-name">NAME</th>
                        {block.pairs.map((_, i) => (
                          <th key={i} className="rb-col-match">{i + 1}</th>
                        ))}
                        <th className="rb-col-sets">Sets<br />won</th>
                        <th className="rb-col-legs">①Legs<br />won</th>
                        <th className="rb-col-legs">②Legs<br />Lost</th>
                        <th className="rb-col-diff">①-②</th>
                        <th className="rb-col-rank">RANK</th>
                      </tr>
                    </thead>
                    <tbody>
                      {block.pairs.map((pair, rowIdx) => {
                        const teamNo = rowIdx + 1;
                        const p1 = pair.player1;
                        const p2 = pair.player2;
                        const rt1 = Number(p1?.rating) || 0;
                        const rt2 = Number(p2?.rating) || 0;
                        const upper = rt1 >= rt2 ? p1 : p2;
                        const lower = rt1 >= rt2 ? p2 : p1;
                        const stat = pageStats[rowIdx] || { setsWon: 0, legsWon: 0, legsLost: 0, rank: '-' };
                        const diff = stat.legsWon - stat.legsLost;
                        const isAdvancing = block.confirmed && stat.rank <= pageAdvanceTo;
                        const isEliminated = block.confirmed && stat.rank > pageAdvanceTo;

                        return (
                          <tr key={rowIdx} className={`${isAdvancing ? 'rb-advancing' : ''} ${isEliminated ? 'rb-eliminated' : ''}`}>
                            <td className="rb-col-no">{teamNo}</td>
                            <td className="rb-col-name">
                              <div className="rb-name-cell">
                                <div className="rb-name-upper" translate="no">
                                  <span className="rb-name-main">{upper?.name || '-'}</span>
                                  {upper?.shop ? <span className="rb-name-shop" translate="no">{upper.shop}</span> : null}
                                </div>
                                <div className="rb-name-lower" translate="no">
                                  <span className="rb-name-main">{lower?.name || '-'}</span>
                                  {lower?.shop ? <span className="rb-name-shop" translate="no">{lower.shop}</span> : null}
                                </div>
                              </div>
                            </td>
                            {block.pairs.map((_, colIdx) => {
                              const opNo = colIdx + 1;
                              if (rowIdx === colIdx) {
                                return <td key={colIdx} className="rb-cell-diagonal">―</td>;
                              }
                              if (block.pairs.length === 6) {
                                const blockedPairs = [[1, 6], [2, 5], [3, 4]];
                                const isBlocked = blockedPairs.some(([a, b]) =>
                                  (teamNo === a && opNo === b) || (teamNo === b && opNo === a)
                                );
                                if (isBlocked) {
                                  return <td key={colIdx} className="rb-cell-blocked">―</td>;
                                }
                              }
                              const md = getMatchDisplay(teamNo, opNo, block.scores);
                              const key = matchKey(teamNo, opNo);
                              // Step3: このセル試合が進行中か判定
                              const cellActiveEntry = block.activeMatches?.[key];
                              const cellInProgress = !!cellActiveEntry;
                              const cellMachineLabel = cellActiveEntry
                                ? ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧'][cellActiveEntry.machineNo - 1] || ''
                                : null;
                              // Step6: NEXT 判定を nextMap (横断スケジューラ v2) ベースに切替
                              //   旧: nextMatchKey === key (現ブロック内の最初の未完了試合だけ)
                              //   新: nextMap[blockId:matchKey] = 順番番号 (1〜machineCount)
                              const nextOrder = !md.hasScore && !cellInProgress && !block.confirmed
                                ? nextMap[`${block.id}:${key}`]
                                : undefined;
                              const isNext = typeof nextOrder === 'number';
                              const nextCircleLabel = isNext ? (['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧'][nextOrder - 1] || `${nextOrder}`) : '';
                              return (
                                <td
                                  key={colIdx}
                                  className={`rb-cell-match ${md.hasScore ? 'has-score' : ''} ${md.aWon ? 'has-won' : md.bWon ? 'has-lost' : md.hasScore ? 'has-draw' : ''} ${isNext ? 'is-next' : ''} ${cellInProgress ? 'is-in-progress' : ''} ${block.confirmed ? 'locked' : 'editable'}`}
                                  onClick={() => !block.confirmed && openScoreInput(teamNo, opNo)}
                                >
                                  {md.hasScore ? (
                                    <>
                                      <div className={`rb-cell-ox ${md.aWon ? 'won' : md.bWon ? 'lost' : 'draw'}`}>
                                        {md.aWon ? '○' : md.bWon ? '×' : '-'}
                                      </div>
                                      <div className="rb-cell-score">{md.a}-{md.b}</div>
                                    </>
                                  ) : (
                                    <>
                                      {/* Step6: 番号付き NEXT バッジ (NEXT ①, NEXT ②, ...) */}
                                      {isNext && (
                                        <div className="next-badge">
                                          <span className="next-badge-label">NEXT</span>
                                          <span className="next-badge-num">{nextCircleLabel}</span>
                                        </div>
                                      )}
                                      {/* Step3: 進行中バッジ (オレンジ、マシン番号付き) */}
                                      {cellInProgress && (
                                        <div className="cell-in-progress-badge">
                                          進行中 {cellMachineLabel}
                                        </div>
                                      )}
                                      <div className="rb-cell-empty">　</div>
                                    </>
                                  )}
                                </td>
                              );
                            })}
                            <td className="rb-col-sets">{stat.setsWon}</td>
                            <td className="rb-col-legs">{stat.legsWon}</td>
                            <td className="rb-col-legs">{stat.legsLost}</td>
                            <td className="rb-col-diff">{diff > 0 ? '+' : ''}{diff}</td>
                            <td className={`rb-col-rank ${stat.isTie ? 'rb-tie' : ''}`}>
                              {stat.rank || '-'}
                              {stat.isTie && <span className="tie-mark">T</span>}
                              {/* Phase 1: 通過予測バッジ (ブロック確定時は pagePredictionMap が空なので非表示) */}
                              {pagePredictionMap[stat.teamNo] === 'ok' && (
                                <span className="rb-prediction-badge ok">◎ 濃厚</span>
                              )}
                              {pagePredictionMap[stat.teamNo] === 'warn' && (
                                <span className="rb-prediction-badge warn">△ 可能性</span>
                              )}
                              {pagePredictionMap[stat.teamNo] === 'out' && (
                                <span className="rb-prediction-badge out">✕ 困難</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>

        {/* 進行順番パネル */}
        <div className="robin-order-panel">
          <h3>進行順番</h3>
          <div className="order-list">
            {matchOrder.map(([a, b], i) => {
              const status = matchStatus[i];
              const done = status?.done;
              // STEP9 v2: nextSchedule からこの試合の NEXT 順位を取得 (1〜3)
              //   既存の nextMatchIdx (1個) ではなく nextSchedule (3個) ベースに変更
              //   タブ・セル・右パネルの NEXT 表示を完全同期させるため
              const nextScheduleEntryForCard = nextSchedule.find(
                ns => ns.blockId === currentBlock.id && ns.matchKey === matchKey(a, b)
              );
              const nextOrderForCard = nextScheduleEntryForCard ? nextScheduleEntryForCard.order : 0;
              const isNext = !done && nextOrderForCard > 0;
              const nextCircleLabel = isNext ? (['①', '②', '③'][nextOrderForCard - 1] || `${nextOrderForCard}`) : '';
              const pa = currentBlock.pairs[a - 1];
              const pb = currentBlock.pairs[b - 1];
              const labelA = pa ? `${pa.player1?.name || '?'}×${pa.player2?.name || '?'}` : '?';
              const labelB = pb ? `${pb.player1?.name || '?'}×${pb.player2?.name || '?'}` : '?';
              const circleNum = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩', '⑪', '⑫'][i] || `${i + 1}`;

              // この試合カードが進行中か
              const orderKey = matchKey(a, b);
              const activeEntry = currentBlock.activeMatches?.[orderKey];
              const isInProgress = !!activeEntry;
              const inProgressMachineLabel = activeEntry
                ? ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧'][activeEntry.machineNo - 1] || ''
                : null;

              // Step3: 進行中ペアを含むか (同ペアの並行進行禁止)
              const pairInUse = isMatchPairInUse(a, b);

              // Step2+3: カードクリック動作
              //   - 完了済み: 何もしない
              //   - 進行中: クリックでキャンセル確認モーダル
              //   - 進行中ペアを含む未開始試合: 警告 (開始不可)
              //   - 未開始 & ブロック未確定: 試合開始モーダル
              const handleCardClick = () => {
                if (done) return;
                if (currentBlock.confirmed) return;
                if (isInProgress) {
                  setCancelInProgressModal({ matchKey: orderKey, blockIdx: currentBlockIdx, rowTeam: a, colTeam: b });
                  return;
                }
                if (pairInUse) {
                  alert('このペアは別の試合で進行中です。\n進行中の試合が終わってから開始してください。');
                  return;
                }
                setMatchStartModal({ matchKey: orderKey, rowTeam: a, colTeam: b });
              };

              return (
                <div
                  key={i}
                  className={`order-item ${done ? 'done' : ''} ${isNext ? 'next' : ''} ${isInProgress ? 'in-progress' : ''} ${pairInUse && !isInProgress && !done ? 'pair-in-use' : ''} ${!done && !currentBlock.confirmed ? 'clickable' : ''}`}
                  onClick={handleCardClick}
                >
                  {/* 進行中バッジ (進行順番カードの上に表示) */}
                  {isInProgress && (
                    <div className="order-in-progress-badge">
                      <span className="order-in-progress-label">進行中</span>
                      {' '}
                      <span className="order-in-progress-machine">{inProgressMachineLabel}</span>
                    </div>
                  )}
                  {/* STEP9 v2: NEXT バッジ (進行中バッジと同形、黄色) */}
                  {isNext && (
                    <div className="order-next-badge">
                      <span className="order-next-label">NEXT</span>
                      {' '}
                      <span className="order-next-num">{nextCircleLabel}</span>
                    </div>
                  )}
                  <div className="order-num-line">
                    {done && <Check size={13} className="order-check" />}
                    <span className="order-num">{circleNum}</span>
                    <span className="order-vs">{a}×{b}</span>
                  </div>
                  <div className="order-pair-names" translate="no">
                    <div>{labelA}</div>
                    <div>{labelB}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 注意書きバー(最下段固定) */}
      <div className="robin-notice">
        <div className="notice-line">※決勝トーナメント進出は、予選ラウンドロビン上位 <strong>{advanceTo}</strong> 位までとなります。</div>
        <div className="notice-line">※ラウンドロビン勝敗の優先順位は ① Sets Wonの多いチーム、② ①と②の差が多いチーム、③ Legs Lostの少ないチーム、④ 直接対決の勝者となります。</div>
        <div className="notice-line">※ 通過予測 (◎ △ ✕) は現時点の暫定値。タイブレーカー(得失差・直接対決)は加味しません。</div>
      </div>

      {/* スコア入力モーダル(4種のみ) */}
      {scoreInput && (
        <div className="score-input-overlay" onClick={() => setScoreInput(null)}>
          <div className="score-input-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              スコア入力 - チーム {scoreInput.rowTeam ?? scoreInput.matchKey.split('-')[0]} 視点
            </h3>
            <div style={{ fontSize: '0.9em', color: 'var(--color-text-secondary, #888)', marginBottom: '8px' }}>
              (対戦相手: チーム {scoreInput.colTeam ?? scoreInput.matchKey.split('-')[1]})
            </div>
            <div className="score-grid-2x2">
              <button className="score-cell" onClick={() => writeScore(2, 0)}>2-0 勝ち</button>
              <button className="score-cell" onClick={() => writeScore(2, 1)}>2-1 勝ち</button>
              <button className="score-cell" onClick={() => writeScore(1, 2)}>1-2 負け</button>
              <button className="score-cell" onClick={() => writeScore(0, 2)}>0-2 負け</button>
            </div>
            <div className="score-modal-actions">
              <button className="btn-danger-small" onClick={() => writeScore(null, null)}>
                スコア削除
              </button>
              <button className="btn-secondary-local" onClick={() => setScoreInput(null)}>
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step2+4+5: 試合開始モーダル (マシン番号選択 + 確認ステップ + 視認性改善) */}
      {matchStartModal && currentBlock && (() => {
        const selectedMachine = matchStartModal.selectedMachineNo;
        const pairA = currentBlock.pairs[matchStartModal.rowTeam - 1];
        const pairB = currentBlock.pairs[matchStartModal.colTeam - 1];
        const pairAName = pairA
          ? `${pairA.player1?.name || '?'}/${pairA.player2?.name || '?'}`
          : '?';
        const pairBName = pairB
          ? `${pairB.player1?.name || '?'}/${pairB.player2?.name || '?'}`
          : '?';
        const selectedCircle = selectedMachine
          ? (['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧'][selectedMachine - 1] || `(${selectedMachine})`)
          : null;
        return (
          <div className="score-input-overlay" onClick={() => setMatchStartModal(null)}>
            <div className="score-input-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460, color: '#222' }}>
              <h3 style={{ color: '#222' }}>試合を開始しますか?</h3>
              <div style={{
                padding: '10px 12px',
                marginBottom: '12px',
                background: '#fff8e1',
                border: '1px solid #ffe600',
                borderRadius: '6px',
                fontSize: '0.95em',
                lineHeight: 1.5,
                color: '#222',
              }}>
                <strong style={{ color: '#222' }}>ブロック{currentBlock.id} - 試合 {matchStartModal.rowTeam} × {matchStartModal.colTeam}</strong>
                <div style={{ fontSize: '0.95em', color: '#222', marginTop: '4px' }}>
                  {pairAName}<br />
                  <span style={{ color: '#666' }}>vs</span><br />
                  {pairBName}
                </div>
              </div>
              <div style={{ marginBottom: '10px', fontWeight: 700, color: '#222' }}>使用するマシンを選択:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                {Array.from({ length: machineCount }, (_, idx) => idx + 1).map(n => {
                  const circle = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧'][n - 1] || `(${n})`;
                  const isUsed = usedMachineNos.has(n);
                  const isSelected = selectedMachine === n;
                  return (
                    <button
                      key={n}
                      onClick={() => !isUsed && setMatchStartModal({ ...matchStartModal, selectedMachineNo: n })}
                      disabled={isUsed}
                      style={{
                        flex: '1 1 80px',
                        padding: '14px 8px',
                        fontSize: '1.4em',
                        fontWeight: 700,
                        borderRadius: '8px',
                        border: isSelected ? '3px solid #c84a00' : (isUsed ? '2px solid #ccc' : '2px solid #ff6b1a'),
                        background: isSelected ? '#ff6b1a' : (isUsed ? '#eee' : '#fff5ee'),
                        color: isSelected ? 'white' : (isUsed ? '#999' : '#c84a00'),
                        cursor: isUsed ? 'not-allowed' : 'pointer',
                        transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                        transition: 'all 0.1s',
                      }}
                    >
                      {circle}
                      {isUsed && <div style={{ fontSize: '0.4em', marginTop: '2px', color: '#999' }}>使用中</div>}
                    </button>
                  );
                })}
              </div>

              {/* Step4: 確認エリア (マシン選択後のみ表示) */}
              {selectedMachine && (
                <div style={{
                  padding: '12px 14px',
                  marginBottom: '14px',
                  background: '#fff5ee',
                  border: '2px solid #ff6b1a',
                  borderRadius: '6px',
                  fontSize: '0.95em',
                  lineHeight: 1.5,
                  color: '#222',
                }}>
                  <div style={{ fontWeight: 700, marginBottom: '6px', color: '#c84a00' }}>確認:</div>
                  <div style={{ color: '#222' }}>
                    <strong style={{ color: '#222' }}>{pairAName}</strong>
                    <span style={{ color: '#666', margin: '0 6px' }}> vs </span>
                    <strong style={{ color: '#222' }}>{pairBName}</strong>
                  </div>
                  <div style={{ marginTop: '6px', color: '#222' }}>
                    を <strong style={{ fontSize: '1.3em', color: '#c84a00' }}>{selectedCircle}</strong>番台で開始しますか?
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="btn-secondary-local"
                  onClick={() => setMatchStartModal(null)}
                >
                  キャンセル
                </button>
                <button
                  onClick={() => selectedMachine && startMatch(selectedMachine)}
                  disabled={!selectedMachine}
                  style={{
                    padding: '10px 20px',
                    fontSize: '1em',
                    fontWeight: 700,
                    borderRadius: '6px',
                    border: 'none',
                    background: selectedMachine ? '#ff6b1a' : '#ccc',
                    color: 'white',
                    cursor: selectedMachine ? 'pointer' : 'not-allowed',
                  }}
                >
                  はい、開始する
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Step2: 進行中キャンセル確認モーダル */}
      {cancelInProgressModal && currentBlock && (
        <div className="score-input-overlay" onClick={() => setCancelInProgressModal(null)}>
          <div className="score-input-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, color: '#222' }}>
            <h3 style={{ color: '#222' }}>進行中の試合をキャンセル</h3>
            <div style={{
              padding: '10px 12px',
              marginBottom: '12px',
              background: '#fff5ee',
              border: '1px solid #ff6b1a',
              borderRadius: '6px',
              fontSize: '0.95em',
              lineHeight: 1.5,
              color: '#222',
            }}>
              <strong style={{ color: '#222' }}>試合 {cancelInProgressModal.rowTeam} × {cancelInProgressModal.colTeam}</strong>
              <div style={{ fontSize: '0.9em', color: '#444', marginTop: '6px' }}>
                スタートを取り消します。試合はまだ開始されていない扱いになります。<br />
                (入力済みスコアがあれば、それは保持されます)
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary-local"
                onClick={() => setCancelInProgressModal(null)}
              >
                閉じる
              </button>
              <button
                className="btn-danger-small"
                onClick={cancelInProgress}
                style={{ background: '#c84a00', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px' }}
              >
                スタートを取り消す
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ペア入れ替え編集モーダル */}
      {editModalOpen && (
        <div className="edit-modal-overlay" onClick={() => { setEditModalOpen(false); setEditSelected(null); }}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h2><Edit2 size={16} /> ペア入れ替え編集</h2>
              <button className="edit-modal-close" onClick={() => { setEditModalOpen(false); setEditSelected(null); }} aria-label="閉じる">
                <X size={20} />
              </button>
            </div>
            <div className="edit-modal-note">
              {editSelected
                ? <>もう1つのペアをタップして入れ替え (<strong>ブロック{blocks[editSelected.blockIdx]?.id}-{editSelected.pairIdx + 1}番</strong> 選択中)<button className="edit-cancel-select" onClick={() => setEditSelected(null)}>選択解除</button></>
                : <>入れ替えたい1つ目のペアをタップ</>}
            </div>
            <div className="edit-modal-body">
              {blocks.map((b, bIdx) => {
                const scoreCount = Object.keys(b.scores || {}).length;
                return (
                  <div key={b.id} className="edit-block">
                    <div className="edit-block-header">
                      <span className="edit-block-id">ブロック {b.id}</span>
                      <span className="edit-block-meta">
                        {b.pairs.length}ペア
                        {scoreCount > 0 && <span className="edit-block-warn">(スコア{scoreCount}試合)</span>}
                        {b.confirmed && <span className="edit-block-confirmed"><CheckCircle size={11} /> 確定済</span>}
                      </span>
                    </div>
                    <div className="edit-pair-list">
                      {b.pairs.map((p, pIdx) => {
                        const isSelected = editSelected && editSelected.blockIdx === bIdx && editSelected.pairIdx === pIdx;
                        return (
                          <div
                            key={pIdx}
                            className={`edit-pair-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleEditPairClick(bIdx, pIdx)}
                          >
                            <span className="edit-pair-num">{pIdx + 1}</span>
                            <span className="edit-pair-names" translate="no">
                              <button type="button" className="edit-player-chip"
                                onClick={(e) => { e.stopPropagation(); openPlayerEdit(bIdx, pIdx, 'player1'); }}
                                title="名前・店舗を編集">
                                {p.player1?.name || '?'} <Edit2 size={11} />
                              </button>
                              <span className="edit-pair-x">×</span>
                              <button type="button" className="edit-player-chip"
                                onClick={(e) => { e.stopPropagation(); openPlayerEdit(bIdx, pIdx, 'player2'); }}
                                title="名前・店舗を編集">
                                {p.player2?.name || '?'} <Edit2 size={11} />
                              </button>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="edit-modal-footer">
              <button className="btn-secondary-local" onClick={() => { setEditModalOpen(false); setEditSelected(null); }}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 1b: 選手名・店舗 編集サブモーダル（純リラベル） */}
      {editPlayerModal && (
        <div className="score-input-overlay" onClick={() => setEditPlayerModal(null)}>
          <div className="score-input-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380, color: '#222' }}>
            <h3 style={{ color: '#222', display: 'flex', alignItems: 'center', gap: 6 }}><Edit2 size={15} /> 選手の名前・店舗を編集</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              <label style={{ fontSize: 13, color: '#222', fontWeight: 500 }}>名前
                <input className="edit-player-input" value={editPlayerName} onChange={(e) => setEditPlayerName(e.target.value)} placeholder="選手名" translate="no" />
              </label>
              <label style={{ fontSize: 13, color: '#222', fontWeight: 500 }}>店舗（任意）
                <input className="edit-player-input" value={editPlayerShop} onChange={(e) => setEditPlayerShop(e.target.value)} placeholder="店舗名" translate="no" />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn-secondary-local" onClick={() => setEditPlayerModal(null)}>キャンセル</button>
              <button className="btn-accent" onClick={updatePairPlayer}>保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   ⑦ 決勝トーナメント (シングルエリミネーション)
   ============================================================ */
function TournamentBracketScreen() {
  const { user, isAdmin, shopName } = useAuthUser();
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scoreInput, setScoreInput] = useState(null); // { matchId, top, bottom, currentScore }
  const [losersInput, setLosersInput] = useState(null);

  // SVG-2: bracket connector line layer refs
  const bracketRef = useRef(null);
  const matchRefs = useRef({});
  const [matchCoords, setMatchCoords] = useState({});
 // { idx } 復活戦選択モーダル

  // P5a: 二重押し防止フラグ (Firestore 保存不要、ローカル状態のみ)
  const [isRebuildingBracket, setIsRebuildingBracket] = useState(false);

  // P5a: パスコード強制続行モーダル
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overridePasscodeInput, setOverridePasscodeInput] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'events', id), (snap) => {
      if (!snap.exists()) {
        alert('イベントが見つかりません');
        navigate('/');
        return;
      }
      const data = snap.data();
      if (user && !isAdmin && data.ownerUid !== user.uid) {
        alert('このイベントへのアクセス権がありません');
        navigate('/');
        return;
      }
      setEvent({ id: snap.id, ...data });
    }, (err) => {
      console.error('イベント購読エラー', err);
      alert('イベントを読み込めませんでした（権限がないか、通信エラーです）');
      navigate('/');
    });
    return () => unsub();
  }, [id, navigate, user]);

  const handleLogout = async () => {
    if (!window.confirm('ログアウトしますか?')) return;
    await signOut(auth);
    navigate('/login');
  };

  const blocks = event?.blocks || [];
  const progressSettings = event?.progressSettings || {};
  const advancePerSize = progressSettings.advancePerSize || {};
  const tournament = event?.tournament || null;
  const losersResults = tournament?.losersResults || [];

  // P5a: placementDirty / placementOverride 状態 (派生)
  // 【undefined 耐性】: 既存ブラケット (placementRule 未定義 = v1扱い) では
  //                     placementDirty 等のフィールドが undefined のため、
  //                     === true 比較で明示的に false 扱いにする (?? false と等価)。
  //                     これにより v1 既存データが「常に正常状態」として扱われる。
  const placementDirty = tournament?.placementDirty === true;
  const placementOverride = tournament?.placementOverride === true;
  const placementDirtyReason = tournament?.placementDirtyReason || null;
  // スコア入力等のブラケット操作がロックされている状態:
  //   dirty=true かつ override=false の時のみロック
  //   dirty=false (正常) または override=true (運営判断で続行中) はロック解除
  const isPlacementLocked = placementDirty && !placementOverride;

  // 進行設定の復活枠数(losersBracket OFF時は0)
  const losersAdvanceCount = useMemo(() => {
    if (!progressSettings.losersBracket) return 0;
    return parseInt(progressSettings.losersBracketAdvanceCount, 10) || 0;
  }, [progressSettings]);

  // 復活枠のプレースホルダ配列(idxだけ持つ)
  const losersAdvancersPlaceholder = useMemo(() => {
    return Array.from({ length: losersAdvanceCount }, (_, i) => ({ idx: i }));
  }, [losersAdvanceCount]);

  // 各ブロックの通過候補スロット(blockId+rankのみ、pair保存しない)
  // 予選確定/未確定に関わらず、ブロック構造があれば候補として列挙
  const allCandidateAdvancers = useMemo(() => {
    const result = [];
    blocks.forEach(block => {
      if (!block.pairs) return;
      const n = block.pairs.length;
      if (n < 1) return;
      const advanceTo = parseInt(advancePerSize[n], 10) || 1;
      for (let rank = 1; rank <= advanceTo; rank++) {
        result.push({ blockId: block.id, rank });
      }
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, JSON.stringify(advancePerSize)]);

  // 確定済みブロック数(進行状況表示用)
  const confirmedBlockCount = useMemo(
    () => blocks.filter(b => b.confirmed).length,
    [blocks]
  );

  // ブラケット生成可能か: ブロックあり & 候補2組以上(予選確定不要)
  const canBuildBracket = useMemo(() => {
    return blocks.length > 0 && allCandidateAdvancers.length >= 2;
  }, [blocks, allCandidateAdvancers]);

  const matches = tournament?.matches || [];
  const totalRounds = tournament?.totalRounds || 0;

  // 高速アクセス用Map
  const matchesMap = useMemo(() => {
    const m = {};
    matches.forEach(x => { m[x.id] = x; });
    return m;
  }, [matches]);

  // ラウンド別グルーピング
  const matchesByRound = useMemo(() => {
    const grouped = {};
    matches.forEach(m => {
      if (!grouped[m.round]) grouped[m.round] = [];
      grouped[m.round].push(m);
    });
    Object.keys(grouped).forEach(r => {
      grouped[r].sort((a, b) => a.position - b.position);
    });
    return grouped;
  }, [matches]);

  // SVG-4: coord measure function (useCallback; reused by ResizeObserver in SVG-5)
  const measureBracketLines = useCallback(() => {
    const bracket = bracketRef.current;
    if (!bracket) return;
    const brect = bracket.getBoundingClientRect();
    const coords = {};
    Object.entries(matchRefs.current).forEach(([mid, el]) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      coords[mid] = {
        left:  r.left  - brect.left + bracket.scrollLeft,
        right: r.right - brect.left + bracket.scrollLeft,
        midY:  r.top   - brect.top  + bracket.scrollTop + r.height / 2,
        h:     r.height,
      };
    });
    setMatchCoords(prev => {
      const prevKeys = Object.keys(prev);
      if (prevKeys.length === Object.keys(coords).length) {
        let same = true;
        for (const k of prevKeys) {
          const a = prev[k], b = coords[k];
          if (!b || Math.abs(a.left-b.left)>0.5 || Math.abs(a.right-b.right)>0.5 ||
              Math.abs(a.midY-b.midY)>0.5 || Math.abs(a.h-b.h)>0.5) { same = false; break; }
        }
        if (same) return prev;
      }
      return coords;
    });
  }, []);

  // SVG-4: re-measure on matches change (score/confirm updates box sizes). Not every render.
  useLayoutEffect(() => {
    measureBracketLines();
  }, [matches, measureBracketLines]);

  // SVG-5: re-measure on size change / resize / font load (no scroll listener)
  useEffect(() => {
    const bracket = bracketRef.current;
    if (!bracket || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { measureBracketLines(); });
    ro.observe(bracket);
    Object.values(matchRefs.current).forEach(el => { if (el) ro.observe(el); });
    const onResize = () => measureBracketLines();
    window.addEventListener('resize', onResize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => measureBracketLines());
    }
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [matches, measureBracketLines]);

  // SVG-4: build koji paths (round>=2, both parents are winners)
  const bracketLinePaths = useMemo(() => {
    const out = [];
    matches.forEach(child => {
      if (child.round < 2) return;
      if (child.top?.sourceType !== 'winner' || child.bottom?.sourceType !== 'winner') return;
      const p1 = matchCoords[child.top.fromMatchId];
      const p2 = matchCoords[child.bottom.fromMatchId];
      const c  = matchCoords[child.id];
      if (!p1 || !p2 || !c) return;
      const midX = (Math.max(p1.right, p2.right) + c.left) / 2;
      const yTop = Math.min(p1.midY, p2.midY, c.midY);
      const yBottom = Math.max(p1.midY, p2.midY, c.midY);
      const d = [
        `M ${p1.right} ${p1.midY} H ${midX}`,
        `M ${p2.right} ${p2.midY} H ${midX}`,
        `M ${midX} ${yTop} V ${yBottom}`,
        `M ${midX} ${c.midY} H ${c.left}`,
      ].join(' ');
      out.push({ id: child.id, d });
    });
    return out;
  }, [matches, matchCoords]);

  // ブラケット生成
  // P5a: 二重押し防止、dirty/override クリアは updateDoc 成功時のみ
  const handleBuildBracket = async () => {
    if (isRebuildingBracket) return; // 二重押し防止
    if (!canBuildBracket) {
      alert('ブロックが作成されていないため、ブラケットを生成できません。先に進行設定で「進行を開始」してください。');
      return;
    }
    if (matches.length > 0) {
      const hasScores = matches.some(m => m.score || m.confirmed);
      // override 中でスコア入力済みは最警戒
      if (hasScores && placementOverride) {
        if (!window.confirm(
          '⚠ 強制続行モードで進行中の試合があります。\n' +
          '再生成すると入力済みスコアは全て消去されます。\n' +
          '本当に続行しますか?'
        )) return;
        if (!window.confirm('最終確認: 入力済みスコアを破棄して再生成しますか?')) return;
      } else if (hasScores) {
        if (!window.confirm('スコア入力済みのデータがあります。本当に消去して再生成しますか?')) return;
        if (!window.confirm('本当に再生成しますか? この操作は取り消せません。')) return;
      } else {
        if (!window.confirm('ブラケットを再生成しますか?')) return;
      }
    }
    const built = buildTournamentMatches(allCandidateAdvancers, losersAdvancersPlaceholder);
    if (!built.matches.length) {
      alert('参加者が2組未満のためブラケットを生成できません');
      return;
    }
    setIsRebuildingBracket(true);
    try {
      await updateDoc(doc(db, 'events', id), {
        tournament: {
          matches: built.matches,
          bracketSize: built.bracketSize,
          totalRounds: built.totalRounds,
          losersResults: tournament?.losersResults || [],
          placementRule: built.placementRule || 'v2-losers-vs-second',
          placementWarnings: built.placementWarnings || [],
          builtAt: serverTimestamp(),
          // P5a: 再生成成功時のみ dirty / override をクリア
          placementDirty: false,
          placementDirtyReason: null,
          placementDirtyAt: null,
          placementOverride: false,
          placementOverrideAt: null,
          placementOverrideBy: null,
        },
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      // P5a: 失敗時は dirty/override は維持 (クリアしない)
      alert('ブラケット生成に失敗しました: ' + err.message);
    } finally {
      setIsRebuildingBracket(false);
    }
  };

  // P5a: パスコード強制続行 (override=true 化)
  const handleOverrideSubmit = async () => {
    if (overridePasscodeInput !== PASSCODE_PROGRESS) {
      alert('パスコードが違います');
      return;
    }
    try {
      await updateDoc(doc(db, 'events', id), {
        tournament: {
          ...tournament,
          placementOverride: true,
          placementOverrideAt: serverTimestamp(),
          placementOverrideBy: user?.uid || null,
        },
        updatedAt: serverTimestamp(),
      });
      setShowOverrideModal(false);
      setOverridePasscodeInput('');
    } catch (err) {
      alert('強制続行の設定に失敗しました: ' + err.message);
    }
  };

  // スコア入力モーダル開く
  const openScoreInput = (match) => {
    // P5a: handler側ガード - disabled の補完防御
    // disabled だけだと Enter送信や将来のUI変更で抜ける可能性があるため、
    // ハンドラ側でも明示的にロック判定する
    if (isPlacementLocked) {
      alert('ブラケット配置が最新でないため、スコア入力できません。\nブラケットを再生成してください。');
      return;
    }
    if (match.confirmed) return;
    if (match.isBye) return;
    const top = resolveTournamentSlot(match.top, matchesMap, blocks, losersResults);
    const bottom = resolveTournamentSlot(match.bottom, matchesMap, blocks, losersResults);
    if (!top || !bottom) return;
    if (!top.pair || !bottom.pair) {
      alert('両者の通過が確定してから入力してください\n(該当ブロックの予選を確定する必要があります)');
      return;
    }
    setScoreInput({
      matchId: match.id,
      top,
      bottom,
      currentScore: match.score,
    });
  };

  // 予選で通過しなかった「落選ペア」一覧(復活戦の選択候補)
  const losersCandidates = useMemo(() => {
    const result = [];
    blocks.forEach(block => {
      if (!block.pairs) return;
      const n = block.pairs.length;
      const advanceTo = parseInt(advancePerSize[n], 10) || 1;
      // ブロック確定済みなら正確なランキング、未確定なら全員候補
      if (block.confirmed) {
        const ranked = computeBlockRanking(block);
        ranked.slice(advanceTo).forEach((r, idx) => {
          result.push({
            blockId: block.id,
            teamNo: r.teamNo,
            rank: advanceTo + idx + 1,
            pair: r.pair,
          });
        });
      } else {
        // 未確定ブロックも候補に入れる(運用上、確定前に手入力したい場合)
        block.pairs.forEach((pair, i) => {
          result.push({
            blockId: block.id,
            teamNo: i + 1,
            rank: null, // 未確定なのでランキング不明
            pair,
          });
        });
      }
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, JSON.stringify(advancePerSize)]);

  // P5b: 復活枠選択モーダルのプレビュー (各候補の対戦相手予測)
  //   - losersInput が変化した時のみ計算 (モーダル開閉時のみ発火)
  //   - 候補数だけ previewLosersOpponent を呼ぶ (N回案)
  //   - 失敗時は null fallback (警告マーク無し、モーダル動作は維持)
  const losersPreviewByCandidate = useMemo(() => {
    if (!losersInput) return new Map();
    const cache = new Map();
    losersCandidates.forEach(c => {
      const key = `${c.blockId}-${c.teamNo}`;
      const preview = previewLosersOpponent(
        losersInput.idx,
        allCandidateAdvancers,
        losersResults,
        losersAdvanceCount,
        { blockId: c.blockId }
      );
      cache.set(key, preview);
    });
    return cache;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [losersInput, losersCandidates, allCandidateAdvancers, JSON.stringify(losersResults), losersAdvanceCount]);

  // P5b: モーダル開いた時の「未選択時の対戦相手予定」(prospectiveLoser=null で計算)
  //   モーダル上部に表示する「現在の対戦相手予定」用
  const losersCurrentPreview = useMemo(() => {
    if (!losersInput) return null;
    return previewLosersOpponent(
      losersInput.idx,
      allCandidateAdvancers,
      losersResults,
      losersAdvanceCount,
      null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [losersInput, allCandidateAdvancers, JSON.stringify(losersResults), losersAdvanceCount]);

  // 既に他の復活枠で選択されているペアID集合(重複防止)
  const usedLosersPairIds = useMemo(() => {
    const set = new Set();
    losersResults.forEach(r => {
      if (r.pair && r.idx !== losersInput?.idx) {
        const key = `${r.pair.player1?.name}|${r.pair.player2?.name}|${r.blockId || ''}|${r.teamNo || ''}`;
        set.add(key);
      }
    });
    return set;
  }, [losersResults, losersInput]);

  // 復活戦勝者を選択(idx番目の復活枠にpairを設定)
  // P5a: 配置に影響するため placementDirty を立てる
  //      override 中の変更は override も解除 (運営判断の再強制)
  const setLosersResult = async (idx, candidate) => {
    const existing = (tournament?.losersResults || []).filter(r => r.idx !== idx);
    const next = [...existing, {
      idx,
      blockId: candidate.blockId,
      teamNo: candidate.teamNo,
      pair: candidate.pair,
    }];
    // ブラケット未生成時 (matches空) は dirty 化不要
    const hasBracket = tournament && tournament.matches && tournament.matches.length > 0;
    try {
      await updateDoc(doc(db, 'events', id), {
        tournament: {
          ...tournament,
          losersResults: next,
          ...(hasBracket ? {
            placementDirty: true,
            placementDirtyReason: 'losers_changed',
            placementDirtyAt: serverTimestamp(),
            placementOverride: false,
          } : {}),
        },
        updatedAt: serverTimestamp(),
      });
      setLosersInput(null);
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    }
  };

  // 復活戦勝者をクリア(idx番目)
  // P5a: dirty 化 + override 解除
  const clearLosersResult = async (idx) => {
    const next = (tournament?.losersResults || []).filter(r => r.idx !== idx);
    const hasBracket = tournament && tournament.matches && tournament.matches.length > 0;
    try {
      await updateDoc(doc(db, 'events', id), {
        tournament: {
          ...tournament,
          losersResults: next,
          ...(hasBracket ? {
            placementDirty: true,
            placementDirtyReason: 'losers_changed',
            placementDirtyAt: serverTimestamp(),
            placementOverride: false,
          } : {}),
        },
        updatedAt: serverTimestamp(),
      });
      setLosersInput(null);
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    }
  };

  // スコア書き込み
  const writeScore = async (winnerKey, score) => {
    if (!scoreInput) return;
    // P5a: handler側ガード - スコア入力中に override 解除されたケースに備える
    if (isPlacementLocked) {
      alert('ブラケット配置が最新でないため、スコア入力できません。\nブラケットを再生成してください。');
      setScoreInput(null);
      return;
    }
    const targetMatchId = scoreInput.matchId;
    const newMatches = matches.map(m =>
      m.id === targetMatchId ? { ...m, score, winner: winnerKey } : m
    );
    try {
      await updateDoc(doc(db, 'events', id), {
        tournament: { ...tournament, matches: newMatches },
        updatedAt: serverTimestamp(),
      });
      setScoreInput(null);
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    }
  };

  // スコアクリア
  const clearScore = async () => {
    if (!scoreInput) return;
    const targetMatchId = scoreInput.matchId;
    const newMatches = matches.map(m =>
      m.id === targetMatchId ? { ...m, score: null, winner: null, confirmed: false } : m
    );
    try {
      await updateDoc(doc(db, 'events', id), {
        tournament: { ...tournament, matches: newMatches },
        updatedAt: serverTimestamp(),
      });
      setScoreInput(null);
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    }
  };

  // 試合確定/解除
  const toggleConfirm = async (matchId) => {
    // P5a: handler側ガード
    if (isPlacementLocked) {
      alert('ブラケット配置が最新でないため、確定操作できません。\nブラケットを再生成してください。');
      return;
    }
    const m = matches.find(x => x.id === matchId);
    if (!m) return;
    if (!m.score) {
      alert('スコアが入力されていません');
      return;
    }
    const newMatches = matches.map(x =>
      x.id === matchId ? { ...x, confirmed: !x.confirmed } : x
    );
    try {
      await updateDoc(doc(db, 'events', id), {
        tournament: { ...tournament, matches: newMatches },
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    }
  };

  // チャンピオン
  const champion = useMemo(() => {
    if (totalRounds === 0) return null;
    const finalMatch = matches.find(m => m.round === totalRounds && m.position === 1);
    if (!finalMatch || !finalMatch.winner || !finalMatch.confirmed) return null;
    const winnerSlot = finalMatch.winner === 'top' ? finalMatch.top : finalMatch.bottom;
    return resolveTournamentSlot(winnerSlot, matchesMap, blocks, losersResults);
  }, [matches, totalRounds, matchesMap, blocks, losersResults]);

  if (!event) return <div className="page"><div className="loading">読み込み中...</div></div>;

  const renderSlot = (slot, match, slotKey) => {
    const p = resolveTournamentSlot(slot, matchesMap, blocks, losersResults);
    const isWinner = match.winner === slotKey;
    const isLoser = match.winner && match.winner !== slotKey && !match.isBye;
    const slotScore = match.score ? match.score.split('-')[slotKey === 'top' ? 0 : 1] : null;
    const isPlaceholder = p && !p.pair && !match.isBye;
    const isWaiting = !p && !match.isBye;
    // 通常スコア入力可能 = pair確定済み & 試合未確定 & BYEじゃない
    //                    & P5a: 配置ロック中 (dirty=true, override=false) ではない
    const clickableScore = p && p.pair && !match.confirmed && !match.isBye && !isPlacementLocked;
    // 復活枠の手入力可能 = sourceType=losers & pair未確定 & 試合未確定
    //                    (復活枠選択は dirty 状態でも変更可能、変更時に dirty 再立て)
    const clickableLosers = p && p.sourceType === 'losers' && !p.pair && !match.confirmed;

    const handleClick = () => {
      if (clickableScore) openScoreInput(match);
      else if (clickableLosers) setLosersInput({ idx: p.losersIdx });
    };

    return (
      <div
        className={`tournament-slot ${isWinner ? 'winner' : ''} ${isLoser ? 'loser' : ''} ${(clickableScore || clickableLosers) ? 'clickable' : ''} ${isPlaceholder ? 'placeholder' : ''}`}
        onClick={handleClick}
      >
        {p ? (
          p.pair ? (
            // 確定済み(実名表示)
            <>
              <span className={`tournament-seed ${p.sourceType === 'losers' ? 'losers-seed' : ''}`}>
                {p.sourceType === 'block' ? `${p.blockId}${p.rank}` : `復${p.losersIdx + 1}`}
              </span>
              <span className="tournament-name" translate="no">
                {p.pair?.player1?.name || '?'} / {p.pair?.player2?.name || '?'}
              </span>
              {slotScore && <span className="tournament-score">{slotScore}</span>}
              {p.sourceType === 'losers' && !match.confirmed && (
                <button
                  className="losers-clear-btn"
                  onClick={(e) => { e.stopPropagation(); clearLosersResult(p.losersIdx); }}
                  aria-label="復活戦勝者を解除"
                  title="解除"
                >×</button>
              )}
            </>
          ) : (
            // 未確定プレースホルダ
            <>
              <span className={`tournament-seed placeholder-seed ${p.sourceType === 'losers' ? 'losers-seed' : ''}`}>
                {p.sourceType === 'block' ? `${p.blockId}${p.rank}` : `復${p.losersIdx + 1}`}
              </span>
              <span className="tournament-name tournament-waiting">
                {p.sourceType === 'block' ? `ブロック${p.blockId} ${p.rank}位 (未確定)` : `復活戦 ${p.losersIdx + 1}位 (タップで入力)`}
              </span>
            </>
          )
        ) : (
          <span className="tournament-name tournament-waiting">
            {match.isBye ? '(不戦勝)' : (isWaiting ? '(勝者待ち)' : '-')}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="page no-select tournament-page">
      <header className="header no-print">
        <h1>
          <button className="btn-back" onClick={() => navigate(`/event/${id}/robin`)}>
            <ArrowLeft size={14} /> ロビン表へ
          </button>
          <span style={{ marginLeft: 8 }}><Trophy size={16} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />決勝トーナメント</span>
        </h1>
        <div className="header-actions header-actions-desktop">
          {matches.length > 0 && (
            <button className="btn-secondary" onClick={handleBuildBracket}>
              <RotateCcw size={14} /> ブラケット再生成
            </button>
          )}
        </div>
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
          <Menu size={20} />
        </button>
      </header>

      <TestModeBanner event={event} navigate={navigate} />

      <HamburgerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        shopName={shopName}
        email={user?.email}
        isAdmin={isAdmin}
        onNavigate={navigate}
        onLogout={handleLogout}
        extraItems={[
          { icon: <List size={18} />, label: 'ロビン表に戻る', onClick: () => navigate(`/event/${id}/robin`) },
          ...(matches.length > 0 ? [{ icon: <RotateCcw size={18} />, label: 'ブラケット再生成', onClick: handleBuildBracket }] : []),
        ]}
      />

      <div className="tournament-container">
        <div className="event-info-bar">
          <span><Calendar size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />{event.date}</span>
          <span translate="no"><Home size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />{event.houseName}</span>
          {matches.length > 0 && <span>{totalRounds}ラウンド / {matches.length}試合</span>}
        </div>

        {/* P5a: placementDirty バナー (黄=要再生成 / 赤=強制続行中) */}
        {placementDirty && matches.length > 0 && !placementOverride && (
          <div style={{
            margin: '12px 0',
            padding: '14px 16px',
            borderRadius: '8px',
            background: '#fffbe6',
            border: '2px solid #fc3',
            color: '#a60',
          }}>
            <div style={{ fontWeight: 'bold', fontSize: '1.05em', marginBottom: '6px' }}>
              ⚠ ブラケット配置の再生成が必要です
            </div>
            <div style={{ fontSize: '0.92em', lineHeight: 1.5, marginBottom: '10px' }}>
              {placementDirtyReason === 'losers_changed'
                ? '復活枠の選択が変更されたため、最適な配置が計算されていません。'
                : placementDirtyReason === 'block_rescored'
                  ? '予選結果が変更されたため、配置を再計算する必要があります。'
                  : placementDirtyReason === 'settings_changed'
                    ? '進行設定が変更されたため、配置を再計算する必要があります。'
                    : '配置を再計算する必要があります。'}
              <br />
              この状態ではスコア入力ができません。
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                className="btn-accent"
                onClick={handleBuildBracket}
                disabled={isRebuildingBracket}
                style={{ padding: '8px 16px' }}
              >
                {isRebuildingBracket ? '再生成中...' : 'ブラケットを再生成'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowOverrideModal(true)}
                disabled={isRebuildingBracket}
                style={{ padding: '8px 16px' }}
              >
                パスコードで強制続行
              </button>
            </div>
          </div>
        )}

        {/* P5a: 強制続行中バナー (赤) */}
        {placementDirty && matches.length > 0 && placementOverride && (
          <div style={{
            margin: '12px 0',
            padding: '14px 16px',
            borderRadius: '8px',
            background: '#fee',
            border: '2px solid #f55',
            color: '#a00',
          }}>
            <div style={{ fontWeight: 'bold', fontSize: '1.05em', marginBottom: '6px' }}>
              ⛔ 強制続行モード中
            </div>
            <div style={{ fontSize: '0.92em', lineHeight: 1.5, marginBottom: '10px' }}>
              ブラケット配置は最新化されていません。<br />
              強制続行は、配置が最新でない状態でも試合入力を許可する操作です。<br />
              <strong>通常はブラケット再生成を推奨します。</strong>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                className="btn-accent"
                onClick={handleBuildBracket}
                disabled={isRebuildingBracket}
                style={{ padding: '8px 16px' }}
              >
                {isRebuildingBracket ? '再生成中...' : '今すぐ再生成'}
              </button>
            </div>
          </div>
        )}

        {champion && (
          <div className="tournament-champion-banner">
            <Trophy size={20} />
            <span translate="no"><strong>{champion.pair?.player1?.name} / {champion.pair?.player2?.name}</strong></span>
            <span> 優勝!</span>
          </div>
        )}

        {blocks.length === 0 ? (
          <div className="empty">ブロックが作成されていません。先に進行設定で「進行を開始」してください。</div>
        ) : matches.length === 0 ? (
          <div className="tournament-build-section">
            <h2>ブラケット生成</h2>
            <p>予選通過枠 <strong>{allCandidateAdvancers.length}</strong>組 + 復活枠 0組 = 計 <strong>{allCandidateAdvancers.length}</strong>組</p>
            <p style={{ fontSize: 13, color: '#666' }}>
              現在 <strong style={{ color: '#1a1a2e' }}>{confirmedBlockCount} / {blocks.length}</strong> ブロック予選確定済み
            </p>
            <p className="tournament-build-note">
              ※ ブラケットは予選確定前でも生成可能です。各スロットには「ブロックA1位」など枠情報のみ表示され、予選確定後に自動的に名前が反映されます。
            </p>
            <button className="btn-accent btn-large" onClick={handleBuildBracket} disabled={!canBuildBracket}>
              <Trophy size={18} /> ブラケットを生成する
            </button>
          </div>
        ) : (
          <div className="tournament-bracket" ref={bracketRef}>
            <svg className="bracket-svg-layer" aria-hidden="true">
              {bracketLinePaths.map(p => (
                <path key={p.id} d={p.d} className="bracket-line" />
              ))}
            </svg>
            {Array.from({ length: totalRounds }, (_, r) => r + 1).map(round => (
              <div key={round} className="tournament-round">
                <div className="tournament-round-header">{getTournamentRoundName(round, totalRounds)}</div>
                <div className="tournament-round-matches">
                  {(matchesByRound[round] || []).map(match => (
                    <div key={match.id} className="match-cell">
                      <div
                        ref={(el) => { matchRefs.current[match.id] = el; }}
                        className={`tournament-match ${match.confirmed ? 'confirmed' : ''} ${match.isBye ? 'bye-match' : ''}`}
                      >
                        {renderSlot(match.top, match, 'top')}
                        <div className="tournament-slot-separator">vs</div>
                        {renderSlot(match.bottom, match, 'bottom')}
                        {match.score && !match.isBye && (
                          <div className="tournament-match-footer">
                            <button
                              className={`btn-confirm-mini ${match.confirmed ? 'confirmed' : ''}`}
                              onClick={(e) => { e.stopPropagation(); toggleConfirm(match.id); }}
                              disabled={isPlacementLocked}
                              style={isPlacementLocked ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                            >
                              {match.confirmed ? <><Lock size={11} /> 確定解除</> : <><Check size={11} /> 確定</>}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* スコア入力モーダル(4ボタン式、ロビン表と同じ) */}
      {scoreInput && (
        <div className="score-input-overlay" onClick={() => setScoreInput(null)}>
          <div className="score-input-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: 13, lineHeight: 1.4, color: '#222', marginBottom: 4 }}>
              勝者と勝ち方を選択
            </h3>

            <div className="winner-pick-group">
              <div className="winner-pick-label" translate="no">
                {scoreInput.top.pair?.player1?.name} / {scoreInput.top.pair?.player2?.name}
                <span className="winner-pick-win"> が勝ち</span>
              </div>
              <div className="winner-pick-buttons">
                <button className="score-cell" onClick={() => writeScore('top', '2-0')}>2-0</button>
                <button className="score-cell" onClick={() => writeScore('top', '2-1')}>2-1</button>
              </div>
            </div>

            <div className="winner-pick-group">
              <div className="winner-pick-label" translate="no">
                {scoreInput.bottom.pair?.player1?.name} / {scoreInput.bottom.pair?.player2?.name}
                <span className="winner-pick-win"> が勝ち</span>
              </div>
              <div className="winner-pick-buttons">
                <button className="score-cell" onClick={() => writeScore('bottom', '0-2')}>2-0</button>
                <button className="score-cell" onClick={() => writeScore('bottom', '1-2')}>2-1</button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4 }}>
              {scoreInput.currentScore && (
                <button className="btn-secondary" onClick={clearScore}>クリア</button>
              )}
              <button className="btn-secondary" onClick={() => setScoreInput(null)}>キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {/* 復活戦勝者の選択モーダル */}
      {losersInput && (
        <div className="score-input-overlay" onClick={() => setLosersInput(null)}>
          <div className="losers-input-modal" onClick={(e) => e.stopPropagation()}>
            <h3>復活戦 {losersInput.idx + 1}位 を選択</h3>

            {/* P5b: 現在の対戦相手予定 (未選択状態でのプレビュー) */}
            {losersCurrentPreview && (
              <div style={{
                padding: '10px 12px',
                marginBottom: '10px',
                background: '#f0f7ff',
                border: '1px solid #6ab',
                borderRadius: '6px',
                fontSize: '0.92em',
                color: '#246',
              }}>
                <strong>対戦相手予定: </strong>
                {losersCurrentPreview.isBye ? (
                  <span>BYE (1回戦不戦勝)</span>
                ) : losersCurrentPreview.opponent ? (
                  losersCurrentPreview.opponent.sourceType === 'block' ? (
                    <span>ブロック{losersCurrentPreview.opponent.blockId} {losersCurrentPreview.opponent.rank}位通過</span>
                  ) : (
                    <span>復活枠{losersCurrentPreview.opponent.losersIdx + 1}</span>
                  )
                ) : (
                  <span style={{ color: '#888' }}>未定</span>
                )}
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                  ※選択するペアによって対戦相手が変わる可能性があります
                </div>
              </div>
            )}

            <p className="losers-modal-note">予選で通過しなかったペアから選択(または手入力)</p>
            <div className="losers-candidate-list">
              {losersCandidates.length === 0 ? (
                <div className="losers-candidate-empty">候補ペアがありません</div>
              ) : (
                losersCandidates.map(c => {
                  const key = `${c.pair.player1?.name}|${c.pair.player2?.name}|${c.blockId}|${c.teamNo}`;
                  const previewKey = `${c.blockId}-${c.teamNo}`;
                  const preview = losersPreviewByCandidate.get(previewKey);
                  const isSameBlockWarning = preview?.sameBlock === true;
                  const isUsed = usedLosersPairIds.has(key);
                  return (
                    <button
                      key={key}
                      className={`losers-candidate ${isUsed ? 'used' : ''}`}
                      onClick={() => !isUsed && setLosersResult(losersInput.idx, c)}
                      disabled={isUsed}
                      title={isSameBlockWarning ? 'このペアを選ぶと同ブロック対戦になります(選択は可能)' : undefined}
                    >
                      <span className="losers-candidate-tag">{c.blockId}{c.teamNo}</span>
                      <span className="losers-candidate-name" translate="no">
                        {c.pair.player1?.name || '?'} / {c.pair.player2?.name || '?'}
                      </span>
                      {/* P5b: 同ブロック警告マーク (選択は許可、警告のみ) */}
                      {isSameBlockWarning && !isUsed && (
                        <span style={{
                          marginLeft: 'auto',
                          fontSize: '0.85em',
                          color: '#a60',
                          fontWeight: 'bold',
                        }}>
                          ⚠ 同ブロック
                        </span>
                      )}
                      {isUsed && <span className="losers-candidate-used-mark">選択済</span>}
                    </button>
                  );
                })
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
              <button className="btn-secondary" onClick={() => clearLosersResult(losersInput.idx)}>解除</button>
              <button className="btn-secondary" onClick={() => setLosersInput(null)}>キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {/* P5a: パスコード強制続行モーダル */}
      {showOverrideModal && (
        <div className="score-input-overlay" onClick={() => { setShowOverrideModal(false); setOverridePasscodeInput(''); }}>
          <div className="score-input-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 style={{ marginBottom: 12, color: '#a00' }}>⛔ 強制続行の確認</h3>
            <div style={{
              padding: '12px',
              background: '#fff8f8',
              border: '1px solid #fcc',
              borderRadius: '6px',
              fontSize: '0.92em',
              lineHeight: 1.6,
              marginBottom: 16,
              color: '#600',
            }}>
              <strong>強制続行は、配置が最新でない状態でも試合入力を許可する操作です。</strong>
              <br /><br />
              通常はブラケット再生成を推奨します。
              <br />
              続行する場合は、運営用パスコードを入力してください。
            </div>
            <input
              type="password"
              inputMode="numeric"
              maxLength={8}
              value={overridePasscodeInput}
              onChange={(e) => setOverridePasscodeInput(e.target.value)}
              placeholder="パスコード"
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '1.1em',
                border: '1px solid #ccc',
                borderRadius: '6px',
                marginBottom: 12,
                boxSizing: 'border-box',
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleOverrideSubmit(); }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={() => { setShowOverrideModal(false); setOverridePasscodeInput(''); }}
              >
                キャンセル
              </button>
              <button
                className="btn-accent"
                onClick={handleOverrideSubmit}
                style={{ background: '#c33', borderColor: '#a22' }}
              >
                強制続行を実行
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   ④ 登録店舗の管理画面
   ============================================================ */
function ShopMaster() {
  const { user, isAdmin, shopName } = useAuthUser();
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [newName, setNewName] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const inputName = useMemo(rndName, []);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      query(collection(db, 'shops'), where('ownerUid', '==', user.uid)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'));
        setShops(list);
      }
    );
    return () => unsub();
  }, [user]);

  const addShop = async () => {
    if (!newName.trim()) return;
    if (shops.some((s) => s.name === newName.trim())) {
      alert('同じ名前の店舗が既に登録されています');
      return;
    }
    try {
      await addDoc(collection(db, 'shops'), {
        ownerUid: user.uid,
        name: newName.trim(),
        createdAt: serverTimestamp()
      });
      setNewName('');
    } catch (err) {
      console.error('店舗追加失敗:', err);
      alert('店舗の追加に失敗しました。通信状況を確認してください。');
    }
  };

  const deleteShop = async (id) => {
    if (!window.confirm('この店舗を削除しますか?')) return;
    try {
      await deleteDoc(doc(db, 'shops', id));
    } catch (err) {
      console.error('店舗削除失敗:', err);
      alert('店舗の削除に失敗しました。通信状況を確認してください。');
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('ログアウトしますか?')) return;
    await signOut(auth);
    navigate('/login');
  };

  return (
    <div className="page no-select">
      <header className="header">
        <h1>
          <button className="btn-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} /> 戻る
          </button>
          <span style={{ marginLeft: 8 }}>登録店舗の管理</span>
        </h1>
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
          <Menu size={20} />
        </button>
      </header>

      <HamburgerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        shopName={shopName}
        email={user?.email}
        isAdmin={isAdmin}
        onNavigate={navigate}
        onLogout={handleLogout}
      />

      <div className="container">
        <div className="shop-add">
          <input
            type="text" name={inputName} value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addShop()}
            placeholder="店舗名を入力"
            autoComplete="off" translate="no" data-form-type="other"
          />
          <button className="btn-primary" style={{ width: 'auto', margin: 0 }} onClick={addShop}>
            <Plus size={16} /> 追加
          </button>
        </div>

        <ul className="shop-list">
          {shops.length === 0 ? (
            <li className="empty">店舗を追加してください</li>
          ) : (
            shops.map((s) => (
              <li key={s.id} className="shop-item">
                <span translate="no">{s.name}</span>
                <button className="btn-delete" onClick={() => deleteShop(s.id)}><X size={14} /></button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

/* ============================================================
   ⑤ 使い方画面
   ============================================================ */
function HelpScreen() {
  const { user, isAdmin, shopName } = useAuthUser();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tab, setTab] = useState('guide');

  const handleLogout = async () => {
    if (!window.confirm('ログアウトしますか?')) return;
    await signOut(auth);
    navigate('/login');
  };

  return (
    <div className="page no-select">
      <header className="header">
        <h1>
          <button className="btn-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} /> 戻る
          </button>
          <span style={{ marginLeft: 8 }}>使い方</span>
        </h1>
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
          <Menu size={20} />
        </button>
      </header>

      <HamburgerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        shopName={shopName}
        email={user?.email}
        isAdmin={isAdmin}
        onNavigate={navigate}
        onLogout={handleLogout}
      />

      <div className="container">
        <div className="help-tabs">
          <button className={`help-tab ${tab === 'guide' ? 'active' : ''}`} onClick={() => setTab('guide')}>使い方</button>
          <button className={`help-tab ${tab === 'faq' ? 'active' : ''}`} onClick={() => setTab('faq')}>よくある質問</button>
        </div>

        {tab === 'guide' && (
          <div className="help-content">
            <h2>はじめに</h2>
            <p>HTS(House Tournament System)は、ダーツバーのハウストーナメント受付・組み合わせ生成をスマホやPCで完結できるシステムです。</p>

            <h3 className="help-phase">■ 事前準備（前日まで）</h3>

            <h2>1. イベントを作成</h2>
            <p>ホームから「新規イベント作成」。日付とハウス名を入力します。</p>
            <h2>2. 店舗を登録</h2>
            <p>「登録店舗の管理」画面から、参加可能性のある店舗を登録します。エントリー入力中でも、店舗欄の「＋店舗を登録」からその場で追加できます。</p>
            <h2>3. エントリー入力</h2>
            <p>名前・店舗・Rt.を入力。フォーカスが外れると自動保存されます。</p>
            <h2>4. ペアリング</h2>
            <p>「ペアリング →」ボタンでペアリング画面へ。自動でハイ&ロー方式(1位×最下位、2位×下から2位…)の組み合わせが生成されます。</p>
            <h2>5. 同店舗ペアの調整</h2>
            <p>同店舗ペアが含まれていればピンク背景で表示されます。「手動入れ替え」で2人をタップして調整してください。</p>
            <h2>6. 手動入れ替え</h2>
            <p>2人をタップして交換。納得いくまで調整できます。</p>
            <h2>7. 保存と一覧</h2>
            <p>「保存」で名前を付けてクラウド保存。「ペア表一覧」で過去の保存パターンを開けます。同じ画面で手動入れ替え&上書き保存も可能です。</p>
            <h2>8. PDF出力</h2>
            <p>確定したペア表は「PDF表示」ボタンでPDFにできます。スマホでもそのまま表示・保存できます。</p>

            <h3 className="help-phase">■ 当日</h3>

            <h2>9. 来場チェック</h2>
            <p>名前欄左のチェックボックスで来場済みをマーク。ペアリング対象には影響しません(あくまで目印)。</p>
          </div>
        )}

        {tab === 'faq' && (
          <div className="help-content">
            <div className="faq-item">
              <div className="faq-q">Q. ペアリングは何方式?</div>
              <div className="faq-a">「ハイ&ロー方式」。Rt.順で1位×最下位、2位×下から2位…と組み合わせます。同店舗ペアは自動では回避せず、視覚的にお知らせするのみです(手動で調整してください)。</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">Q. 同じエントリーで何度生成しても結果は同じ?</div>
              <div className="faq-a">はい。ハイ&ロー方式は決定論的なので、メンバーとRt.が同じなら同じ結果になります。組み合わせを変えたい時は「手動入れ替え」を使ってください。</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">Q. 同店舗ペアになっちゃった</div>
              <div className="faq-a">ピンク背景が同店舗ペアです。「手動入れ替え」で交換してください。</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">Q. ペア表を複数保存できる?</div>
              <div className="faq-a">はい。「保存」ボタンで名前付きで保管。「ペア表一覧」から開いて編集も可能。</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">Q. 来場チェックはペアリングに影響する?</div>
              <div className="faq-a">影響しません。チェックは「来た人の目印」だけ。エントリーした人全員がペアリング対象。</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">Q. データはどこに保存?</div>
              <div className="faq-a">クラウドに保存されます。PC・スマホで同じデータを共有できます。</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">Q. オフラインでも使える?</div>
              <div className="faq-a">保存にはネット接続が必要。受付前にWi-Fi確認を。</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">Q. 過去のイベントを削除したい</div>
              <div className="faq-a">ホーム画面で「削除」をタップ。元に戻せないので注意。</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   ルーター
   ============================================================ */
function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/view/:id" element={<PublicRobinView />} />
        <Route path="/admin" element={<RequireAuth><AdminPanel /></RequireAuth>} />
        <Route path="/" element={<RequireAuth><EventList /></RequireAuth>} />
        <Route path="/event/:id" element={<RequireAuth><EventSheet /></RequireAuth>} />
        <Route path="/event/:id/pairing" element={<RequireAuth><PairingScreen /></RequireAuth>} />
        <Route path="/event/:id/progress-setting" element={<RequireAuth><ProgressSettingScreen /></RequireAuth>} />
        <Route path="/event/:id/robin" element={<RequireAuth><RobinTableScreen /></RequireAuth>} />
        <Route path="/event/:id/tournament" element={<RequireAuth><TournamentBracketScreen /></RequireAuth>} />
        <Route path="/shops" element={<RequireAuth><ShopMaster /></RequireAuth>} />
        <Route path="/help" element={<RequireAuth><HelpScreen /></RequireAuth>} />
      </Routes>
    </HashRouter>
  );
}

export default App;
